/** LiveMap mock: Ao vivo (estático) + Roteirizar com dados reais. */

import {
  clearTechnicianMapPlanRoute,
  createTechnicianRoadmapShare,
  fetchGoogleMapsBrowserKey,
  fetchMonitoramentoTechnician,
  fetchTechnicianPanelDay,
  fetchTechnicianPanelGoEvents,
  fetchTechnicianPanelLiveLocation,
  fetchTechnicianPanelLocationTrack,
  fetchTechnicianPanelMapPlan,
  fetchTechnicianPanelMapStartPoints,
  fetchTechnicianPanelOsDetail,
  fetchTechnicianPanelOsSummary,
  fetchTodayRoute,
  geocodeBatch,
  revokeTechnicianRoadmapShare,
  saveRouteBatch,
} from '../services/technicianPanelApi';
import { printTechnicianOpenRoute } from './technicianPanelMockPrintRoute';
import {
  closeRoadmapSharePanel,
  openRoadmapSharePanel,
  routeShareFingerprint,
} from './roadmapSharePanel';
import './roadmapSharePanel.css';
import { formatShortWeekday, todayYmd, defaultRouteForecastYmd } from './technicianPanelTime';
import { loadGoogleMaps } from './technicianPanelGoogleMaps';
import { getBoardClienteExibicao } from '../../board/utils/clienteBoardDisplay';
import { defeitoTerm } from '../../pg-native/useCutoverActive';
import { preventivaToTipoLetter } from '../../board/board-search-filters/tipoOsFilterUtils';
import {
  normalizeTrackOpType,
  uniqueTrackOpTypes,
  trackServiceTypeLabel,
  trackTripOsHtml,
  trackTripIconsHtml,
  travelTrackLabel,
  travelMetricsHtml,
} from './technicianPanelTrackTripIcons';

const LIVE_LOC_POLL_MS = 10000;
/** Em deslocamento com trip_id: GPS ~5s (track). */
const LIVE_LOC_TRAVEL_POLL_MS = 5000;
/** Snapshot full (mon + events). GPS no poll dedicado. */
const LIVE_FULL_POLL_MS = 25000;
/**
 * Day + OS abertas (SQL pesado). Incluído no first load e a cada ~75s no full;
 * mon/events no full de 25s continuam sem day a cada tick.
 */
const LIVE_DAY_POLL_MS = 75000;
/** Relógio de UI (“há Xs”) — sem hit API. */
const LIVE_UI_TICK_MS = 1000;
/** Bubble “Sem sincronização…” — 8 min desde a última posição (igual MapAlive). */
const LIVE_STALE_MS = 8 * 60 * 1000;
const BR_FALLBACK_CENTER = { lat: -15.78, lng: -47.93 }; // Brasília (não Rio)

/** Cor do pin “ponto de partida” (orientação — não entra no roteiro). CSS: #E93D82 */
const ICON_BUILDING_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M3 21h18"/><path d="M9 8h1"/><path d="M9 12h1"/><path d="M9 16h1"/>' +
  '<path d="M14 8h1"/><path d="M14 12h1"/><path d="M14 16h1"/>' +
  '<path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>' +
  '</svg>';

const ICON_HOME_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M5 12l-2 0l9 -9l9 9l-2 0"/><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7"/>' +
  '<path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6"/>' +
  '</svg>';

/** Cor fixa: trechos sem OS (sem trip_id ou trip sem OS em TRAVEL/SERVICE). */
const TRACK_IDLE_COLOR = '#E93D82';
/** Fallback Amethyst (paleta de deslocamentos com OS). */
const TRACK_TRIP_FALLBACK_COLOR = '#6D54E8';
/** Junta início/fim de atendimento se distância ≤ este valor (metros). */
const SERVICE_PIN_MERGE_METERS = 50;
/** Pins de OS neste raio viram pilha (zoom não separa o mesmo endereço). */
const SERVICE_PIN_STACK_METERS = 45;

let viewerCenterPromise = null;

function ensureViewerMapCenter() {
  if (viewerCenterPromise) return viewerCenterPromise;
  viewerCenterPromise = new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(BR_FALLBACK_CENTER);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos?.coords?.latitude);
        const lng = Number(pos?.coords?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          resolve({ lat, lng });
          return;
        }
        resolve(BR_FALLBACK_CENTER);
      },
      () => resolve(BR_FALLBACK_CENTER),
      { enableHighAccuracy: false, maximumAge: 10 * 60 * 1000, timeout: 5000 }
    );
  });
  return viewerCenterPromise;
}

function formatStaleSyncBubble(updatedAt) {
  if (!updatedAt) return null;
  const d = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  if (Number.isNaN(d.getTime())) return null;
  const totalMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours <= 0) {
    return `Sem sincronização a ${minutes} minuto${minutes === 1 ? '' : 's'}`;
  }
  return `Sem sincronização a ${hours} hora${hours === 1 ? '' : 's'} e ${minutes} minuto${minutes === 1 ? '' : 's'}`;
}

function parseLiveInstantMs(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Referência de “última sync” ao vivo.
 * Com jornada em andamento, não conta posição anterior ao início do dia de hoje.
 */
function resolveLiveSyncReferenceAt(live) {
  if (!live) return null;
  let syncAt = null;
  if (live.source === 'track' && live.trackRecordedAt) syncAt = live.trackRecordedAt;
  else if (live.updatedAt) syncAt = live.updatedAt;
  else if (live.trackRecordedAt) syncAt = live.trackRecordedAt;

  const phase = live.workday?.phase != null ? String(live.workday.phase) : '';
  const dayStart = phase === 'in_progress' ? live.workday?.startedAt : null;
  if (!dayStart) return syncAt;

  const startMs = parseLiveInstantMs(dayStart);
  if (startMs == null) return syncAt;
  const syncMs = parseLiveInstantMs(syncAt);
  if (syncMs == null || startMs > syncMs) return dayStart;
  return syncAt;
}

/**
 * Bubble “Sem sincronização…” — após 8 min sem posição (track ou live).
 */
function resolveLiveStaleBubble(live) {
  if (!live) return null;
  const refAt = resolveLiveSyncReferenceAt(live);
  if (!refAt) return null;
  const ageMs = Date.now() - new Date(refAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs <= LIVE_STALE_MS) return null;
  return formatStaleSyncBubble(refAt);
}

/** Bucket de minutos stale — invalida fingerprint para o texto da bubble atualizar. */
function liveStaleBucketKey(live) {
  if (!live) return '';
  const refAt = resolveLiveSyncReferenceAt(live);
  if (!refAt) return '';
  const ageMs = Date.now() - new Date(refAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs <= LIVE_STALE_MS) return 'sync:ok';
  return `sync:stale:${Math.floor(ageMs / 60000)}`;
}

function findActiveTravels(dayData) {
  const list = Array.isArray(dayData?.deslocamentos) ? dayData.deslocamentos : [];
  return list.filter((t) => t && (t.emAndamento || (t.horaIni && !t.horaFim)));
}

function findActiveTravel(dayData) {
  return findActiveTravels(dayData)[0] || null;
}

function findActiveServiceOs(dayData) {
  const list = Array.isArray(dayData?.os) ? dayData.os : [];
  return list.find((o) => o && o.emServico) || null;
}

function liveStatusLabel(status) {
  switch (status) {
    case 'deslocamento':
      return 'Em deslocamento';
    case 'em_servico':
      return 'Em serviço';
    case 'ocioso':
      return 'Ocioso';
    case 'nao_alocado':
      return 'Não alocado';
    case 'finalizado':
      return 'Finalizado';
    case 'nao_iniciou':
      return 'Não iniciou';
    case 'almoco':
      return 'Em almoço';
    default:
      return '—';
  }
}

/** Almoço ativo (LCMY_GO_LUNCH_BREAK) — pin de entrada e status. */
function resolveLiveLunchPin(live) {
  const lunch = live?.lunch;
  if (!lunch?.active) return null;
  if (!hasValidMapCoords(lunch.lat, lunch.lng)) return null;
  return { lat: Number(lunch.lat), lng: Number(lunch.lng) };
}

/** Posição no mapa Ao vivo: preferir GPS de entrada no almoço quando ativo. */
function resolveLiveMapPos(live) {
  const lunchPin = resolveLiveLunchPin(live);
  if (lunchPin) return lunchPin;
  if (live?.hasLocation && hasValidMapCoords(live.lat, live.lng)) {
    return { lat: Number(live.lat), lng: Number(live.lng) };
  }
  return null;
}

function formatOsList(codes) {
  const unique = [...new Set((codes || []).map((c) => String(c ?? '').trim()).filter(Boolean))];
  if (!unique.length) return '';
  return unique.map((os) => `#${os}`).join(', ');
}

function formatHhMmFromIso(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
}

/** Status + #OS; almoço (LCMY_GO_LUNCH_BREAK) sobrepõe mon; jornada via LCMY_GO_WORKDAY. */
export function formatTechnicianLiveStatus(status, dayData, workday, lunch) {
  if (lunch?.active) {
    const t = formatHhMmFromIso(lunch?.startedAt);
    return t ? `Em almoço desde ${t}` : 'Em almoço';
  }

  const phase = workday?.phase != null ? String(workday.phase) : '';
  if (phase === 'not_started') {
    const t = formatHhMmFromIso(workday?.startedAt);
    return t ? `Não iniciou ${t}` : 'Não iniciou';
  }
  if (phase === 'finished') {
    const t = formatHhMmFromIso(workday?.finishedAt);
    return t ? `Finalizado ${t}` : 'Finalizado';
  }

  const base = liveStatusLabel(status);
  if (status === 'deslocamento') {
    const osLabel = formatOsList(findActiveTravels(dayData).map((t) => t?.os));
    return osLabel ? `${base} ${osLabel}` : base;
  }
  if (status === 'em_servico') {
    const os = String(findActiveServiceOs(dayData)?.os || '').trim();
    return os ? `${base} #${os}` : base;
  }
  return base;
}

function openOrderSeq(order) {
  const n = Number(order?.TB02115_ORDEM);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function resolveNextDestination(openOrders, dayData) {
  const activeTravels = findActiveTravels(dayData);
  if (activeTravels.length) {
    const osLabel = formatOsList(activeTravels.map((t) => t?.os));
    return {
      label: 'Em deslocamento',
      client: activeTravels[0].cliente || '—',
      meta: osLabel || '—',
    };
  }

  const list = Array.isArray(openOrders) ? openOrders : [];
  const routed = list
    .map((o) => ({ o, seq: openOrderSeq(o) }))
    .filter((x) => x.seq != null)
    .sort((a, b) => a.seq - b.seq);
  if (routed.length) {
    const first = routed[0].o;
    const os = String(first.TB02115_CODIGO ?? '').trim();
    const client = getBoardClienteExibicao(first) || '—';
    const bairro = String(first.TB02115_BAIRRO || '').trim();
    return {
      label: 'Próximo destino',
      client,
      meta: os ? `#${os}${bairro ? ` · ${bairro}` : ''}` : '—',
    };
  }
  const n = list.length;
  if (n <= 0) {
    return { label: 'Próximo destino', client: 'Nenhuma OS em aberto', meta: '—' };
  }
  return {
    label: 'Próximo destino',
    client: `${n} OS não roteirizada${n === 1 ? '' : 's'}`,
    meta: '—',
  };
}

function isLiveTravelFast(state) {
  const tripId = state?.live?.tripId != null ? String(state.live.tripId).trim() : '';
  return state?.liveMonStatus === 'deslocamento' && !!tripId;
}

function livePanelFingerprint({ live, monStatus, dayData, openOrders, events }) {
  const travel = findActiveTravel(dayData);
  const service = findActiveServiceOs(dayData);
  const routeKey = (openOrders || [])
    .map((o) => `${o.TB02115_CODIGO || ''}:${o.TB02115_ORDEM || ''}`)
    .join('|');
  const eventsKey = (Array.isArray(events) ? events : [])
    .map(
      (ev) =>
        `${ev?.sortUpdatedAt || ''}:${ev?.typeAction || ''}:${ev?.qtyRecords || 0}:${ev?.sub || ''}:${
          ev?.photoSelectable ? 1 : 0
        }:${ev?.photoViewUrl ? 'p' : ''}:${ev?.speedLabel || ''}`
    )
    .join('|');
  return [
    live?.lat ?? '',
    live?.lng ?? '',
    live?.updatedAt ?? '',
    live?.accuracyM ?? '',
    live?.batteryPct ?? '',
    live?.tripId ?? '',
    live?.trackRecordedAt ?? '',
    live?.source ?? '',
    live?.hasLocation ? 1 : 0,
    live?.message ?? '',
    live?.workday?.phase ?? '',
    live?.workday?.startedAt ?? '',
    live?.workday?.finishedAt ?? '',
    live?.lunch?.active ? 1 : 0,
    live?.lunch?.startedAt ?? '',
    live?.lunch?.cancelledAt ?? '',
    live?.lunch?.lat ?? '',
    live?.lunch?.lng ?? '',
    monStatus ?? '',
    liveStaleBucketKey(live),
    travel?.os ?? '',
    travel?.cliente ?? '',
    service?.os ?? '',
    routeKey,
    eventsKey,
  ].join('~');
}

function getState(mapRoot) {
  if (!mapRoot.__tpmMapState) {
    mapRoot.__tpmMapState = {
      mode: mapRoot.getAttribute('data-map-mode') || 'live',
      day: mapRoot.getAttribute('data-plan-day') || 'amanha',
      routes: { amanha: [], hoje: [] },
      spotsByDay: { amanha: [], hoje: [] },
      restGroupByCity: false,
      shareMenuOpen: false,
      shareBusy: false,
      shareActive: false,
      shareCache: { amanha: null, hoje: null },
      forecastByDay: {
        amanha: defaultRouteForecastYmd(),
        hoje: todayYmd(),
      },
      live: null,
      liveDay: null,
      liveOpenOrders: null,
      liveMonStatus: null,
      liveEvents: null,
      selectedEventKey: null,
      selectedTrackSegmentKey: null,
      expandedTrackSvcClusterKey: null,
      trackDate: todayYmd(),
      trackTimeFrom: '',
      trackTimeTo: '',
      trackTimePopOpen: false,
      trackPoints: [],
      trackTrips: [],
      trackServices: [],
      trackTimeline: [],
      trackLoading: false,
      trackError: null,
      trackFp: '',
      liveFp: '',
      liveLoading: false,
      liveError: null,
      liveLocPollId: null,
      liveFullPollId: null,
      liveLocPollMs: null,
      liveLocInFlight: false,
      liveLocQueued: false,
      liveFullInFlight: false,
      liveFullQueued: false,
      liveVisHandler: null,
      liveDayFetchedAt: 0,
      liveUiTickId: null,
      liveFullForceDay: false,
      loading: false,
      geocoding: false,
      geocodingCount: 0,
      geocodeJobs: 0,
      saving: false,
      /** Snapshot da sequência persistida por dia (`hoje`/`amanha`) — dirty check do Salvar. */
      savedRoutes: { hoje: null, amanha: null },
      error: null,
      loadGen: 0,
      startPoint: null,
      startPointsList: null,
      startPointsOpen: false,
      startPointBusy: false,
      startPointsError: null,
    };
  }
  return mapRoot.__tpmMapState;
}

function dayLabel(day) {
  return day === 'hoje' ? 'hoje' : 'amanhã';
}

function dayDateLabel(day, forecastYmd) {
  try {
    const [y, m, d] = String(forecastYmd || '').split('-').map(Number);
    if (y && m && d) return formatShortWeekday(new Date(y, m - 1, d));
  } catch {
    /* ignore */
  }
  return day === 'hoje' ? formatShortWeekday(new Date()) : formatShortWeekday(new Date(Date.now() + 86400000));
}

function formatPrevisao(value) {
  if (value == null || value === '') return { label: '—', color: '#9a9aa6' };
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    const s = String(value).trim();
    return { label: s || '—', color: '#9a9aa6' };
  }

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  const startDayAfter = new Date(startToday);
  startDayAfter.setDate(startDayAfter.getDate() + 2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const time = `${hh}:${mm}`;

  if (d >= startToday && d < startTomorrow) {
    return { label: `hoje ${time}`, color: '#d1443e' };
  }
  if (d >= startTomorrow && d < startDayAfter) {
    return { label: `amanhã ${time}`, color: '#b06a10' };
  }
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return { label: `${day}/${month}`, color: '#12a375' };
}

function clearGoogleOverlays(mapRoot) {
  const g = mapRoot.__tpmGmap;
  if (!g) return;
  (g.markers || []).forEach((m) => m.setMap(null));
  g.markers = [];
  (g.overlays || []).forEach((o) => o.setMap(null));
  g.overlays = [];
  if (g.polyline) {
    g.polyline.setMap(null);
    g.polyline = null;
  }
  (g.polylines || []).forEach((p) => p.setMap(null));
  g.polylines = [];
  if (g.accuracyCircle) {
    g.accuracyCircle.setMap(null);
    g.accuracyCircle = null;
  }
}

function normalizeMapHexColor(input) {
  if (input == null) return null;
  let s = String(input).trim();
  if (!s) return null;
  if (!s.startsWith('#')) s = `#${s}`;
  if (/^#[0-9a-fA-F]{3}$/i.test(s)) {
    s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/i.test(s)) return null;
  return s;
}

/** Cores do badge de tipo OS (mesmas da coluna Em aberto do board). */
function mapServiceTypeColor(tipo) {
  switch (String(tipo || '').toUpperCase()) {
    case 'E':
      return '#9c27b0';
    case 'B':
      return '#ff5722';
    case 'A':
      return '#607d8b';
    case 'R':
      return '#795548';
    case 'D':
      return '#f44336';
    case 'I':
      return '#2196f3';
    case 'S':
      return '#4caf50';
    case 'C':
      return '#ff9800';
    default:
      return '#9e9e9e';
  }
}

/** Badge compacto (defeito_alert + color_bg, ou letra de TB02115_PREVENTIVA). */
function buildMapServiceTypeBadgeHtml(spot) {
  const alert = String(spot?.defeitoAlert || '').trim();
  if (alert) {
    const bg = normalizeMapHexColor(spot?.colorBg) || '#8458fe';
    return (
      `<span class="service-type-badge-compact service-type-badge-compact--defeito-alert tpm-map-type-badge"` +
      ` style="background-color:${escHtml(bg)}" data-tpm-tip="${escHtml(alert)}">${escHtml(alert)}</span>`
    );
  }
  const letter = preventivaToTipoLetter(spot?.preventiva || 'N');
  const bg = mapServiceTypeColor(letter);
  return (
    `<span class="service-type-badge-compact tpm-map-type-badge"` +
    ` style="background-color:${escHtml(bg)}">${escHtml(letter)}</span>`
  );
}

function shortClientLabel(name) {
  const s = String(name || '').trim();
  if (!s) return '—';
  if (s.length <= 24) return s;
  return `${s.slice(0, 22)}…`;
}

/** Tooltip estruturado do pin multi-OS (agrupa por cliente). */
function buildClusterTipPayload(orders) {
  const list = Array.isArray(orders) ? orders : [];
  const total = list.length;
  if (total === 0) return null;
  if (total === 1) {
    const o = list[0];
    return {
      total: 1,
      plain: `#${o.os} · ${o.client || '—'}`,
      groups: null,
    };
  }
  const byClient = new Map();
  list.forEach((o) => {
    const client = String(o?.client || '—').trim() || '—';
    if (!byClient.has(client)) byClient.set(client, []);
    byClient.get(client).push(String(o.os || '').trim());
  });
  const groups = [...byClient.entries()].map(([client, osList]) => ({
    client,
    count: osList.filter(Boolean).length,
    osList: osList.filter(Boolean),
  }));
  return { total, plain: null, groups };
}

function buildPinButton({ badge, client, title, tipPayload, on }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `tpm-map-pin${on ? ' is-on' : ''}`;
  if (tipPayload?.groups?.length) {
    btn.setAttribute('data-tpm-tip', `${tipPayload.total} OS neste ponto`);
    btn.setAttribute('data-tpm-tip-cluster', JSON.stringify(tipPayload));
    btn.setAttribute('data-tpm-tip-wrap', '');
  } else if (tipPayload?.plain) {
    btn.setAttribute('data-tpm-tip', tipPayload.plain);
  } else if (title) {
    btn.setAttribute('data-tpm-tip', title);
  }
  btn.innerHTML =
    `<span class="tpm-map-pin-bubble"><span class="tpm-map-pin-badge">${badge}</span>` +
    `<span class="tpm-map-pin-label">${shortClientLabel(client)}</span></span>` +
    `<span class="tpm-map-pin-stem"></span><span class="tpm-map-pin-dot"></span>`;
  return btn;
}

function buildStartPointPinEl(origem, tipo) {
  const el = document.createElement('div');
  el.className = 'tpm-map-pin tpm-map-pin--start is-on';
  el.setAttribute('data-tpm-tip', `Ponto de partida · ${origem}`);
  const icon = tipo === 'tecnico' ? ICON_HOME_SVG : ICON_BUILDING_SVG;
  el.innerHTML =
    `<span class="tpm-map-pin-bubble">` +
    `<span class="tpm-map-pin-badge tpm-map-pin-badge--icon">${icon}</span>` +
    `<span class="tpm-map-pin-label">${shortClientLabel(origem)}</span></span>` +
    `<span class="tpm-map-pin-stem"></span><span class="tpm-map-pin-dot"></span>`;
  return el;
}

function appendStartPointOverlay(g, state) {
  const sp = state.startPoint;
  if (!sp || !hasValidMapCoords(sp.lat, sp.lng) || !g?.map || !window.google?.maps) return false;
  const overlay = createPinOverlay(window.google.maps, {
    lat: Number(sp.lat),
    lng: Number(sp.lng),
    el: buildStartPointPinEl(sp.origem, sp.tipo),
    anchor: 'bottom',
  });
  overlay.setMap(g.map);
  g.overlays.push(overlay);
  return true;
}

function createPinOverlay(googleMaps, { lat, lng, el, onClick, anchor = 'bottom', pixelOffset = null }) {
  class PinOverlay extends googleMaps.OverlayView {
    constructor() {
      super();
      this.position = new googleMaps.LatLng(lat, lng);
      this.el = el;
      this.onClick = onClick;
      this.anchor = anchor;
      this.pixelOffset = pixelOffset || { x: 0, y: 0 };
    }

    onAdd() {
      this.el.style.position = 'absolute';
      this.el.style.zIndex = '10';
      const panes = this.getPanes();
      panes.overlayMouseTarget.appendChild(this.el);
      this._click = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onClick?.();
      };
      this.el.addEventListener('click', this._click);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection || !this.el) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      const ox = Number(this.pixelOffset.x) || 0;
      const oy = Number(this.pixelOffset.y) || 0;
      this.el.style.left = `${point.x + ox}px`;
      this.el.style.top = `${point.y + oy}px`;
      this.el.style.transform =
        this.anchor === 'center' ? 'translate(-50%, -50%)' : 'translate(-50%, -100%)';
    }

    onRemove() {
      if (this._click) this.el?.removeEventListener('click', this._click);
      if (this.el?.parentNode) this.el.parentNode.removeChild(this.el);
      this.el = null;
    }
  }
  return new PinOverlay();
}

function ensureGoogleHost(root) {
  const canvas = root.querySelector('.tpm-map-canvas');
  if (!canvas) return null;
  let host = canvas.querySelector('[data-tpm="map-google"]');
  if (!host) {
    host = document.createElement('div');
    host.className = 'tpm-map-google';
    host.setAttribute('data-tpm', 'map-google');
    canvas.insertBefore(host, canvas.firstChild);
  }
  return host;
}

/**
 * Overlay central enquanto o Google Maps (script + 1º paint de tiles) ainda não está pronto.
 * Reutiliza visual de plan-busy; z-index abaixo do toggle/hint para não cobrir controles.
 */
function setMapBootLoading(root, on, opts = {}) {
  const canvas = root.querySelector('.tpm-map-canvas');
  if (!canvas) return;
  let el = canvas.querySelector('[data-tpm="map-boot-loading"]');
  if (!on) {
    if (el) el.hidden = true;
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tpm-map-boot-loading';
    el.setAttribute('data-tpm', 'map-boot-loading');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    canvas.appendChild(el);
  }
  const title = opts.title || 'Carregando mapa';
  const sub = opts.sub || 'Preparando visualização…';
  el.innerHTML =
    `<div class="tpm-map-boot-loading-card">` +
    `<span class="tpm-map-boot-loading-spinner" aria-hidden="true"></span>` +
    `<div class="tpm-map-boot-loading-text">` +
    `<strong>${escHtml(title)}</strong>` +
    `<span>${escHtml(sub)}</span>` +
    `</div></div>`;
  el.hidden = false;
}

function waitForMapFirstIdle(map, timeoutMs = 10000) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    try {
      window.google.maps.event.addListenerOnce(map, 'idle', finish);
    } catch {
      finish();
      return;
    }
    window.setTimeout(finish, timeoutMs);
  });
}

async function ensureGoogleMap(root) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return null;
  if (mapRoot.__tpmGmap?.map) return mapRoot.__tpmGmap;
  // Evita N× load concorrente (paint live/track/plan) e mantém 1 overlay de boot.
  if (mapRoot.__tpmGmapPromise) return mapRoot.__tpmGmapPromise;

  mapRoot.__tpmGmapPromise = (async () => {
    setMapBootLoading(root, true, {
      title: 'Carregando mapa',
      sub: 'Preparando visualização…',
    });
    try {
      const host = ensureGoogleHost(root);
      if (!host) throw new Error('Canvas do mapa indisponível');

      const key = await fetchGoogleMapsBrowserKey();
      if (!key) throw new Error('Google Maps key indisponível');
      await loadGoogleMaps(key);

      const center = await ensureViewerMapCenter();
      const map = new window.google.maps.Map(host, {
        center,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });

      // Gray canvas = host vazio até o 1º idle (tiles/projection).
      await waitForMapFirstIdle(map);

      mapRoot.__tpmGmap = {
        map,
        markers: [],
        overlays: [],
        polyline: null,
        lastFitKey: '',
        lastLiveKey: '',
      };
      setMapBootLoading(root, false);
      return mapRoot.__tpmGmap;
    } catch (err) {
      mapRoot.__tpmGmapPromise = null;
      setMapBootLoading(root, false);
      throw err;
    }
  })();

  return mapRoot.__tpmGmapPromise;
}

function syncGoogleMap(root, state, opts, { fit = false } = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  const g = mapRoot?.__tpmGmap;
  if (!g?.map || !window.google?.maps) return;

  clearGoogleOverlays(mapRoot);

  const spots = state.spotsByDay[state.day] || [];
  const route = state.routes[state.day] || [];
  const clusters = clusterPins(spots);
  const bounds = new window.google.maps.LatLngBounds();
  let hasPoint = false;

  clusters.forEach((c) => {
    if (!hasValidMapCoords(c.lat, c.lng)) return;
    const inRoute = c.orders
      .map((o) => ({ o, idx: route.indexOf(o.os) }))
      .filter((x) => x.idx >= 0)
      .sort((a, b) => a.idx - b.idx);
    const on = inRoute.length > 0;
    let badge = '+';
    if (on) badge = String(inRoute[0].idx + 1);
    else if (c.orders.length > 1) badge = String(c.orders.length);

    // Label = OS principal do cluster (1ª da rota; senão 1ª da lista)
    const primary = on ? inRoute[0].o : c.orders[0];
    const pinClient = primary?.client || c.client || '—';
    const tipPayload = buildClusterTipPayload(c.orders);
    const el = buildPinButton({
      badge,
      client: pinClient,
      tipPayload,
      title: tipPayload?.plain || '',
      on,
    });
    const overlay = createPinOverlay(window.google.maps, {
      lat: Number(c.lat),
      lng: Number(c.lng),
      el,
      onClick: () => {
        toggleCluster(
          root,
          c.orders.map((o) => o.os).join(','),
          opts
        );
      },
    });
    overlay.setMap(g.map);
    g.overlays.push(overlay);
    bounds.extend({ lat: Number(c.lat), lng: Number(c.lng) });
    hasPoint = true;
  });

  if (appendStartPointOverlay(g, state)) {
    bounds.extend({ lat: Number(state.startPoint.lat), lng: Number(state.startPoint.lng) });
    hasPoint = true;
  }

  const path = route
    .map((os) => spotByOs(state, os))
    .filter((p) => p && hasValidMapCoords(p.lat, p.lng))
    .map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));

  if (path.length > 1) {
    g.polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#6d54e8',
      strokeOpacity: 0.85,
      strokeWeight: 2,
      map: g.map,
    });
  }

  const startKey = state.startPoint
    ? `${state.startPoint.id}:${Number(state.startPoint.lat).toFixed(5)},${Number(state.startPoint.lng).toFixed(5)}`
    : '';
  const fitKey = `${state.day}:${spots.map((s) => s.os).join(',')}:${startKey}`;
  if (fit || g.lastFitKey !== fitKey) {
    g.lastFitKey = fitKey;
    if (hasPoint) {
      g.map.fitBounds(bounds, 56);
      window.google.maps.event.addListenerOnce(g.map, 'idle', () => {
        (g.overlays || []).forEach((o) => {
          if (typeof o.draw === 'function') o.draw();
        });
      });
    }
  }
}

function setFakeMapVisible(root, visible) {
  const canvas = root.querySelector('.tpm-map-canvas');
  if (!canvas) return;
  canvas.classList.toggle('has-google', !visible);
  const pins = root.querySelector('[data-tpm="map-pins"]');
  if (pins) pins.hidden = !visible;
  const svg = root.querySelector('[data-tpm="map-route"]');
  if (svg) svg.hidden = true;
  const googleHost = root.querySelector('[data-tpm="map-google"]');
  if (googleHost) googleHost.hidden = visible;
  const tech = root.querySelector('[data-tpm="map-tech"]');
  if (tech) tech.hidden = !visible;
}

function formatLiveAgo(updatedAt) {
  if (!updatedAt) return null;
  const d = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  if (Number.isNaN(d.getTime())) return null;
  const sec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `há ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} h`;
  const days = Math.floor(h / 24);
  return `há ${days} d`;
}

/** Instantâneo de sync da posição (track em trip ou live.updatedAt; piso = início do dia). */
function liveSyncAt(live) {
  return resolveLiveSyncReferenceAt(live);
}

function liveTechDisplayName(root, opts = {}) {
  const fromOpts = String(opts?.technicianName || '').trim();
  if (fromOpts) return fromOpts;
  const el = root?.querySelector?.('[data-tpm="tech-name"]');
  return String(el?.textContent || '').trim() || 'Técnico';
}

/**
 * Ao vivo: pin só entre início e fim da jornada.
 * finished → aviso até 23:59 BRT; no dia seguinte a API volta not_started.
 */
function resolveLiveWorkdayGate(live, techName) {
  const phase = live?.workday?.phase != null ? String(live.workday.phase) : '';
  if (!phase) return { hidePin: false, kind: null, message: '' };
  if (phase === 'in_progress') return { hidePin: false, kind: null, message: '' };
  const label = `@${String(techName || 'Técnico').trim() || 'Técnico'}`;
  if (phase === 'finished') {
    const t = formatHhMmFromIso(live?.workday?.finishedAt);
    return {
      hidePin: true,
      kind: 'finished',
      message: t ? `${label} finalizou o dia às ${t}` : `${label} finalizou o dia.`,
    };
  }
  return {
    hidePin: true,
    kind: 'not_started',
    message: `${label} ainda não iniciou o dia.`,
  };
}

function syncLiveWorkdayOffChrome(root, state, opts = {}) {
  const canvas = root.querySelector('.tpm-map-canvas');
  if (!canvas) return { hidePin: false, kind: null, message: '' };
  const unlinked = isLiveUnlinkedState(state);
  const gate =
    state.mode === 'live' && !unlinked && state.live
      ? resolveLiveWorkdayGate(state.live, liveTechDisplayName(root, opts))
      : { hidePin: false, kind: null, message: '' };
  const on = !!gate.hidePin;
  canvas.classList.toggle('is-workday-off', on);
  let el = canvas.querySelector('[data-tpm="map-workday-off"]');
  if (!on) {
    if (el) el.remove();
    return gate;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tpm-map-workday-off';
    el.setAttribute('data-tpm', 'map-workday-off');
    el.innerHTML = '<div class="tpm-map-workday-off-card"><span class="tpm-map-workday-off-msg"></span></div>';
    canvas.appendChild(el);
  }
  const msg = el.querySelector('.tpm-map-workday-off-msg');
  if (msg) msg.textContent = gate.message;
  return gate;
}

function formatLiveHint(live) {
  if (live?.lunch?.active) {
    if (resolveLiveLunchPin(live)) return 'Em almoço — local da entrada no almoço';
    return 'Em almoço';
  }
  if (!live?.hasLocation) return live?.message || 'Sem posição ao vivo';
  const ago = formatLiveAgo(liveSyncAt(live)) || 'agora';
  const acc =
    live.accuracyM != null && Number.isFinite(Number(live.accuracyM))
      ? ` · precisão ${Math.round(Number(live.accuracyM))} m`
      : '';
  return `Posição atualizada ${ago}${acc}`;
}

/** Mensagem “App GO não vinculado” na vista Ao vivo (hint central + mapa esmaecido). */
function isLiveUnlinkedMessage(text) {
  return /app\s*go\s*n[aã]o\s+vinculado/i.test(String(text || ''));
}

function isLiveUnlinkedState(state) {
  if (state.mode !== 'live') return false;
  // Mantém o visual mesmo em reload/poll se já sabemos que não está vinculado.
  if (state.liveError && isLiveUnlinkedMessage(state.liveError)) return true;
  if (state.live && !state.live.hasLocation && isLiveUnlinkedMessage(state.live.message)) {
    return true;
  }
  return false;
}

function batteryTone(pct) {
  if (pct == null || !Number.isFinite(pct)) return 'muted';
  if (pct <= 20) return 'danger';
  if (pct <= 40) return 'warn';
  return 'ok';
}

function closeWorkdayPhotoModal(root) {
  const doc = root?.ownerDocument || document;
  doc.querySelectorAll('[data-tpm="workday-photo-overlay"]').forEach((el) => el.remove());
}

/** Modal de foto início/fim do dia — reutiliza shell de Fotos da OS / assinatura. */
function openWorkdayPhotoModal(root, { title, sub, url }) {
  closeWorkdayPhotoModal(root);
  const viewUrl = url != null ? String(url).trim() : '';
  if (!viewUrl) return;
  const doc = root?.ownerDocument || document;
  const overlay = doc.createElement('div');
  overlay.className = 'tpm-os-fotos-overlay';
  overlay.setAttribute('data-tpm', 'workday-photo-overlay');
  overlay.setAttribute('role', 'presentation');
  overlay.innerHTML =
    '<div class="tpm-os-fotos-modal tpm-workday-photo-modal" role="dialog" aria-modal="true">' +
    '<div class="tpm-os-fotos-header">' +
    '<div class="tpm-os-fotos-header-info">' +
    '<h3 class="tpm-os-fotos-title"></h3>' +
    '<div class="tpm-os-fotos-subtitle"></div>' +
    '</div>' +
    '<button type="button" class="tpm-os-fotos-close" data-tpm="workday-photo-close" aria-label="Fechar">✕</button>' +
    '</div>' +
    '<div class="tpm-os-fotos-body tpm-workday-photo-body">' +
    '<div class="tpm-workday-photo-frame">' +
    '<span class="tpm-workday-photo-wrap">' +
    '<img class="tpm-workday-photo-img" alt="" />' +
    '<button type="button" class="tpm-os-fotos-zoom-btn" data-tpm="workday-photo-zoom" aria-label="Ampliar foto" data-tpm-tip="Ampliar foto">' +
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="m21 21-6-6m6 6v-4.8m0 4.8h-4.8"/><path d="M3 16.2V21m0 0h4.8M3 21l6-6"/>' +
    '<path d="M21 7.8V3m0 0h-4.8M21 3l-6 6"/><path d="M3 7.8V3m0 0h4.8M3 3l6 6"/></svg>' +
    '</button>' +
    '</span>' +
    '</div></div></div>';
  const titleEl = overlay.querySelector('.tpm-os-fotos-title');
  const subEl = overlay.querySelector('.tpm-os-fotos-subtitle');
  const img = overlay.querySelector('.tpm-workday-photo-img');
  const dialog = overlay.querySelector('[role="dialog"]');
  const safeTitle = title || 'Foto da jornada';
  if (dialog) dialog.setAttribute('aria-label', safeTitle);
  if (titleEl) titleEl.textContent = safeTitle;
  if (subEl) {
    subEl.textContent = sub || '';
    subEl.hidden = !sub;
  }
  if (img) {
    img.alt = safeTitle;
    img.src = viewUrl;
  }
  const closeZoom = () => {
    const zoom = overlay.querySelector('[data-tpm="workday-photo-zoom-overlay"]');
    if (!zoom) return false;
    zoom.remove();
    return true;
  };
  const openZoom = () => {
    closeZoom();
    const zoom = doc.createElement('div');
    zoom.className = 'tpm-os-fotos-overlay tpm-os-fotos-overlay--zoom';
    zoom.setAttribute('data-tpm', 'workday-photo-zoom-overlay');
    zoom.setAttribute('role', 'presentation');
    zoom.innerHTML =
      '<div class="tpm-os-fotos-zoom-modal">' +
      '<button type="button" class="tpm-os-fotos-close" data-tpm="workday-photo-zoom-close" aria-label="Fechar">✕</button>' +
      '<div class="tpm-os-fotos-zoom-body"><img class="tpm-os-fotos-zoom-img" alt="" /></div>' +
      '</div>';
    const zoomImg = zoom.querySelector('img');
    zoomImg.alt = `${safeTitle} (ampliada)`;
    zoomImg.src = viewUrl;
    zoom.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (ev.target === zoom || ev.target.closest('[data-tpm="workday-photo-zoom-close"]')) {
        zoom.remove();
      }
    });
    overlay.appendChild(zoom);
  };
  const onKey = (ev) => {
    if (ev.key !== 'Escape') return;
    if (!closeZoom()) onClose();
  };
  const onClose = () => {
    overlay.removeEventListener('click', onOverlayClick);
    doc.removeEventListener('keydown', onKey);
    overlay.remove();
  };
  const onOverlayClick = (ev) => {
    if (ev.target.closest('[data-tpm="workday-photo-zoom"]')) {
      openZoom();
      return;
    }
    if (ev.target === overlay || ev.target.closest('[data-tpm="workday-photo-close"]')) {
      onClose();
    }
  };
  overlay.addEventListener('click', onOverlayClick);
  doc.addEventListener('keydown', onKey);
  doc.body.appendChild(overlay);
}

function stopLivePoll(state) {
  if (!state) return;
  if (state.liveLocPollId) {
    window.clearInterval(state.liveLocPollId);
    state.liveLocPollId = null;
  }
  if (state.liveFullPollId) {
    window.clearInterval(state.liveFullPollId);
    state.liveFullPollId = null;
  }
  if (state.liveUiTickId) {
    window.clearInterval(state.liveUiTickId);
    state.liveUiTickId = null;
  }
  // Legado (poll único) — limpa se ainda existir em sessão antiga.
  if (state.livePollId) {
    window.clearInterval(state.livePollId);
    state.livePollId = null;
  }
  if (state.liveVisHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', state.liveVisHandler);
  }
  state.liveVisHandler = null;
  state.liveLocPollMs = null;
  state.livePollMs = null;
  state.liveTravelTick = 0;
  state.liveLocInFlight = false;
  state.liveLocQueued = false;
  state.liveFullInFlight = false;
  state.liveFullQueued = false;
  state.liveFullForceDay = false;
}

function isDocumentHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function desiredLiveLocPollMs(state) {
  return isLiveTravelFast(state) ? LIVE_LOC_TRAVEL_POLL_MS : LIVE_LOC_POLL_MS;
}

function shouldFetchLiveDay(state, { initial = false, forceDay = false } = {}) {
  if (initial || forceDay) return true;
  const last = Number(state.liveDayFetchedAt) || 0;
  if (!last) return true;
  return Date.now() - last >= LIVE_DAY_POLL_MS;
}

function buildLiveMarkerEl(opts = {}) {
  const wrap = document.createElement('div');
  wrap.className = `tpm-map-live-marker${opts.lunch ? ' is-lunch' : ''}`;
  if (opts.bubbleText) {
    const bubble = document.createElement('span');
    bubble.className = `tpm-map-pin-bubble tpm-map-live-stale-bubble${opts.lunch ? ' is-lunch' : ''}`;
    bubble.textContent = opts.bubbleText;
    wrap.appendChild(bubble);
  }
  const pulse = document.createElement('span');
  pulse.className = 'tpm-map-live-marker-pulse';
  const dot = document.createElement('span');
  dot.className = 'tpm-map-live-marker-dot';
  if (opts.avatarUrl) {
    const img = document.createElement('img');
    img.className = 'tpm-map-live-marker-img';
    img.src = opts.avatarUrl;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';
    img.addEventListener('error', () => {
      img.remove();
      dot.textContent = opts.initials || '•';
    });
    dot.appendChild(img);
  } else {
    dot.textContent = opts.initials || '•';
  }
  wrap.appendChild(pulse);
  wrap.appendChild(dot);
  return wrap;
}

function resolveSelectedEvent(state) {
  const key = state.selectedEventKey;
  if (!key || !Array.isArray(state.liveEvents)) return null;
  return state.liveEvents.find((ev) => ev && ev.eventKey === key && ev.mapSelectable) || null;
}

function buildEventFocusPinEl(ev) {
  const wrap = document.createElement('div');
  wrap.className = 'tpm-map-event-focus-pin';
  const color = ev.color || '#6d54e8';
  const title = String(ev.cliente || ev.title || 'Evento');
  const subParts = [];
  if (ev.equipamento) subParts.push(String(ev.equipamento));
  if (ev.sub) subParts.push(String(ev.sub));
  const sub = subParts.join(' · ');
  const tipText = [ev.title, title, sub].filter(Boolean).join(' · ');
  if (tipText) {
    wrap.setAttribute('data-tpm-tip', tipText);
    if (tipText.length > 36) wrap.setAttribute('data-tpm-tip-wrap', '');
  }
  wrap.innerHTML =
    `<span class="tpm-map-event-focus-pin-bubble"><span class="tpm-map-event-focus-pin-title"></span>` +
    `<span class="tpm-map-event-focus-pin-sub"></span></span>` +
    `<span class="tpm-map-event-focus-pin-stem"></span>` +
    `<span class="tpm-map-event-focus-pin-dot" style="background:${color};box-shadow:0 0 0 3px ${color}33"></span>`;
  const t = wrap.querySelector('.tpm-map-event-focus-pin-title');
  const s = wrap.querySelector('.tpm-map-event-focus-pin-sub');
  if (t) t.textContent = title;
  if (s) {
    s.textContent = sub;
    s.hidden = !sub;
  }
  return wrap;
}

function syncEventFocusChrome(root, state) {
  const canvas = root.querySelector('.tpm-map-canvas');
  if (!canvas) return;
  const ev = resolveSelectedEvent(state);
  let bar = canvas.querySelector('[data-tpm="map-event-focus"]');
  if (!ev) {
    if (bar) bar.remove();
    canvas.classList.remove('has-event-focus');
    return;
  }
  canvas.classList.add('has-event-focus');
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'tpm-map-event-focus';
    bar.setAttribute('data-tpm', 'map-event-focus');
    bar.innerHTML =
      '<span class="tpm-map-event-focus-dot" aria-hidden="true"></span>' +
      '<span class="tpm-map-event-focus-body">' +
      '<span class="tpm-map-event-focus-kicker"></span>' +
      '<span class="tpm-map-event-focus-title"></span>' +
      '<span class="tpm-map-event-focus-sub"></span></span>' +
      '<button type="button" class="tpm-map-event-focus-close" data-tpm="map-event-focus-close" aria-label="Fechar visão do evento">×</button>';
    canvas.appendChild(bar);
  }
  const dot = bar.querySelector('.tpm-map-event-focus-dot');
  const kickerEl = bar.querySelector('.tpm-map-event-focus-kicker');
  const titleEl = bar.querySelector('.tpm-map-event-focus-title');
  const subEl = bar.querySelector('.tpm-map-event-focus-sub');
  if (dot) dot.style.background = ev.color || '#6d54e8';
  if (kickerEl) kickerEl.textContent = ev.title || 'Evento';
  if (titleEl) titleEl.textContent = ev.cliente || '—';
  if (subEl) {
    const parts = [];
    if (ev.equipamento) parts.push(ev.equipamento);
    if (ev.sub) parts.push(ev.sub);
    subEl.textContent = parts.join(' · ') || '';
    subEl.hidden = !subEl.textContent;
  }
}

function syncLiveGoogleMap(root, state, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  const g = mapRoot?.__tpmGmap;
  if (!g?.map || !window.google?.maps) return;

  clearGoogleOverlays(mapRoot);
  syncEventFocusChrome(root, state);

  const live = state.live;
  const selected = resolveSelectedEvent(state);
  const workdayGate = syncLiveWorkdayOffChrome(root, state, opts);
  const hideLivePin = !!workdayGate.hidePin;
  const mapPos = hideLivePin ? null : resolveLiveMapPos(live);
  const hasLive = !!mapPos;
  const fromLunch = !!(!hideLivePin && live?.lunch?.active && resolveLiveLunchPin(live));
  const hasEvent =
    !!(selected && selected.lat != null && selected.lng != null && Number.isFinite(Number(selected.lat)) && Number.isFinite(Number(selected.lng)));

  if (!hasLive && !hasEvent) {
    if (hideLivePin) {
      g.lastLiveKey = `wd:${workdayGate.kind || 'off'}`;
      return;
    }
    ensureViewerMapCenter().then((center) => {
      if (!g.map || resolveLiveMapPos(getState(mapRoot).live) || resolveSelectedEvent(getState(mapRoot))) return;
      const key = `viewer:${center.lat.toFixed(4)},${center.lng.toFixed(4)}`;
      if (g.lastLiveKey !== key) {
        g.lastLiveKey = key;
        g.map.setCenter(center);
        g.map.setZoom(12);
      }
    });
    return;
  }

  if (hasLive && !hasEvent) {
    const lat = mapPos.lat;
    const lng = mapPos.lng;
    const pos = { lat, lng };
    const accuracyM = fromLunch ? NaN : Number(live.accuracyM);

    if (Number.isFinite(accuracyM) && accuracyM > 0) {
      g.accuracyCircle = new window.google.maps.Circle({
        map: g.map,
        center: pos,
        radius: accuracyM,
        clickable: false,
        fillColor: '#6d54e8',
        fillOpacity: 0.18,
        strokeColor: '#6d54e8',
        strokeOpacity: 0.42,
        strokeWeight: 1,
      });
    }

    let staleBubble = fromLunch ? 'Em almoço' : resolveLiveStaleBubble(live);

    const el = buildLiveMarkerEl({
      avatarUrl: opts.avatarPhotoUrl || null,
      initials: opts.initials || '•',
      bubbleText: staleBubble,
      lunch: fromLunch,
    });
    const overlay = createPinOverlay(window.google.maps, {
      lat,
      lng,
      el,
      anchor: 'center',
    });
    overlay.setMap(g.map);
    g.overlays.push(overlay);
  }

  if (hasEvent) {
    const elat = Number(selected.lat);
    const elng = Number(selected.lng);
    const eventEl = buildEventFocusPinEl(selected);
    const eventOverlay = createPinOverlay(window.google.maps, {
      lat: elat,
      lng: elng,
      el: eventEl,
      anchor: 'bottom',
    });
    eventOverlay.setMap(g.map);
    g.overlays.push(eventOverlay);
  }

  const focusPos = hasEvent
    ? { lat: Number(selected.lat), lng: Number(selected.lng) }
    : { lat: mapPos.lat, lng: mapPos.lng };
  const key = [
    hasEvent
      ? 'hidelive'
      : hideLivePin
        ? `wd:${workdayGate.kind || 'off'}`
        : hasLive
        ? `${fromLunch ? 'lunch:' : ''}${mapPos.lat.toFixed(5)},${mapPos.lng.toFixed(5)}`
        : 'nolive',
    hasEvent ? `ev:${selected.eventKey}:${Number(selected.lat).toFixed(5)},${Number(selected.lng).toFixed(5)}` : 'noev',
  ].join('|');

  if (g.lastLiveKey !== key) {
    g.lastLiveKey = key;
    g.map.setCenter(focusPos);
    if (g.map.getZoom() < 14) g.map.setZoom(15);
    window.google.maps.event.addListenerOnce(g.map, 'idle', () => {
      (g.overlays || []).forEach((o) => {
        if (typeof o.draw === 'function') o.draw();
      });
    });
  } else if (hasEvent) {
    g.map.panTo(focusPos);
  } else if (hasLive) {
    g.map.panTo(focusPos);
  }
}

function liveEventIconKeys(ev) {
  const action = String(ev?.typeAction || '')
    .trim()
    .toLowerCase();
  const types = uniqueTrackOpTypes(ev?.operationTypes);
  if (action.startsWith('deslocamento')) {
    return types.length ? ['travel', ...types] : ['travel'];
  }
  if (
    action.startsWith('atendimento') ||
    action.includes('entrega') ||
    action.includes('retirada')
  ) {
    if (types.length) return types;
    if (action.includes('entrega')) return ['entrega'];
    if (action.includes('retirada')) return ['retirada'];
    return ['atendimento'];
  }
  if (action === 'jornada inicio') return ['sunrise'];
  if (action === 'jornada fim') return ['sunset'];
  return types;
}

const LIVE_PAIR_FAMILIES = [
  { start: ['deslocamento inicio'], end: ['deslocamento fim', 'deslocamento redirecionado'] },
  { start: ['atendimento inicio'], end: ['atendimento fim'] },
  { start: ['entrega inicio'], end: ['entrega fim'] },
  { start: ['retirada inicio'], end: ['retirada fim'] },
];

function liveEventOsKeys(ev) {
  return (ev?.operationCodes || [])
    .map((c) => String(c ?? '').trim().replace(/^0+/, '') || '0')
    .filter(Boolean);
}

/** Junta fim/cancelamento com o início (mesma família + OS) em um item com os dois horários. Lista desc. */
function groupLiveEventPairs(events) {
  const list = Array.isArray(events) ? events : [];
  const used = new Set();
  const out = [];
  list.forEach((ev, i) => {
    if (used.has(i)) return;
    const action = String(ev?.typeAction || '').trim().toLowerCase();
    const fam = LIVE_PAIR_FAMILIES.find((f) => f.end.includes(action));
    if (!fam) {
      out.push(ev);
      return;
    }
    const keys = liveEventOsKeys(ev);
    let startIdx = -1;
    for (let j = i + 1; j < list.length; j += 1) {
      if (used.has(j)) continue;
      const cand = list[j];
      const candAction = String(cand?.typeAction || '').trim().toLowerCase();
      if (fam.end.includes(candAction)) {
        const candKeys = liveEventOsKeys(cand);
        if (!keys.length || candKeys.some((k) => keys.includes(k))) break;
        continue;
      }
      if (!fam.start.includes(candAction)) continue;
      const candKeys = liveEventOsKeys(cand);
      if (!keys.length || !candKeys.length || candKeys.some((k) => keys.includes(k))) {
        startIdx = j;
        break;
      }
    }
    if (startIdx < 0) {
      out.push(ev);
      return;
    }
    used.add(startIdx);
    const start = list[startIdx];
    const pick = (k) => (ev[k] != null && ev[k] !== '' ? ev[k] : start[k]);
    const endMapable = !!ev.mapSelectable;
    const endPhoto = !!ev.photoSelectable && !!ev.photoViewUrl;
    out.push({
      ...start,
      ...ev,
      eventKey: endMapable || !start.mapSelectable ? ev.eventKey : start.eventKey,
      mapSelectable: endMapable || !!start.mapSelectable,
      photoSelectable: endPhoto || !!start.photoSelectable,
      photoViewUrl: endPhoto ? ev.photoViewUrl : start.photoViewUrl || ev.photoViewUrl,
      sub: pick('sub'),
      distanceKm: pick('distanceKm'),
      avgSpeedKmh: pick('avgSpeedKmh'),
      cancelled: !!ev.cancelled || !!start.cancelled,
      cancelReason: pick('cancelReason'),
      title: start.cancelled && !ev.cancelled ? start.title : ev.title,
      color: start.cancelled && !ev.cancelled ? start.color : ev.color,
      startTime: start.time || '—',
      endTime: ev.time || '—',
    });
  });
  return out;
}

/** No mesmo minuto: atendimento iniciado, depois aguardando, depois deslocamento finalizado. */
const SAME_MIN_STACK = ['atendimento inicio', 'aguardando atendimento', 'deslocamento fim'];

function liveEventMinute(ev) {
  const m = String(ev?.endTime || ev?.time || '').match(/\d{2}:\d{2}/);
  return m ? m[0] : '';
}

function stackAction(ev) {
  const action = String(ev?.typeAction || '').trim().toLowerCase();
  if (action === 'atendimento inicio' && ev?.cancelled) return '';
  return action;
}

function orderSameMinuteServiceStack(events) {
  const list = Array.isArray(events) ? events : [];
  const byMinute = new Map();
  list.forEach((ev, i) => {
    const minute = liveEventMinute(ev);
    const action = stackAction(ev);
    if (!minute || !SAME_MIN_STACK.includes(action)) return;
    if (!byMinute.has(minute)) byMinute.set(minute, []);
    byMinute.get(minute).push(i);
  });
  const rankAt = new Map();
  byMinute.forEach((indexes) => {
    const actions = new Set(indexes.map((i) => stackAction(list[i])));
    if (!SAME_MIN_STACK.every((action) => actions.has(action))) return;
    indexes.forEach((i) => rankAt.set(i, SAME_MIN_STACK.indexOf(stackAction(list[i]))));
  });
  if (!rankAt.size) return list;
  return list
    .map((ev, i) => ({ ev, i, rank: rankAt.has(i) ? rankAt.get(i) : null }))
    .sort((a, b) => {
      if (liveEventMinute(a.ev) === liveEventMinute(b.ev) && a.rank != null && b.rank != null && a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      return a.i - b.i;
    })
    .map((row) => row.ev);
}

function renderLiveSide(root, state, opts = {}) {
  const live = state.live;
  const agoEl = root.querySelector('[data-tpm="map-live-ago"]');
  const batteryEl = root.querySelector('[data-tpm="map-battery"]');
  const statusEl = root.querySelector('[data-tpm="map-status"]');
  const headerStatus = root.querySelector('[data-tpm="status"]');
  const nextLabelEl = root.querySelector('[data-tpm="map-next-label"]');
  const nextClientEl = root.querySelector('[data-tpm="map-next-client"]');
  const nextMetaEl = root.querySelector('[data-tpm="map-next-meta"]');
  const eventsWrap = root.querySelector('[data-tpm="map-events"]');
  const ago = formatLiveAgo(liveSyncAt(live));

  if (agoEl) {
    agoEl.textContent = state.liveLoading && !live ? '…' : ago || (live?.hasLocation ? 'agora' : '—');
  }
  if (batteryEl) {
    const pct = live?.batteryPct;
    if (pct == null || !Number.isFinite(Number(pct))) {
      batteryEl.textContent = '—';
      batteryEl.removeAttribute('data-tone');
    } else {
      const n = Math.round(Number(pct));
      batteryEl.textContent = `${n}%`;
      batteryEl.setAttribute('data-tone', batteryTone(n));
    }
  }

  const statusLabel =
    state.liveMonStatus != null || state.live?.workday || state.live?.lunch
      ? formatTechnicianLiveStatus(
          state.liveMonStatus,
          state.liveDay,
          state.live?.workday,
          state.live?.lunch
        )
      : opts.statusLabel != null && String(opts.statusLabel).trim() !== ''
        ? String(opts.statusLabel)
        : headerStatus?.textContent?.trim() || '—';
  if (statusEl) {
    statusEl.textContent = statusLabel;
    statusEl.classList.toggle('is-lunch', !!state.live?.lunch?.active);
    if (state.live?.lunch?.active) statusEl.setAttribute('data-tone', 'danger');
    else statusEl.removeAttribute('data-tone');
  }
  if (headerStatus && (state.liveMonStatus != null || state.live?.workday || state.live?.lunch)) {
    headerStatus.textContent = statusLabel;
    headerStatus.classList.toggle('is-lunch', !!state.live?.lunch?.active);
  }

  const openOrders =
    state.liveOpenOrders != null ? state.liveOpenOrders : opts.openOrders || [];
  const next = resolveNextDestination(openOrders, state.liveDay);
  if (nextLabelEl) nextLabelEl.textContent = next.label;
  if (nextClientEl) nextClientEl.textContent = next.client;
  if (nextMetaEl) nextMetaEl.textContent = next.meta;

  // Premissa: quando liveEvents for array (API), substitui o mock.
  if (eventsWrap && Array.isArray(state.liveEvents)) {
    const esc = (v) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    if (!state.liveEvents.length) {
      eventsWrap.innerHTML =
        '<span class="tpm-map-section-label">Últimos eventos</span>' +
        '<div class="tpm-map-events-empty">Sem eventos recentes</div>';
    } else {
      const selectedKey = state.selectedEventKey || '';
      eventsWrap.innerHTML =
        '<span class="tpm-map-section-label">Últimos eventos</span>' +
        orderSameMinuteServiceStack(groupLiveEventPairs(state.liveEvents))
          .map((ev) => {
            const mapable = !!ev.mapSelectable;
            const photoable = !!ev.photoSelectable && !!ev.photoViewUrl;
            const clickable = mapable || photoable;
            const selected = mapable && ev.eventKey && ev.eventKey === selectedKey;
            const tag = clickable ? 'button' : 'div';
            const typeAttr = clickable ? ' type="button"' : '';
            const lunchEv =
              String(ev.typeAction || '').toLowerCase().includes('almoco') ||
              String(ev.eventKey || '').startsWith('lunch-');
            const cancelledEv = !!ev.cancelled;
            const cancelReason =
              ev.cancelReason != null && String(ev.cancelReason).trim()
                ? String(ev.cancelReason).trim()
                : '';
            const cls = [
              'tpm-map-event',
              mapable ? ' is-mapable' : '',
              photoable ? ' is-photoable' : '',
              selected ? ' is-selected' : '',
              lunchEv ? ' is-lunch' : '',
              cancelledEv ? ' is-cancelled' : '',
            ].join('');
            const data =
              ` data-tpm="map-event"` +
              (mapable ? ` data-mapable="1" data-event-key="${esc(ev.eventKey || '')}"` : '') +
              (photoable
                ? ` data-photoable="1" data-photo-url="${esc(ev.photoViewUrl)}" data-photo-title="${esc(
                    ev.title || 'Foto'
                  )}" data-photo-sub="${esc(ev.sub || '')}"`
                : '');
            return (
              `<${tag}${typeAttr} class="${cls}"${data}>` +
              (ev.startTime
                ? `<span class="tpm-map-event-t is-range" title="Início ${esc(ev.startTime)} · Fim ${esc(ev.endTime)}">` +
                  `<span class="tpm-map-event-t-start">${esc(ev.startTime)}</span>` +
                  `<span class="tpm-map-event-t-end">${esc(ev.endTime)}</span></span>`
                : `<span class="tpm-map-event-t">${esc(ev.time || '—')}</span>`) +
              `<span class="tpm-map-event-dot" style="background:${esc(ev.color || '#a8a8b4')}"></span>` +
              `<span class="tpm-map-event-body">` +
              `<span class="tpm-map-event-title-row">` +
              `<span class="tpm-map-event-title">${esc(ev.title || '—')}</span>` +
              `</span>` +
              (ev.sub
                ? `<span class="tpm-map-event-sub">${trackTripIconsHtml(liveEventIconKeys(ev))}<span class="tpm-map-event-sub-text">${esc(ev.sub)}</span></span>`
                : '<span class="tpm-map-event-sub"></span>') +
              travelMetricsHtml(ev.distanceKm, ev.avgSpeedKmh, {
                className: 'tpm-map-event-stats',
              }) +
              (cancelReason
                ? `<span class="tpm-map-event-cancel">${esc(cancelReason)}</span>`
                : '') +
              `</span></${tag}>`
            );
          })
          .join('');
    }
  }
}

function clusterPins(spots) {
  const map = new Map();
  spots.forEach((s) => {
    const key =
      s.lat != null && s.lng != null
        ? `${Number(s.lat).toFixed(5)},${Number(s.lng).toFixed(5)}`
        : `os:${s.os}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        x: s.x,
        y: s.y,
        client: s.client,
        lat: s.lat,
        lng: s.lng,
        orders: [s],
      });
    } else {
      map.get(key).orders.push(s);
    }
  });
  return [...map.values()];
}

function spotByOs(state, os) {
  return (state.spotsByDay[state.day] || []).find((s) => s.os === os);
}

/** Rastreio só no App Go Premium (id_produto = 4, via /api/auth/verify → go_id_produto). */
function allowTrackMode(opts = {}) {
  return Number(opts.goIdProduto) === 4;
}

function applyTrackModeGate(root, mapRoot, state, opts = {}) {
  const allow = allowTrackMode(opts);
  root.querySelectorAll('[data-tpm="map-mode"][data-mode="track"]').forEach((btn) => {
    btn.hidden = !allow;
  });
  if (!allow && state.mode === 'track') {
    state.mode = 'live';
  }
}

function syncModeUi(root, mapRoot, state) {
  const mode = state.mode === 'plan' || state.mode === 'track' ? state.mode : 'live';
  mapRoot.setAttribute('data-map-mode', mode);
  mapRoot.setAttribute('data-plan-day', state.day);
  root.querySelectorAll('[data-tpm="map-mode"]').forEach((btn) => {
    btn.classList.toggle('is-on', btn.getAttribute('data-mode') === state.mode);
  });
  const dayTabs = root.querySelector('[data-tpm="map-day-tabs"]');
  const daySep = root.querySelector('[data-tpm="map-day-sep"]');
  const sideLive = root.querySelector('[data-tpm="map-side-live"]');
  const sidePlan = root.querySelector('[data-tpm="map-side-plan"]');
  const sideTrack = root.querySelector('[data-tpm="map-side-track"]');
  const hint = root.querySelector('[data-tpm="map-hint"]');
  if (dayTabs) dayTabs.hidden = mode !== 'plan';
  if (daySep) daySep.hidden = mode !== 'plan';
  if (sideLive) sideLive.hidden = mode !== 'live';
  if (sidePlan) sidePlan.hidden = mode !== 'plan';
  if (sideTrack) sideTrack.hidden = mode !== 'track';
  if (hint) {
    if (state.geocoding) hint.textContent = 'Carregando coordenadas…';
    else if (state.mode === 'plan' && state.loading) hint.textContent = 'Carregando OS…';
    else if (state.mode === 'track') {
      if (state.trackLoading) hint.textContent = 'Carregando rastreio…';
      else if (state.trackError) hint.textContent = state.trackError;
      else if (!(state.trackPoints || []).length) hint.textContent = 'Sem pontos de rastreio neste dia';
      else {
        const first = state.trackPoints[0]?.timeBr || '';
        const last = state.trackPoints[state.trackPoints.length - 1]?.timeBr || '';
        hint.textContent = first && last ? `${first} → ${last}` : state.trackDate || 'Rastreio';
      }
    } else if (state.mode === 'live') {
      const wdGate = resolveLiveWorkdayGate(state.live, liveTechDisplayName(root));
      if (state.liveLoading && !state.live) hint.textContent = 'Carregando posição…';
      else if (state.liveError) hint.textContent = state.liveError;
      else if (wdGate.hidePin) hint.textContent = '';
      else hint.textContent = formatLiveHint(state.live);
    } else {
      hint.textContent = 'Clique nos pontos na ordem de atendimento';
    }
    const unlinked = isLiveUnlinkedState(state);
    hint.classList.toggle('is-live-unlinked', unlinked);
    if (unlinked) {
      const msg = String(hint.textContent || '').trim();
      hint.innerHTML = `<span class="tpm-map-hint-label">${escHtml(msg)}</span>`;
    }
  }
  const canvas = root.querySelector('.tpm-map-canvas');
  if (canvas) canvas.classList.toggle('is-live-unlinked', isLiveUnlinkedState(state));
  syncStartPointUi(root, state);
}

function renderStartPointsMenuHtml(items, selectedId) {
  if (!items?.length) {
    return '<div class="tpm-map-start-empty">Nenhum ponto disponível</div>';
  }
  return items
    .map((item) => {
      const icon = item.tipo === 'tecnico' ? ICON_HOME_SVG : ICON_BUILDING_SVG;
      const on = selectedId && selectedId === item.id ? ' is-on' : '';
      const hasAddr = String(item.endereco || '').replace(/[,\s-]+/g, '').length > 0;
      return (
        `<button type="button" class="tpm-map-start-option${on}" role="option"` +
        ` data-tpm="map-start-option" data-id="${escHtml(item.id)}"` +
        (hasAddr ? '' : ' disabled data-tpm-tip="Endereço não cadastrado"') +
        `>` +
        `<span class="tpm-map-start-option-icon" aria-hidden="true">${icon}</span>` +
        `<span class="tpm-map-start-option-text">` +
        `<span class="tpm-map-start-option-label">${escHtml(item.origem)}</span>` +
        (!hasAddr
          ? `<span class="tpm-map-start-option-sub">Sem endereço</span>`
          : '') +
        `</span></button>`
      );
    })
    .join('');
}

function syncStartPointUi(root, state) {
  const wrap = root.querySelector('[data-tpm="map-start"]');
  if (!wrap) return;
  const isPlan = state.mode === 'plan';
  wrap.hidden = !isPlan;
  if (!isPlan) state.startPointsOpen = false;

  const trigger = wrap.querySelector('[data-tpm="map-start-trigger"]');
  const chip = wrap.querySelector('[data-tpm="map-start-chip"]');
  const chipLabel = wrap.querySelector('[data-tpm="map-start-chip-label"]');
  const menu = wrap.querySelector('[data-tpm="map-start-menu"]');
  const selected = state.startPoint;
  const open = isPlan && !!state.startPointsOpen;

  if (trigger) {
    trigger.hidden = Boolean(selected);
    trigger.classList.toggle('is-open', open);
    trigger.classList.toggle('is-busy', !!state.startPointBusy && !selected);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  if (chip) {
    chip.hidden = !selected;
    chip.classList.toggle('is-busy', !!state.startPointBusy && !!selected);
    if (chipLabel) chipLabel.textContent = selected?.origem || '';
  }
  if (menu) {
    menu.hidden = !open;
    if (open) {
      if (state.startPointsList == null && !state.startPointsError) {
        menu.innerHTML = '<div class="tpm-map-start-empty">Carregando…</div>';
      } else if (state.startPointsError) {
        menu.innerHTML = `<div class="tpm-map-start-empty">${escHtml(state.startPointsError)}</div>`;
      } else {
        menu.innerHTML = renderStartPointsMenuHtml(state.startPointsList || [], selected?.id);
      }
    }
  }
}

async function ensureStartPointsList(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  if (Array.isArray(state.startPointsList)) return;
  try {
    state.startPointsError = null;
    paint(root, opts);
    const items = await fetchTechnicianPanelMapStartPoints(opts.technicianId);
    if (getState(mapRoot).loadGen !== state.loadGen) return;
    state.startPointsList = Array.isArray(items) ? items : [];
  } catch (err) {
    if (getState(mapRoot).loadGen !== state.loadGen) return;
    state.startPointsError = err?.message || 'Erro ao carregar origens';
    state.startPointsList = [];
  }
  paint(root, opts);
}

async function selectMapStartPoint(root, opts, itemId) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  const item = (state.startPointsList || []).find((x) => x.id === itemId);
  if (!item) return;
  const address = String(item.endereco || '').trim();
  if (!address || address.replace(/[,\s-]+/g, '') === '') {
    window.alert('Este ponto não possui endereço cadastrado.');
    return;
  }

  state.startPointsOpen = false;
  state.startPointBusy = true;
  paint(root, opts);

  try {
    const geoResults = await geocodeBatch([{ orderId: item.id, address }]);
    if (getState(mapRoot).loadGen !== state.loadGen) return;
    const hit = Array.isArray(geoResults)
      ? geoResults.find((g) => String(g.orderId || '') === String(item.id))
      : null;
    if (!hit || hit.error || !hasValidMapCoords(hit.lat, hit.lng)) {
      window.alert(
        hit?.error || 'Não foi possível obter coordenadas para o endereço selecionado.'
      );
      return;
    }
    state.startPoint = {
      id: item.id,
      origem: item.origem,
      endereco: address,
      tipo: item.tipo,
      lat: parseCoord(hit.lat),
      lng: parseCoord(hit.lng),
    };
  } catch (err) {
    if (getState(mapRoot).loadGen !== state.loadGen) return;
    window.alert(err?.message || 'Erro ao geocodificar o ponto de partida.');
  } finally {
    if (getState(mapRoot).loadGen === state.loadGen) {
      state.startPointBusy = false;
      paint(root, opts, { fit: true });
    }
  }
}

function clearMapStartPoint(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  state.startPoint = null;
  state.startPointsOpen = false;
  state.startPointBusy = false;
  paint(root, opts, { fit: true });
}

function setCoordsLoading(root, on, opts = {}) {
  let el = root.querySelector('[data-tpm="map-coords-loading"]');
  if (!el) {
    const canvas = root.querySelector('.tpm-map-canvas');
    if (!canvas) return;
    el = document.createElement('div');
    el.className = 'tpm-map-coords-loading';
    el.setAttribute('data-tpm', 'map-coords-loading');
    canvas.appendChild(el);
  }
  const label =
    opts.label ||
    (opts.count
      ? `Buscando coordenadas (${opts.count})…`
      : 'Carregando coordenadas…');
  el.innerHTML =
    `<span class="tpm-map-coords-spinner" aria-hidden="true"></span>` +
    `<span class="tpm-map-coords-label">${escHtml(label)}</span>` +
    (opts.sub
      ? `<span class="tpm-map-coords-sub">${escHtml(opts.sub)}</span>`
      : '');
  el.classList.toggle('is-busy-banner', !!opts.banner);
  el.hidden = !on;
}

/** Overlay central no mapa quando carrega OS / geocode (modo Roteirizar). */
function setPlanBusyOverlay(root, state) {
  const canvas = root.querySelector('.tpm-map-canvas');
  if (!canvas) return;
  let el = root.querySelector('[data-tpm="map-plan-busy"]');
  const busy =
    state.mode === 'plan' && (state.loading || state.geocoding || state.geocodeJobs > 0);
  if (!busy) {
    if (el) el.hidden = true;
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tpm-map-plan-busy';
    el.setAttribute('data-tpm', 'map-plan-busy');
    canvas.appendChild(el);
  }
  const geocoding = state.geocoding || state.geocodeJobs > 0;
  const title = geocoding
    ? 'Localizando endereços'
    : 'Carregando ordens de serviço';
  const sub = geocoding
    ? state.geocodingCount > 0
      ? `Consultando geolocalização de ${state.geocodingCount} endereço${state.geocodingCount === 1 ? '' : 's'}…`
      : 'Consultando geolocalização…'
    : 'Buscando OS de Hoje e Amanhã…';
  el.innerHTML =
    `<div class="tpm-map-plan-busy-card">` +
    `<span class="tpm-map-plan-busy-spinner" aria-hidden="true"></span>` +
    `<div class="tpm-map-plan-busy-text">` +
    `<strong>${escHtml(title)}</strong>` +
    `<span>${escHtml(sub)}</span>` +
    `</div></div>`;
  el.hidden = false;
}

function getUnlocatedSpots(state, day) {
  const d = day || state.day;
  return (state.spotsByDay[d] || []).filter((s) => isSpotUnlocated(s));
}

function closeUnlocatedModal() {
  document.querySelectorAll('[data-tpm="map-unlocated-modal"]').forEach((el) => el.remove());
}

function closeMapOsDetailModal() {
  closeMapOsFotosModal();
  document.querySelectorAll('[data-tpm="map-os-detail-modal"]').forEach((el) => el.remove());
}

function resolveMapOsFotoSrc(foto) {
  if (!foto || typeof foto !== 'object') return '';
  if (foto.url != null && String(foto.url).trim()) return String(foto.url).trim();
  if (foto.base64 != null && String(foto.base64).trim()) {
    const mime = foto.mimeType || 'image/jpeg';
    return `data:${mime};base64,${foto.base64}`;
  }
  return '';
}

function resolveMapOsVideoThumbSrc(src) {
  const s = String(src || '').trim();
  if (!s || s.startsWith('data:')) return s;
  if (/#t=/i.test(s)) return s;
  return `${s}#t=0.1`;
}

function isMapOsFotoVideo(foto) {
  if (!foto || typeof foto !== 'object') return false;
  if (foto.isVideo === true) return true;
  return String(foto.mimeType || '')
    .toLowerCase()
    .startsWith('video/');
}

function closeMapOsFotosModal() {
  document.querySelectorAll('[data-tpm="map-os-fotos-modal"]').forEach((el) => el.remove());
  document.querySelectorAll('[data-tpm="map-os-fotos-zoom"]').forEach((el) => el.remove());
}

function openMapOsFotosModal({ os, equip, serie, fotos, startIdx = 0 }) {
  closeMapOsFotosModal();
  const list = Array.isArray(fotos) ? fotos.filter((f) => resolveMapOsFotoSrc(f)) : [];
  let selectedIdx = Math.max(0, Math.min(Number(startIdx) || 0, Math.max(0, list.length - 1)));

  const overlay = document.createElement('div');
  overlay.className = 'tpm-os-fotos-overlay';
  overlay.setAttribute('data-tpm', 'map-os-fotos-modal');
  overlay.setAttribute('role', 'presentation');

  const render = () => {
    const current = list[selectedIdx] || null;
    const src = current ? resolveMapOsFotoSrc(current) : '';
    const video = current ? isMapOsFotoVideo(current) : false;
    const thumbs =
      list.length === 0
        ? ''
        : list
            .map((foto, idx) => {
              const tSrc = resolveMapOsFotoSrc(foto);
              const tVideo = isMapOsFotoVideo(foto);
              const label = escHtml(foto.operacao || (tVideo ? `Vídeo ${idx + 1}` : `Foto ${idx + 1}`));
              return (
                `<button type="button" class="tpm-os-fotos-thumb${idx === selectedIdx ? ' is-active' : ''}${
                  tVideo ? ' tpm-os-fotos-thumb--video' : ''
                }" data-tpm-foto-idx="${idx}" title="${label}" aria-label="${label}">` +
                (tVideo
                  ? `<video class="tpm-os-fotos-thumb-preview" src="${escHtml(resolveMapOsVideoThumbSrc(tSrc))}" muted playsinline preload="metadata" aria-hidden="true"></video>` +
                    `<span class="tpm-os-fotos-thumb-video" aria-hidden="true">` +
                    `<span class="tpm-os-fotos-thumb-video-icon">▶</span>` +
                    `<span class="tpm-os-fotos-thumb-video-label">Vídeo</span>` +
                    `</span>`
                  : `<img src="${escHtml(tSrc)}" alt="${label}" />`) +
                `</button>`
              );
            })
            .join('');

    overlay.innerHTML =
      `<div class="tpm-os-fotos-modal" role="dialog" aria-modal="true" aria-label="Fotos da OS ${escHtml(os || '')}">` +
      `<div class="tpm-os-fotos-header">` +
      `<div class="tpm-os-fotos-header-info">` +
      `<h3 class="tpm-os-fotos-title">Fotos da OS ${escHtml(os || '—')}</h3>` +
      `<div class="tpm-os-fotos-subtitle"><span>${escHtml(equip || '—')}</span><span>•</span>` +
      `<span>Série: ${escHtml(serie || '—')}</span></div></div>` +
      `<button type="button" class="tpm-os-fotos-close" data-tpm="map-os-fotos-close" aria-label="Fechar">✕</button>` +
      `</div>` +
      `<div class="tpm-os-fotos-body">` +
      (list.length === 0
        ? `<div class="tpm-os-fotos-empty">Nenhuma foto encontrada para esta OS.</div>`
        : `<div class="tpm-os-fotos-layout">` +
          `<div class="tpm-os-fotos-thumbs">${thumbs}</div>` +
          `<div class="tpm-os-fotos-main">` +
          (current
            ? `<figure class="tpm-os-fotos-item"><div class="tpm-os-fotos-wrapper">` +
              (video
                ? `<video class="tpm-os-fotos-img tpm-os-fotos-video" src="${escHtml(src)}" controls playsinline preload="metadata" controlslist="nodownload"></video>`
                : `<img class="tpm-os-fotos-img" src="${escHtml(src)}" alt="Foto ${selectedIdx + 1} da OS ${escHtml(os || '')}" />` +
                  `<button type="button" class="tpm-os-fotos-zoom-btn" data-tpm="map-os-fotos-zoom" aria-label="Ampliar foto">` +
                  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">` +
                  `<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg></button>`) +
              `</div>` +
              (current.operacao
                ? `<figcaption class="tpm-os-fotos-caption">${escHtml(String(current.operacao))}</figcaption>`
                : '') +
              `</figure>`
            : '') +
          `</div></div>`) +
      `</div></div>`;
  };

  const openZoom = () => {
    const current = list[selectedIdx];
    if (!current || isMapOsFotoVideo(current)) return;
    const src = resolveMapOsFotoSrc(current);
    if (!src) return;
    document.querySelectorAll('[data-tpm="map-os-fotos-zoom"]').forEach((el) => el.remove());
    const z = document.createElement('div');
    z.className = 'tpm-os-fotos-overlay tpm-os-fotos-overlay--zoom';
    z.setAttribute('data-tpm', 'map-os-fotos-zoom');
    z.innerHTML =
      `<div class="tpm-os-fotos-zoom-modal">` +
      `<button type="button" class="tpm-os-fotos-close" data-tpm="map-os-fotos-zoom-close" aria-label="Fechar">✕</button>` +
      `<div class="tpm-os-fotos-zoom-body">` +
      `<img class="tpm-os-fotos-zoom-img" src="${escHtml(src)}" alt="Foto ampliada da OS ${escHtml(os || '')}" />` +
      `</div></div>`;
    z.addEventListener('click', (e) => {
      if (e.target === z || e.target.closest('[data-tpm="map-os-fotos-zoom-close"]')) z.remove();
    });
    document.body.appendChild(z);
  };

  const onKey = (e) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    const zoom = document.querySelector('[data-tpm="map-os-fotos-zoom"]');
    if (zoom) {
      zoom.remove();
      return;
    }
    closeMapOsFotosModal();
  };
  const onClick = (e) => {
    if (e.target === overlay || e.target.closest('[data-tpm="map-os-fotos-close"]')) {
      e.preventDefault();
      closeMapOsFotosModal();
      return;
    }
    const thumb = e.target.closest('[data-tpm-foto-idx]');
    if (thumb) {
      selectedIdx = Number(thumb.getAttribute('data-tpm-foto-idx')) || 0;
      render();
      return;
    }
    if (e.target.closest('[data-tpm="map-os-fotos-zoom"]')) openZoom();
  };

  overlay.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey, true);
  const prevRemove = overlay.remove.bind(overlay);
  overlay.remove = () => {
    document.removeEventListener('keydown', onKey, true);
    document.querySelectorAll('[data-tpm="map-os-fotos-zoom"]').forEach((el) => el.remove());
    prevRemove();
  };

  render();
  document.body.appendChild(overlay);
}

function fillMapOsDetailFotosFooter(overlay, spot, fotos) {
  const footer = overlay.querySelector('[data-tpm="map-os-detail-fotos"]');
  if (!footer) return;
  const list = Array.isArray(fotos) ? fotos.filter((f) => resolveMapOsFotoSrc(f)) : [];
  const travelWrap = footer.querySelector('.tpm-map-os-detail-travel-wrap');
  const travelHtml = travelWrap ? travelWrap.outerHTML : '';
  if (!list.length) {
    if (travelHtml) {
      footer.hidden = false;
      footer.innerHTML = travelHtml;
    } else {
      footer.hidden = true;
      footer.innerHTML = '';
    }
    return;
  }
  const serie = String(spot.serie || '').trim() || '—';
  const equip = String(spot.equipamento || '').trim() || '—';
  footer.hidden = false;
  footer.innerHTML =
    travelHtml +
    `<div class="tpm-map-os-detail-fotos-label">Fotos</div>` +
    `<div class="tpm-map-os-detail-fotos-strip" role="list">` +
    list
      .map((foto, idx) => {
        const src = resolveMapOsFotoSrc(foto);
        const video = isMapOsFotoVideo(foto);
        const label = escHtml(foto.operacao || (video ? `Vídeo ${idx + 1}` : `Foto ${idx + 1}`));
        return (
          `<button type="button" class="tpm-map-os-detail-foto-thumb${video ? ' is-video' : ''}" ` +
          `data-tpm-foto-open="${idx}" role="listitem" title="${label}" aria-label="${label}">` +
          (video
            ? `<span aria-hidden="true">▶</span>`
            : `<img src="${escHtml(src)}" alt="${label}" loading="lazy" />`) +
          `</button>`
        );
      })
      .join('') +
    `</div>`;

  footer.onclick = (e) => {
    const btn = e.target.closest('[data-tpm-foto-open]');
    if (!btn) return;
    e.preventDefault();
    openMapOsFotosModal({
      os: spot.os,
      equip,
      serie,
      fotos: list,
      startIdx: Number(btn.getAttribute('data-tpm-foto-open')) || 0,
    });
  };
}

async function loadMapOsDetailFotos(overlay, spot) {
  if (!overlay?.isConnected || !spot?.os) return;
  const footer = overlay.querySelector('[data-tpm="map-os-detail-fotos"]');
  const travelWrap = footer?.querySelector('.tpm-map-os-detail-travel-wrap');
  const travelHtml = travelWrap ? travelWrap.outerHTML : '';
  if (footer) {
    footer.hidden = false;
    footer.innerHTML =
      travelHtml + `<div class="tpm-map-os-detail-fotos-loading">Carregando fotos…</div>`;
  }
  try {
    const data = await fetchTechnicianPanelOsDetail(spot.os);
    if (!overlay.isConnected) return;
    fillMapOsDetailFotosFooter(overlay, spot, data?.fotos || []);
  } catch (_) {
    if (!overlay.isConnected) return;
    fillMapOsDetailFotosFooter(overlay, spot, []);
  }
}

/** Soma km / média de velocidade dos deslocamentos da OS na timeline do rastreio. */
function resolveOsTravelStats(state, osCode) {
  const code = String(osCode || '').trim();
  if (!code || !state) return null;
  const items = Array.isArray(state.trackTimeline) ? state.trackTimeline : [];
  let distSum = 0;
  let hasDist = false;
  let speedWeighted = 0;
  let weight = 0;
  let speedSum = 0;
  let speedN = 0;
  let found = false;
  for (const item of items) {
    if (!item || (item.kind && item.kind !== 'travel')) continue;
    const codes = (item.operationCodes || []).map((c) => String(c).trim());
    if (!codes.includes(code)) continue;
    found = true;
    const d = item.distanceKm != null ? Number(item.distanceKm) : null;
    const s = item.avgSpeedKmh != null ? Number(item.avgSpeedKmh) : null;
    if (d != null && Number.isFinite(d) && d > 0) {
      distSum += d;
      hasDist = true;
      if (s != null && Number.isFinite(s) && s >= 0) {
        speedWeighted += s * d;
        weight += d;
      }
    } else if (s != null && Number.isFinite(s) && s >= 0) {
      speedSum += s;
      speedN += 1;
    }
  }
  if (!found) return null;
  const avgSpeedKmh =
    weight > 0
      ? Math.round((speedWeighted / weight) * 10) / 10
      : speedN
        ? Math.round((speedSum / speedN) * 10) / 10
        : null;
  const distanceKm = hasDist ? Math.round(distSum * 100) / 100 : null;
  if (distanceKm == null && avgSpeedKmh == null) return null;
  return { distanceKm, avgSpeedKmh };
}

export function openMapOsDetailModal(spot) {
  closeMapOsDetailModal();
  if (!spot?.os) return;

  const fromTrack = !!spot.fromTrack;
  const sla = formatPrevisao(spot.sla);
  // Rastreio: pin já está no mapa (GPS). Aviso de “sem coordenadas” é só do plano/roteirizar.
  const unmapped = !fromTrack && isSpotUnlocated(spot);
  const addr = String(spot.endereco || '').trim() || '—';
  const equip = String(spot.equipamento || '').trim() || '—';
  const serie = String(spot.serie || '').trim();
  const pat = String(spot.pat || '').trim();
  const bairro = String(spot.bairro || '').trim();
  const cidade = String(spot.cidade || '').trim();
  const place = [bairro, cidade].filter(Boolean).join(' · ');
  const addrLine = [place, addr !== '—' ? addr : ''].filter(Boolean).join(' · ') || '—';
  const seriePat = [serie && `Série ${serie}`, pat && `PAT ${pat}`].filter(Boolean).join(' · ') || '—';
  const defeito = String(spot.defeito || '').trim() || '—';
  const loading = !!spot.loadingExtras;
  const horariosVal = spot.window || (loading ? 'Carregando…' : '—');
  const condVal = spot.condNome || (loading ? 'Carregando…' : '—');
  const laudoVal = spot.laudo || (loading ? 'Carregando…' : '—');
  const travelStatsHtml = fromTrack
    ? travelMetricsHtml(spot.distanceKm, spot.avgSpeedKmh, {
        className: 'tpm-map-os-detail-travel-stats',
        showLabels: true,
      })
    : '';

  const overlay = document.createElement('div');
  overlay.className = 'tpm-unlocated-modal-overlay';
  overlay.setAttribute('data-tpm', 'map-os-detail-modal');
  overlay.setAttribute('role', 'presentation');

  overlay.innerHTML =
    `<div class="tpm-unlocated-modal tpm-map-os-detail-modal" role="dialog" aria-modal="true" aria-labelledby="tpm-os-detail-title">` +
    `<div class="tpm-unlocated-head">` +
    `<h3 id="tpm-os-detail-title" class="tpm-unlocated-title">` +
    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">` +
    `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>` +
    `<path d="M14 2v6h6"></path><path d="M8 13h8M8 17h5"></path></svg>` +
    ` Detalhe da OS ${
      spot.outsideRadius ? '(fora do raio) ' : ''
    }${escHtml(spot.os)}</h3>` +
    `<button type="button" class="tpm-unlocated-x" data-tpm="map-os-detail-close" aria-label="Fechar">` +
    `<svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4" fill="none"><path d="M5 5l14 14M19 5L5 19"></path></svg>` +
    `</button></div>` +
    `<div class="tpm-map-os-detail-body">` +
    `<div class="tpm-map-os-detail-client">${escHtml(spot.client || '—')}</div>` +
    (unmapped
      ? `<div class="tpm-map-os-detail-warn">Sem coordenadas — não aparece no mapa</div>`
      : '') +
    `<div class="tpm-map-os-detail-rows">` +
    `<div class="tpm-map-os-detail-row"><span>Equipamento</span><strong>${escHtml(equip)}</strong></div>` +
    `<div class="tpm-map-os-detail-row"><span>Identificação</span><strong>${escHtml(seriePat)}</strong></div>` +
    (fromTrack
      ? ''
      : `<div class="tpm-map-os-detail-row"><span>Previsão</span><strong style="color:${escHtml(sla.color)}">${escHtml(sla.label || '—')}</strong></div>`) +
    (Number(spot.returnCount) > 0
      ? `<div class="tpm-map-os-detail-row"><span>Retorno</span><strong>${buildReturnBadgeHtml(spot)}` +
        `${escHtml([spot.lastReturnCondition, formatErpDateTime(spot.lastReturnAt)].filter(Boolean).join(' · '))}</strong></div>`
      : '') +
    `<div class="tpm-map-os-detail-row tpm-map-os-detail-row--block"><span>${defeitoTerm()}</span><strong>${escHtml(defeito)}</strong></div>` +
    `<div class="tpm-map-os-detail-place">` +
    `<div class="tpm-map-os-detail-field"><span class="tpm-map-os-detail-field-title">Endereço</span>` +
    `<strong>${escHtml(addrLine)}</strong></div>` +
    `</div>` +
    `</div>` +
    (fromTrack
      ? `<div class="tpm-map-os-detail-closing">` +
        `<section class="tpm-map-os-detail-section">` +
        `<h4 class="tpm-map-os-detail-section-title">Horários</h4>` +
        `<p class="tpm-map-os-detail-section-body">${escHtml(horariosVal)}</p></section>` +
        `<section class="tpm-map-os-detail-section">` +
        `<h4 class="tpm-map-os-detail-section-title">Condição</h4>` +
        `<p class="tpm-map-os-detail-section-body">${escHtml(condVal)}</p></section>` +
        `<section class="tpm-map-os-detail-section">` +
        `<h4 class="tpm-map-os-detail-section-title">Laudo</h4>` +
        `<p class="tpm-map-os-detail-section-body tpm-map-os-detail-section-body--block">${escHtml(laudoVal)}</p></section>` +
        `</div>`
      : '') +
    `</div>` +
    `<div class="tpm-map-os-detail-footer" data-tpm="map-os-detail-fotos"${
      travelStatsHtml ? '' : ' hidden'
    }>` +
    (travelStatsHtml
      ? `<div class="tpm-map-os-detail-travel-wrap">${travelStatsHtml}</div>`
      : '') +
    `</div>` +
    `</div>`;

  const onKey = (e) => {
    if (e.key !== 'Escape') return;
    if (document.querySelector('[data-tpm="map-os-fotos-modal"], [data-tpm="map-os-fotos-zoom"]')) return;
    closeMapOsDetailModal();
  };
  const onClick = (e) => {
    if (e.target === overlay || e.target.closest('[data-tpm="map-os-detail-close"]')) {
      e.preventDefault();
      closeMapOsDetailModal();
    }
  };
  overlay.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey);
  const prevRemove = overlay.remove.bind(overlay);
  overlay.remove = () => {
    document.removeEventListener('keydown', onKey);
    prevRemove();
  };
  document.body.appendChild(overlay);
  void loadMapOsDetailFotos(overlay, spot);
}

function openOsDetailFromMap(root, os) {
  if (!os) return;
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  const spot = spotByOs(state, String(os).trim());
  if (!spot) return;
  openMapOsDetailModal(spot);
}

const trackOsSummaryCache = new Map();

function trackServiceWindow(pin) {
  const a = pin?.startBr || pin?.timeBr || '';
  const b = pin?.endBr || '';
  if (a && b) return `${a} → ${b}`;
  return a || b || '';
}

async function openTrackOsDetail(root, pin) {
  const os = pin?.os != null ? String(pin.os).trim() : '';
  if (!os) return;
  const mapRoot = root?.querySelector?.('[data-tpm="map"]');
  const state = mapRoot ? getState(mapRoot) : null;
  const travelStats = resolveOsTravelStats(state, os);
  const cached = trackOsSummaryCache.get(os);
  const fallbackWindow = trackServiceWindow(pin);
  const outsideRadius = !!pin.outsideRadius || !!cached?.outsideRadius;
  const travelFields = {
    distanceKm: travelStats?.distanceKm ?? cached?.distanceKm ?? null,
    avgSpeedKmh: travelStats?.avgSpeedKmh ?? cached?.avgSpeedKmh ?? null,
  };
  openMapOsDetailModal({
    os,
    fromTrack: true,
    outsideRadius,
    client: pin.cliente || cached?.client || '',
    equipamento: cached?.equipamento || '',
    serie: cached?.serie || '',
    pat: cached?.pat || '',
    bairro: cached?.bairro || '',
    cidade: cached?.cidade || '',
    endereco: cached?.endereco || '',
    defeito: cached?.defeito || '',
    sla: cached?.sla || null,
    window: cached?.window || fallbackWindow,
    condNome: cached?.condNome || '',
    laudo: cached?.laudo || '',
    loadingExtras: !cached,
    ...travelFields,
  });
  if (cached) return;
  try {
    const data = await fetchTechnicianPanelOsSummary(os);
    if (!data?.found) {
      openMapOsDetailModal({
        os,
        fromTrack: true,
        outsideRadius,
        client: pin.cliente || '',
        window: fallbackWindow,
        ...travelFields,
      });
      return;
    }
    const spot = {
      os,
      fromTrack: true,
      outsideRadius,
      client: data.client || pin.cliente || '',
      equipamento: data.equipamento || '',
      serie: data.serie || '',
      pat: data.pat || '',
      bairro: data.bairro || '',
      cidade: data.cidade || '',
      endereco: data.endereco || '',
      defeito: data.defeito || '',
      sla: null,
      window:
        data.window && data.window !== 'aguardando check-in' && data.window !== '—'
          ? data.window
          : fallbackWindow,
      condNome: data.condNome || '',
      laudo: data.laudo || '',
      ...travelFields,
    };
    trackOsSummaryCache.set(os, spot);
    openMapOsDetailModal(spot);
  } catch (_) {
    openMapOsDetailModal({
      os,
      fromTrack: true,
      outsideRadius,
      client: pin.cliente || '',
      window: fallbackWindow,
      ...travelFields,
    });
  }
}

function openUnlocatedModal(root, opts, spots) {
  closeUnlocatedModal();
  const list = (spots || []).filter(Boolean);
  if (!list.length) return;

  const overlay = document.createElement('div');
  overlay.className = 'tpm-unlocated-modal-overlay';
  overlay.setAttribute('data-tpm', 'map-unlocated-modal');
  overlay.setAttribute('role', 'presentation');

  const itemsHtml = list
    .map((s) => {
      const addr = String(s.endereco || '').trim() || 'Sem endereço cadastrado';
      const equip = String(s.equipamento || '').trim();
      return (
        `<div class="tpm-unlocated-item" data-os="${escHtml(s.os)}">` +
        `<div class="tpm-unlocated-item-head">` +
        `<span class="tpm-unlocated-os">OS ${escHtml(s.os)}</span>` +
        `<span class="tpm-unlocated-client">${escHtml(s.client || '—')}</span>` +
        (equip ? `<span class="tpm-unlocated-equip">${escHtml(equip)}</span>` : '') +
        `</div>` +
        `<div class="tpm-unlocated-addr">${escHtml(addr)}</div>` +
        `</div>`
      );
    })
    .join('');

  overlay.innerHTML =
    `<div class="tpm-unlocated-modal" role="dialog" aria-modal="true" aria-labelledby="tpm-unlocated-title">` +
    `<div class="tpm-unlocated-head">` +
    `<h3 id="tpm-unlocated-title" class="tpm-unlocated-title">` +
    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">` +
    `<path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"></path>` +
    `</svg> Ordens não localizadas (${list.length})</h3>` +
    `<button type="button" class="tpm-unlocated-x" data-tpm="map-unlocated-close" aria-label="Fechar">` +
    `<svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4" fill="none"><path d="M5 5l14 14M19 5L5 19"></path></svg>` +
    `</button></div>` +
    `<p class="tpm-unlocated-lead">Sem coordenadas no mapa (${list.length}). Estes pontos não entraram no pin / rota visual.</p>` +
    `<button type="button" class="tpm-unlocated-retry" data-tpm="map-unlocated-retry">` +
    `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">` +
    `<path d="M21 12a9 9 0 1 1-2.6-6.4"></path><path d="M21 3v6h-6"></path></svg>` +
    `<span class="tpm-unlocated-retry-label">Refazer busca do(s) endereço(s)</span></button>` +
    `<div class="tpm-unlocated-list">${itemsHtml}</div></div>`;

  const onKey = (e) => {
    if (e.key === 'Escape') closeUnlocatedModal();
  };

  const onClick = async (e) => {
    if (e.target === overlay || e.target.closest('[data-tpm="map-unlocated-close"]')) {
      e.preventDefault();
      closeUnlocatedModal();
      return;
    }
    const retry = e.target.closest('[data-tpm="map-unlocated-retry"]');
    if (retry && !retry.disabled) {
      e.preventDefault();
      retry.disabled = true;
      retry.classList.add('is-busy');
      const label = retry.querySelector('.tpm-unlocated-retry-label');
      if (label) label.textContent = 'Buscando…';
      try {
        await retryUnlocatedGeocode(root, opts);
        const mapRoot = root.querySelector('[data-tpm="map"]');
        const state = mapRoot ? getState(mapRoot) : null;
        const left = state ? getUnlocatedSpots(state) : [];
        if (!left.length) {
          closeUnlocatedModal();
        } else {
          closeUnlocatedModal();
          openUnlocatedModal(root, opts, left);
        }
      } catch {
        retry.disabled = false;
        retry.classList.remove('is-busy');
        if (label) label.textContent = 'Refazer busca do(s) endereço(s)';
      }
    }
  };

  overlay.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey);
  const prevRemove = overlay.remove.bind(overlay);
  overlay.remove = () => {
    document.removeEventListener('keydown', onKey);
    prevRemove();
  };
  document.body.appendChild(overlay);
}

function maybeShowUnlocatedForDay(root, opts, day) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  if (state.mode !== 'plan') return;
  if (day != null && day !== state.day) return;
  if (state.loading || state.geocoding || state.geocodeJobs > 0) return;
  const unlocated = getUnlocatedSpots(state, state.day);
  if (unlocated.length) openUnlocatedModal(root, opts, unlocated);
  else closeUnlocatedModal();
}

async function retryUnlocatedGeocode(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  const day = state.day;
  const unlocated = getUnlocatedSpots(state, day);
  if (!unlocated.length) return;

  state.geocodeJobs = (state.geocodeJobs || 0) + 1;
  state.geocoding = true;
  state.geocodingCount = unlocated.length;
  paint(root, opts);

  try {
    const payload = unlocated
      .map((s) => ({
        orderId: s.os,
        address: s.endereco || '',
      }))
      .filter((p) => p.address);
    const geoResults = payload.length ? await geocodeBatch(payload) : [];
    const byOs = new Map();
    (geoResults || []).forEach((g) => {
      const id = g.orderId != null ? String(g.orderId).trim() : '';
      if (!id || g.error || !hasValidMapCoords(g.lat, g.lng)) return;
      byOs.set(id, { lat: parseCoord(g.lat), lng: parseCoord(g.lng) });
    });
    state.spotsByDay[day] = (state.spotsByDay[day] || []).map((s) => {
      const hit = byOs.get(String(s.os || '').trim());
      if (!hit) return s;
      return { ...s, lat: hit.lat, lng: hit.lng };
    });
  } finally {
    resetGeocodeState(state);
    paint(root, opts, { fit: true });
  }
}

function beginGeocodeJob(state, count) {
  state.geocodeJobs = (state.geocodeJobs || 0) + 1;
  state.geocoding = true;
  state.geocodingCount = (state.geocodingCount || 0) + (count || 0);
}

function endGeocodeJob(state, count) {
  state.geocodeJobs = Math.max(0, (state.geocodeJobs || 1) - 1);
  state.geocodingCount = Math.max(0, (state.geocodingCount || 0) - (count || 0));
  if (state.geocodeJobs === 0) {
    state.geocoding = false;
    state.geocodingCount = 0;
  }
}

/** Selo "Retorno N" da OS nativa com retornos (mesma cor do selo de retorno do board). */
function buildReturnBadgeHtml(p) {
  const n = Number(p?.returnCount) || 0;
  if (!n) return '';
  const tip = `Retorno ${n}${p.lastReturnCondition ? ` · ${p.lastReturnCondition}` : ''}`;
  return `<span class="tpm-return-badge" data-tpm-tip="${escHtml(tip)}">Retorno ${n}</span>`;
}

/** Data ERP marcada como 'Z' → dd/mm/aaaa hh:mm. */
function formatErpDateTime(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const p = (v) => String(v).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

function buildMapRestItemHtml(p) {
  const sla = formatPrevisao(p.sla);
  const unmapped = isSpotUnlocated(p);
  const metaParts = [String(p.os || '').trim(), p.bairro ? String(p.bairro).trim() : '']
    .filter(Boolean)
    .join(' · ');
  const metaExtra = unmapped ? (metaParts ? ' · sem mapa' : 'sem mapa') : '';
  return (
    `<div class="tpm-map-rest-item${unmapped ? ' is-unmapped' : ''}" data-tpm="map-rest-card" data-os="${escHtml(p.os)}">` +
    `<button type="button" class="tpm-map-rest-plus" data-tpm="map-rest-add" data-os="${escHtml(p.os)}"` +
    ` data-tpm-tip="Adicionar ao roteiro" aria-label="Adicionar ao roteiro">+</button>` +
    buildMapServiceTypeBadgeHtml(p) +
    `<button type="button" class="tpm-map-rest-main" data-tpm="map-os-detail" data-os="${escHtml(p.os)}"` +
    ` aria-label="Detalhe da OS ${escHtml(p.os)}">` +
    `<span class="tpm-map-rest-body">` +
    `<span class="tpm-map-rest-client">${escHtml(p.client || '—')}</span>` +
    `<span class="tpm-map-rest-meta">${escHtml(metaParts)}${escHtml(metaExtra)}${buildReturnBadgeHtml(p)}</span>` +
    `</span>` +
    `<span class="tpm-map-rest-sla" style="color:${escHtml(sla.color)}">${escHtml(sla.label)}</span>` +
    `</button></div>`
  );
}

const ICON_PLAYLIST_ADD_SVG =
  `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
  `<path d="M19 8h-14"></path><path d="M5 12h9"></path><path d="M11 16h-6"></path>` +
  `<path d="M15 16h6"></path><path d="M18 13v6"></path></svg>`;

const ICON_CLOSE_SVG =
  `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
  `<path d="M5 5l14 14M19 5L5 19"></path></svg>`;

const ICON_GRIP_SVG =
  `<svg class="tpm-map-plan-grip-icon" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">` +
  `<circle cx="9" cy="6" r="1.5"></circle><circle cx="15" cy="6" r="1.5"></circle>` +
  `<circle cx="9" cy="12" r="1.5"></circle><circle cx="15" cy="12" r="1.5"></circle>` +
  `<circle cx="9" cy="18" r="1.5"></circle><circle cx="15" cy="18" r="1.5"></circle></svg>`;

function groupRestByCity(list) {
  const byCity = new Map();
  (list || []).forEach((p) => {
    const city = String(p.cidade || '').trim() || 'Sem cidade';
    if (!byCity.has(city)) byCity.set(city, []);
    byCity.get(city).push(p);
  });
  const cities = [...byCity.keys()].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  return cities.map((city) => {
    const orders = [...byCity.get(city)].sort((a, b) => {
      const ba = String(a.bairro || '').trim();
      const bb = String(b.bairro || '').trim();
      if (ba !== bb) return ba.localeCompare(bb, 'pt-BR');
      return String(a.client || '').localeCompare(String(b.client || ''), 'pt-BR');
    });
    return { city, orders };
  });
}

/** Agrupa OS do roteiro por cidade (ordem da cidade = 1ª aparição na sequência). */
function groupPlannedByCity(seq) {
  const byCity = new Map();
  (seq || []).forEach((p, i) => {
    const city = String(p.cidade || '').trim() || 'Sem cidade';
    if (!byCity.has(city)) byCity.set(city, []);
    byCity.get(city).push({ p, i });
  });
  return [...byCity.entries()]
    .sort((a, b) => a[1][0].i - b[1][0].i)
    .map(([city, items]) => ({ city, items }));
}

function buildMapPlanItemHtml(p, seqIndex) {
  const unmapped = isSpotUnlocated(p);
  const metaParts = [String(p.os || '').trim(), p.bairro ? String(p.bairro).trim() : '']
    .filter(Boolean)
    .join(' · ');
  const metaExtra = unmapped ? (metaParts ? ' · sem mapa' : 'sem mapa') : '';
  return (
    `<div class="tpm-map-plan-item${unmapped ? ' is-unmapped' : ''}" draggable="true" data-tpm="map-plan-item" data-os="${escHtml(p.os)}"` +
    (unmapped
      ? ' data-tpm-tip="OS no roteiro sem coordenadas — não aparece no mapa" data-tpm-tip-wrap'
      : '') +
    `>` +
    `<span class="tpm-map-plan-grip" data-tpm-tip="Arrastar para reordenar" aria-hidden="true">${ICON_GRIP_SVG}</span>` +
    `<span class="tpm-map-plan-n">${seqIndex + 1}</span>` +
    buildMapServiceTypeBadgeHtml(p) +
    `<button type="button" class="tpm-map-plan-item-main" data-tpm="map-os-detail" data-os="${escHtml(p.os)}"` +
    ` aria-label="Detalhe da OS ${escHtml(p.os)}" draggable="false">` +
    `<span class="tpm-map-plan-item-body"><span class="tpm-map-plan-item-client">${escHtml(p.client || '—')}</span>` +
    `<span class="tpm-map-plan-item-meta">${escHtml(metaParts)}${escHtml(metaExtra)}${buildReturnBadgeHtml(p)}</span></span>` +
    `</button>` +
    `<button type="button" class="tpm-map-plan-remove" data-tpm="map-remove" data-os="${escHtml(p.os)}" data-tpm-tip="Remover do roteiro" draggable="false">` +
    `<svg width="10" height="10" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.6" fill="none"><path d="M5 5l14 14M19 5L5 19"></path></svg>` +
    `</button></div>`
  );
}

function buildCityGroupToggleHtml(groupByCity) {
  const tip = groupByCity ? 'Fechar visão por cidade' : 'Mostrar cidade';
  const icon = groupByCity ? ICON_CLOSE_SVG : ICON_PLAYLIST_ADD_SVG;
  return (
    `<button type="button" class="tpm-map-rest-city-btn${groupByCity ? ' is-on' : ''}"` +
    ` data-tpm="map-rest-city-toggle" data-tpm-tip="${escHtml(tip)}"` +
    ` aria-label="${escHtml(tip)}" aria-pressed="${groupByCity ? 'true' : 'false'}">${icon}</button>`
  );
}

function buildMapPlannedListHtml(seq, groupByCity, { showCityToggle = false } = {}) {
  if (!seq.length) return '';
  const head = showCityToggle
    ? `<div class="tpm-map-planned-head">` +
      `<span class="tpm-map-section-label">No roteiro (${seq.length})</span>` +
      buildCityGroupToggleHtml(groupByCity) +
      `</div>`
    : '';
  const body = !groupByCity
    ? seq.map((p, i) => buildMapPlanItemHtml(p, i)).join('')
    : groupPlannedByCity(seq)
        .map(
          (g) =>
            `<div class="tpm-map-plan-city">` +
            `<span class="tpm-map-plan-city-title">${escHtml(g.city)}` +
            `<span class="tpm-map-plan-city-count">${g.items.length}</span></span>` +
            `<div class="tpm-map-plan-city-list">${g.items.map(({ p, i }) => buildMapPlanItemHtml(p, i)).join('')}</div>` +
            `</div>`
        )
        .join('');
  return head + body;
}

function buildMapRestListHtml(rest, groupByCity, { showCityToggle = true } = {}) {
  const n = rest.length;
  const head =
    `<div class="tpm-map-rest-head">` +
    `<span class="tpm-map-section-label">Fora do roteiro (${n})</span>` +
    (showCityToggle ? buildCityGroupToggleHtml(groupByCity) : '') +
    `</div>`;

  if (!groupByCity) {
    return head + rest.map((p) => buildMapRestItemHtml(p)).join('');
  }

  return (
    head +
    groupRestByCity(rest)
      .map(
        (g) =>
          `<div class="tpm-map-rest-city">` +
          `<span class="tpm-map-rest-city-title">${escHtml(g.city)}` +
          `<span class="tpm-map-rest-city-count">${g.orders.length}</span></span>` +
          `<div class="tpm-map-rest-city-list">${g.orders.map((p) => buildMapRestItemHtml(p)).join('')}</div>` +
          `</div>`
      )
      .join('')
  );
}

function clearPlanDragMarks(root) {
  root.querySelectorAll('.tpm-map-plan-item.is-dragging, .tpm-map-plan-item.is-drag-over-before, .tpm-map-plan-item.is-drag-over-after').forEach((el) => {
    el.classList.remove('is-dragging', 'is-drag-over-before', 'is-drag-over-after');
  });
}

function reorderRouteOs(route, fromOs, toOs, placeAfter) {
  if (!fromOs || !toOs || fromOs === toOs) return route;
  const next = route.filter((os) => os !== fromOs);
  let idx = next.indexOf(toOs);
  if (idx < 0) return route;
  if (placeAfter) idx += 1;
  next.splice(idx, 0, fromOs);
  return next;
}

function routeFingerprint(route) {
  return (route || []).map((os) => String(os || '').trim()).filter(Boolean).join('|');
}

function snapshotSavedRoute(state, day) {
  if (!state.savedRoutes) state.savedRoutes = { hoje: null, amanha: null };
  state.savedRoutes[day] = routeFingerprint(state.routes[day] || []);
}

function isPlanRouteDirty(state, day = state.day) {
  if (!state.savedRoutes) return true;
  const saved = state.savedRoutes[day];
  // Antes do 1º load ainda não temos baseline → permite salvar se houver OS.
  if (saved == null) return true;
  return routeFingerprint(state.routes[day] || []) !== saved;
}

function renderPlanSide(root, state) {
  const spots = state.spotsByDay[state.day] || [];
  const route = state.routes[state.day] || [];
  const seq = route.map((os) => spotByOs(state, os)).filter(Boolean);
  const rest = spots.filter((s) => !route.includes(s.os));
  const forecast = state.forecastByDay[state.day];
  const pct = spots.length ? Math.round((route.length / spots.length) * 100) : 0;
  const groupByCity = !!state.restGroupByCity;

  const title = root.querySelector('.tpm-map-plan-title');
  const date = root.querySelector('.tpm-map-plan-date');
  const count = root.querySelector('.tpm-map-plan-count');
  const bar = root.querySelector('.tpm-map-plan-bar-fill');
  const planMeta = root.querySelector('.tpm-map-plan-meta');
  if (title) title.textContent = `Roteiro de ${dayLabel(state.day)}`;
  if (date) date.textContent = dayDateLabel(state.day, forecast);
  if (count) count.textContent = `${route.length} de ${spots.length}`;
  if (bar) bar.style.width = `${pct}%`;
  if (planMeta) {
    const geoBusy = state.geocoding || state.geocodeJobs > 0;
    const metaMsg = state.error
      ? state.error
      : geoBusy
        ? state.geocodingCount > 0
          ? `Localizando ${state.geocodingCount} endereço(s)…`
          : 'Localizando endereços…'
        : state.loading
          ? 'Carregando OS…'
          : '';
    planMeta.innerHTML = `<span>${escHtml(metaMsg)}</span><span></span>`;
  }

  root.querySelectorAll('[data-tpm="map-day"]').forEach((btn) => {
    const day = btn.getAttribute('data-day');
    btn.classList.toggle('is-on', day === state.day);
    const c = btn.querySelector('.tpm-map-day-count');
    if (c) c.textContent = String((state.spotsByDay[day] || []).length);
    const fc = state.forecastByDay[day];
    btn.setAttribute(
      'data-tpm-tip',
      `Roteirizar OS de ${dayLabel(day)} · ${dayDateLabel(day, fc)}`
    );
  });

  const plannedEl = root.querySelector('[data-tpm="map-planned"]');
  const emptyEl = root.querySelector('[data-tpm="map-plan-empty"]');
  const restWrap = root.querySelector('[data-tpm="map-rest-list"]');
  const planBody = root.querySelector('.tpm-map-plan-body');

  const unlocated = getUnlocatedSpots(state);
  const busy = state.loading || state.geocoding || state.geocodeJobs > 0;
  let unBar = root.querySelector('[data-tpm="map-unlocated-bar"]');
  if (planBody && !unBar) {
    unBar = document.createElement('button');
    unBar.type = 'button';
    unBar.className = 'tpm-map-unlocated-bar';
    unBar.setAttribute('data-tpm', 'map-unlocated-bar');
    planBody.insertBefore(unBar, planBody.firstChild);
  }
  if (unBar) {
    if (unlocated.length && !busy) {
      unBar.hidden = false;
      unBar.innerHTML =
        `<span class="tpm-map-unlocated-bar-ico" aria-hidden="true">!</span>` +
        `<span class="tpm-map-unlocated-bar-txt">Não localizadas no mapa (${unlocated.length}) — abrir e refazer busca</span>`;
    } else {
      unBar.hidden = true;
      unBar.innerHTML = '';
    }
  }

  if (plannedEl) {
    plannedEl.classList.toggle('is-grouped-city', groupByCity && seq.length > 0);
    // Toggle fica no "fora do roteiro" quando há pendentes; se tudo já está roteirizado, no próprio roteiro.
    plannedEl.innerHTML = buildMapPlannedListHtml(seq, groupByCity, {
      showCityToggle: seq.length > 0 && rest.length === 0,
    });
  }
  if (emptyEl) {
    if (busy && !seq.length) {
      emptyEl.hidden = false;
      emptyEl.innerHTML =
        state.geocoding || state.geocodeJobs > 0
          ? 'Localizando endereços no mapa…'
          : 'Carregando ordens de serviço…';
    } else if (!spots.length && !busy) {
      emptyEl.innerHTML = 'Nenhuma OS para este dia';
      emptyEl.hidden = false;
    } else if (!seq.length) {
      emptyEl.hidden = false;
      emptyEl.innerHTML = 'Clique nos pontos do mapa<br>na ordem em que vai atender';
    } else {
      emptyEl.hidden = true;
    }
  }

  if (restWrap) {
    restWrap.hidden = rest.length === 0;
    restWrap.classList.toggle('is-grouped-city', groupByCity);
    restWrap.innerHTML = rest.length
      ? buildMapRestListHtml(rest, groupByCity, { showCityToggle: true })
      : '';
  }

  const finish = root.querySelector('[data-tpm="map-finish"]');
  if (finish) {
    const dirty = isPlanRouteDirty(state);
    const ready = route.length > 0 && dirty && !state.saving && !state.loading;
    finish.disabled = !ready;
    finish.classList.toggle('is-disabled', !ready);
    if (state.saving) {
      finish.textContent = 'Salvando…';
    } else if (!route.length) {
      finish.textContent = 'Selecione as OS';
    } else if (!dirty) {
      finish.textContent = 'Sequência salva';
    } else {
      finish.textContent = 'Salvar sequência';
    }
  }

  const shareBtn = root.querySelector('[data-tpm="map-share"]');
  if (shareBtn) {
    const canShare = route.length > 0 && !state.loading && !state.shareBusy;
    shareBtn.disabled = !canShare;
    shareBtn.classList.toggle('is-disabled', !canShare);
  }
  const shareMenu = root.querySelector('[data-tpm="map-share-menu"]');
  if (shareMenu && !state.shareMenuOpen) {
    shareMenu.hidden = true;
    if (shareBtn) shareBtn.setAttribute('aria-expanded', 'false');
  }
}

function escHtml(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatTrackDateLabel(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return 'Selecionar data';
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  try {
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return ymd;
  }
}

/** HH:mm ou HH:mm:ss → minutos do dia (0–1439). */
function parseHhMmToMinutes(value) {
  if (value == null || value === '') return null;
  const m = String(value)
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh > 23 || mm > 59) return null;
  return hh * 60 + mm;
}

/**
 * Filtro de horário do rastreio.
 * @returns {null|{fromMin:number,toMin:number}|{invalid:true}}
 */
function getTrackTimeFilterBounds(state) {
  const from = parseHhMmToMinutes(state?.trackTimeFrom);
  const to = parseHhMmToMinutes(state?.trackTimeTo);
  if (from == null && to == null) return null;
  const fromMin = from != null ? from : 0;
  const toMin = to != null ? to : 24 * 60 - 1;
  if (fromMin > toMin) return { invalid: true };
  return { fromMin, toMin };
}

function itemOverlapsTimeFilter(item, bounds) {
  if (!bounds) return true;
  if (bounds.invalid) return false;
  let s = parseHhMmToMinutes(item?.startedAtBr);
  let e = parseHhMmToMinutes(item?.finishedAtBr);
  if (s == null && e == null) return false;
  if (s == null) s = e;
  if (e == null) e = s;
  if (e < s) e = s;
  return s <= bounds.toMin && e >= bounds.fromMin;
}

function timeBrInFilter(timeBr, bounds) {
  if (!bounds) return true;
  if (bounds.invalid) return false;
  const m = parseHhMmToMinutes(timeBr);
  if (m == null) return false;
  return m >= bounds.fromMin && m <= bounds.toMin;
}

function formatMinutesAsHhMm(mins) {
  if (!Number.isFinite(mins)) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTrackTimeInputValue(value) {
  const mins = parseHhMmToMinutes(value);
  return mins == null ? '' : formatMinutesAsHhMm(mins);
}

function trackTimeFilterRangeLabel(fromVal, toVal) {
  const from = formatTrackTimeInputValue(fromVal);
  const to = formatTrackTimeInputValue(toVal);
  if (from && to) return `${from} → ${to}`;
  if (from) return `A partir de ${from}`;
  if (to) return `Até ${to}`;
  return 'Todo o dia';
}

function readTrackTimePopDraft(root) {
  const fromEl = root.querySelector('[data-tpm="map-track-time-from"]');
  const toEl = root.querySelector('[data-tpm="map-track-time-to"]');
  return {
    from: fromEl ? fromEl.value || '' : '',
    to: toEl ? toEl.value || '' : '',
  };
}

function trackTimeDraftInvalid(draft) {
  const from = parseHhMmToMinutes(draft?.from);
  const to = parseHhMmToMinutes(draft?.to);
  return from != null && to != null && from > to;
}

function refreshTrackTimePopValidity(root) {
  const errEl = root.querySelector('[data-tpm="map-track-time-pop-err"]');
  const applyEl = root.querySelector('[data-tpm="map-track-time-apply"]');
  const invalid = trackTimeDraftInvalid(readTrackTimePopDraft(root));
  if (errEl) errEl.hidden = !invalid;
  if (applyEl) applyEl.disabled = invalid;
  return !invalid;
}

function positionTrackTimePop(root) {
  const btn = root.querySelector('[data-tpm="map-track-time-btn"]');
  const pop = root.querySelector('[data-tpm="map-track-time-pop"]');
  if (!btn || !pop || pop.hidden) return;
  const r = btn.getBoundingClientRect();
  const width = Math.max(260, Math.round(r.width));
  let left = Math.round(r.left);
  if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
  const topBelow = Math.round(r.bottom + 6);
  pop.style.width = `${width}px`;
  pop.style.left = `${left}px`;
  const popH = pop.offsetHeight || 0;
  const top = topBelow + popH > window.innerHeight - 8 ? Math.max(8, Math.round(r.top - popH - 6)) : topBelow;
  pop.style.top = `${top}px`;
}

function setTrackTimePopOpen(root, state, open) {
  state.trackTimePopOpen = !!open;
  const btn = root.querySelector('[data-tpm="map-track-time-btn"]');
  const pop = root.querySelector('[data-tpm="map-track-time-pop"]');
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (!pop) return;
  pop.hidden = !open;
  if (!open) return;
  const fromEl = pop.querySelector('[data-tpm="map-track-time-from"]');
  const toEl = pop.querySelector('[data-tpm="map-track-time-to"]');
  if (fromEl) fromEl.value = state.trackTimeFrom || '';
  if (toEl) toEl.value = state.trackTimeTo || '';
  refreshTrackTimePopValidity(root);
  positionTrackTimePop(root);
}

/** View model filtrado do rastreio (timeline + pontos GPS). */
function getTrackFilteredView(state) {
  const allPoints = Array.isArray(state.trackPoints) ? state.trackPoints : [];
  const trips = Array.isArray(state.trackTrips) ? state.trackTrips : [];
  const services = Array.isArray(state.trackServices) ? state.trackServices : [];
  const fullTimeline =
    Array.isArray(state.trackTimeline) && state.trackTimeline.length
      ? state.trackTimeline
      : [...trips, ...services].sort((a, b) =>
          String(a.startedAt || '').localeCompare(String(b.startedAt || ''))
        );
  const timeBounds = getTrackTimeFilterBounds(state);
  if (timeBounds?.invalid) {
    return { timeBounds, timeline: [], points: [], services: [] };
  }
  const timeline = timeBounds
    ? fullTimeline.filter((item) => itemOverlapsTimeFilter(item, timeBounds))
    : fullTimeline;
  const points = timeBounds
    ? allPoints.filter((p) => timeBrInFilter(p.timeBr, timeBounds))
    : allPoints;
  const filteredServices = timeBounds
    ? services.filter((s) => itemOverlapsTimeFilter(s, timeBounds))
    : services;
  return { timeBounds, timeline, points, services: filteredServices, trips };
}

function renderTrackSide(root, state) {
  const dateBtn = root.querySelector('[data-tpm="map-track-date"]');
  const metaEl = root.querySelector('[data-tpm="map-track-meta"]');
  const tripsWrap = root.querySelector('[data-tpm="map-track-trips"]');
  const diarioBtn = root.querySelector('[data-tpm="map-track-diario"]');
  const timeBtn = root.querySelector('[data-tpm="map-track-time-btn"]');
  const timeRangeEl = root.querySelector('[data-tpm="map-track-time-btn-range"]');
  const timeBadgeEl = root.querySelector('[data-tpm="map-track-time-btn-badge"]');
  const dateYmd = state.trackDate || todayYmd();
  if (dateBtn) {
    const fromValBtn = state.trackTimeFrom || '';
    const toValBtn = state.trackTimeTo || '';
    const timePart =
      fromValBtn || toValBtn ? ` · ${trackTimeFilterRangeLabel(fromValBtn, toValBtn)}` : '';
    dateBtn.textContent = `${formatTrackDateLabel(dateYmd)}${timePart}`;
    dateBtn.setAttribute('data-date', dateYmd);
    dateBtn.setAttribute(
      'data-tpm-tip',
      fromValBtn || toValBtn
        ? `Data e horário · ${trackTimeFilterRangeLabel(fromValBtn, toValBtn)}`
        : 'Data e horário do rastreio'
    );
  }
  // Diário técnico: disponível assim que há dia selecionado no Rastreio.
  if (diarioBtn) {
    diarioBtn.hidden = !dateYmd;
  }
  // Filtro de horário migrado para o popover da data.
  const timeField = root.querySelector('.tpm-map-track-time-field');
  if (timeField) timeField.hidden = true;
  const fromVal = state.trackTimeFrom || '';
  const toVal = state.trackTimeTo || '';
  const timeFiltered = !!(fromVal || toVal);
  if (timeBtn) {
    timeBtn.classList.toggle('is-filtered', timeFiltered);
    timeBtn.setAttribute('aria-expanded', state.trackTimePopOpen ? 'true' : 'false');
    timeBtn.setAttribute(
      'data-tpm-tip',
      timeFiltered ? `Filtro de horário ativo · ${trackTimeFilterRangeLabel(fromVal, toVal)}` : 'Filtrar horários'
    );
  }
  if (timeRangeEl) timeRangeEl.textContent = trackTimeFilterRangeLabel(fromVal, toVal);
  if (timeBadgeEl) timeBadgeEl.hidden = !timeFiltered;
  const timePop = root.querySelector('[data-tpm="map-track-time-pop"]');
  if (timePop) {
    timePop.hidden = !state.trackTimePopOpen;
    if (state.trackTimePopOpen) positionTrackTimePop(root);
  }

  const allPoints = Array.isArray(state.trackPoints) ? state.trackPoints : [];
  const allServices = Array.isArray(state.trackServices) ? state.trackServices : [];
  const { timeBounds, timeline, points: filteredPoints } = getTrackFilteredView(state);
  const n = filteredPoints.length;

  if (
    state.selectedTrackSegmentKey &&
    timeline.length &&
    !timeline.some((t) => {
      const key = state.selectedTrackSegmentKey;
      if ((t.segmentKey || '') === key) return true;
      return Array.isArray(t.mapSegmentKeys) && t.mapSegmentKeys.includes(key);
    })
  ) {
    state.selectedTrackSegmentKey = null;
  }
  const selectedSeg = state.selectedTrackSegmentKey || '';

  if (metaEl) {
    if (state.trackLoading) metaEl.textContent = '…';
    else if (state.trackError) metaEl.textContent = 'erro';
    else {
      const parts = [];
      const svcItems = timeline.filter((t) => t.kind === 'service');
      const nAt = svcItems.filter((t) => normalizeTrackOpType(t.operationType, 'atendimento') === 'atendimento').length;
      const nEn = svcItems.filter((t) => t.operationType === 'entrega').length;
      const nRe = svcItems.filter((t) => t.operationType === 'retirada').length;
      if (nAt) parts.push(`${nAt} atend.`);
      if (nEn) parts.push(`${nEn} entrega${nEn === 1 ? '' : 's'}`);
      if (nRe) parts.push(`${nRe} retirada${nRe === 1 ? '' : 's'}`);
      if (selectedSeg) parts.push('trecho');
      if (fromVal || toVal) parts.push('filtro');
      metaEl.textContent = parts.length ? parts.join(' · ') : '—';
    }
  }
  if (!tripsWrap) return;

  let emptyMsg = '';
  if (state.trackLoading) emptyMsg = 'Carregando pontos do dia…';
  else if (state.trackError) emptyMsg = state.trackError;
  else if (timeBounds?.invalid) emptyMsg = 'Horário inválido: “de” deve ser menor ou igual a “até”.';
  else if (!allPoints.length && !allServices.length && !timeline.length) {
    emptyMsg = 'Nenhum ponto de rastreio neste dia.';
  } else if (!timeline.length) {
    const range =
      timeBounds && !timeBounds.invalid
        ? `${formatMinutesAsHhMm(timeBounds.fromMin)} → ${formatMinutesAsHhMm(timeBounds.toMin)}`
        : '';
    emptyMsg = range ? `Nenhum registro entre ${range}.` : 'Nenhum registro neste dia.';
  }

  if (emptyMsg) {
    tripsWrap.hidden = false;
    tripsWrap.innerHTML = `<div class="tpm-map-track-trips-empty">${escHtml(emptyMsg)}</div>`;
    return;
  }

  const rangeLabel = n
    ? `${filteredPoints[0]?.timeBr || '—'} → ${filteredPoints[n - 1]?.timeBr || '—'}`
    : '';
  tripsWrap.hidden = false;
  tripsWrap.innerHTML =
    '<div class="tpm-map-track-trips-head">' +
    '<span class="tpm-map-section-label">Linha do tempo</span>' +
    (rangeLabel ? `<span class="tpm-map-track-trips-range">${escHtml(rangeLabel)}</span>` : '') +
    '</div>' +
    [...timeline]
      .reverse()
      .map((item) => {
        const segKey = item.segmentKey || '';
        const selected = !!selectedSeg && segKey && selectedSeg === segKey;
        const selCls = selected ? ' is-selected' : '';
        if (item.kind === 'workday-start' || item.kind === 'workday-end') {
          const t = item.startedAtBr || '—';
          const title = item.title || (item.kind === 'workday-end' ? 'Fim do dia' : 'Início do dia');
          const km = item.kmLabel || '';
          const color = item.color || (item.kind === 'workday-end' ? '#64748b' : '#0d9488');
          const photoable = !!item.photoSelectable && !!item.photoViewUrl;
          const photoAttrs = photoable
            ? ` data-photoable="1" data-photo-url="${escHtml(item.photoViewUrl)}" data-photo-title="${escHtml(
                title
              )}" data-photo-sub="${escHtml(km)}"`
            : '';
          return (
            `<button type="button" class="tpm-map-track-trip is-workday${photoable ? ' is-photoable' : ''}${selCls}"` +
            ` data-tpm="map-track-trip" data-kind="${escHtml(item.kind)}" data-segment-key="${escHtml(segKey)}"` +
            `${photoAttrs}` +
            ` data-tpm-tip="${photoable ? 'Ver foto da jornada' : escHtml(title)}">` +
            `<span class="tpm-map-track-trip-swatch" style="background:${escHtml(color)}"></span>` +
            `<span class="tpm-map-track-trip-body">` +
            `<span class="tpm-map-track-trip-row">` +
            `<span class="tpm-map-track-trip-times">${escHtml(t)}</span>` +
            (km ? `<span class="tpm-map-track-trip-km">${escHtml(km)}</span>` : '') +
            `</span>` +
            trackTripOsHtml(item.kind === 'workday-end' ? ['sunset'] : ['sunrise'], escHtml(title)) +
            `</span></button>`
          );
        }
        if (item.kind === 'lunch-start' || item.kind === 'lunch-end') {
          const t = item.startedAtBr || '—';
          const title =
            item.title || (item.kind === 'lunch-end' ? 'Fim do almoço' : 'Início do almoço');
          const color = item.color || '#f59e0b';
          const mapable = !!item.mapSelectable && item.lat != null && item.lng != null;
          const tip = mapable ? 'Ver local do almoço no mapa' : title;
          return (
            `<button type="button" class="tpm-map-track-trip is-lunch${mapable ? ' is-mapable is-focusable' : ''}${selCls}"` +
            ` data-tpm="map-track-trip" data-kind="${escHtml(item.kind)}" data-segment-key="${escHtml(segKey)}"` +
            (mapable ? ` data-mapable="1"` : '') +
            ` aria-pressed="${selected ? 'true' : 'false'}" data-tpm-tip="${escHtml(tip)}">` +
            `<span class="tpm-map-track-trip-swatch" style="background:${escHtml(color)}"></span>` +
            `<span class="tpm-map-track-trip-body">` +
            `<span class="tpm-map-track-trip-row">` +
            `<span class="tpm-map-track-trip-times">${escHtml(t)}</span>` +
            `</span>` +
            `<span class="tpm-map-track-trip-os">${escHtml(title)}</span>` +
            `</span></button>`
          );
        }
        if (item.kind === 'service') {
          const os = item.operationCode || (item.operationCodes || [])[0] || '';
          const opType = normalizeTrackOpType(item.operationType, 'atendimento');
          const cancelled = !!item.cancelled;
          const cancelReason =
            item.cancelReason != null && String(item.cancelReason).trim()
              ? String(item.cancelReason).trim()
              : '';
          const typeLabel = cancelled
            ? opType === 'atendimento'
              ? 'Atendimento cancelado'
              : `${trackServiceTypeLabel(opType)} cancelada`
            : trackServiceTypeLabel(opType);
          const start = item.startedAtBr || '—';
          const end = item.finishedAtBr || '—';
          const color = cancelled ? '#ef4444' : item.color || TRACK_TRIP_FALLBACK_COLOR;
          const segAttr = escHtml(segKey);
          const cliente =
            selected && item.cliente != null && String(item.cliente).trim()
              ? String(item.cliente).trim()
              : '';
          const outsideRadius = !!item.outsideRadius;
          const osLabel = os
            ? `${outsideRadius ? '(fora do raio) ' : ''}#${os}`
            : outsideRadius
              ? '(fora do raio)'
              : '';
          const tip = cancelled
            ? cancelReason
              ? `Atendimento cancelado: ${cancelReason}`
              : 'Atendimento cancelado'
            : `Focar ${opType === 'atendimento' ? 'este atendimento' : opType === 'entrega' ? 'esta entrega' : 'esta retirada'} no mapa`;
          return (
            `<button type="button" class="tpm-map-track-trip is-service is-focusable${cancelled ? ' is-cancelled' : ''}${selCls}" data-tpm="map-track-trip" data-kind="service"` +
            ` data-segment-key="${segAttr}" data-os="${escHtml(os)}" aria-pressed="${selected ? 'true' : 'false'}"` +
            ` data-tpm-tip="${escHtml(tip)}">` +
            `<span class="tpm-map-track-trip-swatch" style="background:${escHtml(color)}"></span>` +
            `<span class="tpm-map-track-trip-body">` +
            `<span class="tpm-map-track-trip-row">` +
            `<span class="tpm-map-track-trip-times">${escHtml(start)} → ${escHtml(end)}</span>` +
            `</span>` +
            trackTripOsHtml([opType], escHtml(`${typeLabel}${osLabel ? ` ${osLabel}` : ''}`)) +
            (cliente
              ? `<span class="tpm-map-track-trip-client">${escHtml(cliente)}</span>`
              : '') +
            (cancelReason
              ? `<span class="tpm-map-track-trip-cancel">${escHtml(cancelReason)}</span>`
              : '') +
            `</span></button>`
          );
        }
        const codes = Array.isArray(item.operationCodes) ? item.operationCodes : [];
        const isIdle = item.kind === 'idle' || !item.tripId;
        const hasOs = !!(item.hasOs || codes.length);
        const types = uniqueTrackOpTypes(item.operationTypes, hasOs ? 'atendimento' : null);
        const redirected =
          !!item.redirected || String(item.outcome || '').toLowerCase() === 'redirected';
        const travelLabel = travelTrackLabel(hasOs, codes, types, redirected);
        const travelIcons = hasOs ? ['travel', ...types] : ['travel'];
        const start = item.startedAtBr || '—';
        const end = item.finishedAtBr || '—';
        const color =
          item.color || (hasOs ? TRACK_TRIP_FALLBACK_COLOR : TRACK_IDLE_COLOR);
        const tripAttr = item.tripId ? escHtml(item.tripId) : '';
        const segAttr = escHtml(segKey);
        const mapKeys = Array.isArray(item.mapSegmentKeys) ? item.mapSegmentKeys : [];
        const selectedByMap =
          !!selectedSeg && mapKeys.length > 0 && mapKeys.includes(selectedSeg);
        const travelSelected = selected || selectedByMap;
        const travelCliente =
          travelSelected && item.cliente != null && String(item.cliente).trim()
            ? String(item.cliente).trim()
            : '';
        const metricsHtml =
          travelSelected && hasOs
            ? travelMetricsHtml(item.distanceKm, item.avgSpeedKmh)
            : '';
        return (
          `<button type="button" class="tpm-map-track-trip is-focusable${hasOs ? '' : ' is-idle'}${isIdle ? ' is-no-trip' : ''}${travelSelected ? ' is-selected' : ''}" data-tpm="map-track-trip" data-kind="travel"` +
          ` data-trip-id="${tripAttr}" data-segment-key="${segAttr}" data-has-os="${hasOs ? '1' : '0'}"` +
          ` aria-pressed="${travelSelected ? 'true' : 'false'}" data-tpm-tip="Focar este trecho no mapa">` +
          `<span class="tpm-map-track-trip-swatch" style="background:${escHtml(color)}"></span>` +
          `<span class="tpm-map-track-trip-body">` +
          `<span class="tpm-map-track-trip-row">` +
          `<span class="tpm-map-track-trip-times">${escHtml(start)} → ${escHtml(end)}</span>` +
          `</span>` +
          trackTripOsHtml(travelIcons, escHtml(travelLabel)) +
          (travelCliente
            ? `<span class="tpm-map-track-trip-client">${escHtml(travelCliente)}</span>`
            : '') +
          metricsHtml +
          `</span></button>`
        );
      })
      .join('');
}

function buildTrackEndpointEl(label, tone) {
  const wrap = document.createElement('div');
  wrap.className = `tpm-map-track-endpoint tpm-map-track-endpoint--${tone || 'mid'}`;
  wrap.innerHTML =
    `<span class="tpm-map-track-endpoint-bubble"></span>` +
    `<span class="tpm-map-track-endpoint-dot"></span>`;
  const bubble = wrap.querySelector('.tpm-map-track-endpoint-bubble');
  if (bubble) bubble.textContent = label || '';
  return wrap;
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

function clusterNearbyServicePins(pins, meters = SERVICE_PIN_STACK_METERS) {
  const items = (pins || []).filter((p) => hasValidMapCoords(p.lat, p.lng));
  const used = new Array(items.length).fill(false);
  const clusters = [];
  for (let i = 0; i < items.length; i += 1) {
    if (used[i]) continue;
    const group = [items[i]];
    used[i] = true;
    for (let gi = 0; gi < group.length; gi += 1) {
      const cur = group[gi];
      for (let j = 0; j < items.length; j += 1) {
        if (used[j]) continue;
        if (haversineMeters(cur.lat, cur.lng, items[j].lat, items[j].lng) <= meters) {
          used[j] = true;
          group.push(items[j]);
        }
      }
    }
    const lat = group.reduce((s, p) => s + p.lat, 0) / group.length;
    const lng = group.reduce((s, p) => s + p.lng, 0) / group.length;
    clusters.push({
      key: `svc-stack:${lat.toFixed(5)},${lng.toFixed(5)}`,
      lat,
      lng,
      pins: group,
    });
  }
  return clusters;
}

function servicePinSpiderOffsets(n) {
  if (n <= 1) return [{ x: 0, y: 0 }];
  const radius = n <= 2 ? 84 : Math.min(132, 58 + n * 14);
  const start = n === 2 ? Math.PI : -Math.PI / 2;
  const step = (2 * Math.PI) / n;
  return Array.from({ length: n }, (_, i) => {
    const a = start + i * step;
    return { x: Math.round(Math.cos(a) * radius), y: Math.round(Math.sin(a) * radius) };
  });
}

function buildTrackServiceClusterPinEl(pins) {
  const wrap = document.createElement('button');
  wrap.type = 'button';
  wrap.className = 'tpm-map-track-service-pin tpm-map-track-svc-stack is-interactive';
  wrap.setAttribute('data-tpm-tip', `${pins.length} OS neste ponto · clique para separar`);
  const colors = [];
  (pins || []).forEach((p) => {
    if (p.color && !colors.includes(p.color)) colors.push(p.color);
  });
  const dots = colors
    .slice(0, 3)
    .map((c) => `<span class="tpm-map-track-svc-stack-dot" style="background:${c}"></span>`)
    .join('');
  wrap.innerHTML =
    `<span class="tpm-map-track-service-pin-bubble">` +
    `<span class="tpm-map-track-service-pin-phase">${pins.length} OS</span>` +
    `<span class="tpm-map-track-service-pin-os">mesmo ponto</span>` +
    `<span class="tpm-map-track-service-pin-sub">clique para separar</span>` +
    `</span>` +
    `<span class="tpm-map-track-svc-stack-dots">${dots || '<span class="tpm-map-track-svc-stack-dot"></span>'}</span>`;
  return wrap;
}

function buildTrackServiceStackHubEl(count) {
  const wrap = document.createElement('button');
  wrap.type = 'button';
  wrap.className = 'tpm-map-track-svc-hub is-interactive';
  wrap.setAttribute('data-tpm-tip', 'Fechar');
  wrap.innerHTML = `<span class="tpm-map-track-svc-hub-count">${count}</span>`;
  return wrap;
}

/**
 * Pins de atendimento: se início≈fim (≤ 50 m), um único marcador Início/Fim + OS.
 * Caso contrário, pin de início e pin de fim (rótulos no horário).
 * Não usa Number(null) — vira 0 e gera pin falso em 0,0 (ex.: OS aberta sem CLOSING).
 */
function buildServicePinsFromServices(services) {
  const pins = [];
  (services || []).forEach((svc) => {
    const color = svc.color || TRACK_TRIP_FALLBACK_COLOR;
    const os = svc.operationCode || (svc.operationCodes || [])[0] || '';
    const operationType = normalizeTrackOpType(svc.operationType, 'atendimento');
    const segmentKey = svc.segmentKey || null;
    const tripId = svc.travelEventId || svc.tripId || null;
    const cliente =
      svc.cliente != null && String(svc.cliente).trim() ? String(svc.cliente).trim() : '';
    const sLat = parseCoord(svc.startLat);
    const sLng = parseCoord(svc.startLng);
    const eLat = parseCoord(svc.endLat);
    const eLng = parseCoord(svc.endLng);
    const hasStart = hasValidMapCoords(sLat, sLng);
    const hasEnd = hasValidMapCoords(eLat, eLng);
    const startBr = svc.startedAtBr || '';
    const endBr = svc.finishedAtBr || '';
    const outsideRadius = !!svc.outsideRadius;

    if (hasStart && hasEnd) {
      const dist = haversineMeters(sLat, sLng, eLat, eLng);
      if (dist <= SERVICE_PIN_MERGE_METERS) {
        pins.push({
          lat: sLat,
          lng: sLng,
          phase: 'both',
          os,
          operationType,
          color,
          startBr,
          endBr,
          segmentKey,
          tripId,
          cliente,
          outsideRadius,
        });
        return;
      }
    }
    if (hasStart && endBr && !hasEnd) {
      pins.push({
        lat: sLat,
        lng: sLng,
        phase: 'both',
        os,
        operationType,
        color,
        startBr,
        endBr,
        segmentKey,
        tripId,
        cliente,
        outsideRadius,
      });
      return;
    }
    if (hasStart) {
      pins.push({
        lat: sLat,
        lng: sLng,
        phase: 'start',
        os,
        operationType,
        color,
        timeBr: startBr,
        segmentKey,
        tripId,
        cliente,
        outsideRadius,
      });
    }
    if (hasEnd) {
      pins.push({
        lat: eLat,
        lng: eLng,
        phase: 'end',
        os,
        operationType,
        color,
        timeBr: endBr,
        segmentKey,
        tripId,
        cliente,
        outsideRadius,
      });
    }
  });
  return pins;
}

function buildTrackServicePinEl(osCode, phase, color, meta = {}) {
  const wrap = document.createElement(meta.interactive ? 'button' : 'div');
  if (meta.interactive) wrap.type = 'button';
  wrap.className = `tpm-map-track-service-pin tpm-map-track-service-pin--${phase || 'start'}${
    meta.interactive ? ' is-interactive' : ''
  }`;
  if (meta.interactive) wrap.setAttribute('data-tpm-tip', 'Ver detalhe da OS');
  if (color) wrap.style.setProperty('--tpm-svc-color', color);
  const os = osCode || 'OS';
  const opType = normalizeTrackOpType(meta.operationType, 'atendimento');
  const typeLabel = trackServiceTypeLabel(opType);
  const cliente =
    meta.cliente != null && String(meta.cliente).trim() ? String(meta.cliente).trim() : '';
  const osDisplay = meta.outsideRadius && osCode ? `(fora do raio) ${os}` : os;
  let sub = '';
  if (phase === 'both') {
    const t0 = meta.startBr || '';
    const t1 = meta.endBr || '';
    if (t0 && t1) sub = `Início ${t0} → Fim ${t1}`;
    else if (t0) sub = `Início ${t0}`;
    else if (t1) sub = `Fim ${t1}`;
  } else if (meta.timeBr) {
    sub = `${phase === 'end' ? 'Fim' : 'Início'} ${meta.timeBr}`;
  }
  wrap.innerHTML =
    `<span class="tpm-map-track-service-pin-bubble">` +
    `<span class="tpm-map-track-service-pin-type">` +
    trackTripIconsHtml([opType]) +
    `<span>${typeLabel}</span>` +
    `</span>` +
    `<span class="tpm-map-track-service-pin-os">${escHtml(osDisplay)}</span>` +
    (cliente
      ? `<span class="tpm-map-track-service-pin-client">${escHtml(cliente)}</span>`
      : '') +
    (sub ? `<span class="tpm-map-track-service-pin-sub">${sub}</span>` : '') +
    `</span>` +
    `<span class="tpm-map-track-service-pin-dot"></span>`;
  return wrap;
}

/** Mesmas réguas do backend (MAP_SEG_GAP_S / MAP_SEG_JUMP_M): acima disso não há trajeto conhecido. */
const TRACK_BRIDGE_MAX_GAP_S = 300;
const TRACK_BRIDGE_MAX_JUMP_M = 1500;
/** Parado (só batimentos espaçados): emenda normal mesmo com intervalo longo. */
const TRACK_BRIDGE_STATIONARY_M = 150;
const TRACK_GAP_COLOR = '#94a3b8';

function isContiguousTrackPair(a, b) {
  if (!a || !b) return false;
  const distM = haversineMeters(a.lat, a.lng, b.lat, b.lng);
  if (distM <= TRACK_BRIDGE_STATIONARY_M) return true;
  const ta = trackPointTimeMs(a);
  const tb = trackPointTimeMs(b);
  if (Number.isFinite(ta) && Number.isFinite(tb) && (tb - ta) / 1000 > TRACK_BRIDGE_MAX_GAP_S) {
    return false;
  }
  return distM <= TRACK_BRIDGE_MAX_JUMP_M;
}

/**
 * Liga o fim de cada trecho ao início do próximo: contínuo → emenda na cor do trecho;
 * lacuna de sinal (tempo/distância) → segmento cinza tracejado (sem inventar rota).
 */
function bridgeTrackSegments(segments) {
  const out = [];
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    const next = segments[i + 1];
    out.push(seg);
    const lastPt = seg.lastPoint;
    const nextPt = next?.firstPoint;
    if (!lastPt || !nextPt) continue;
    if (isContiguousTrackPair(lastPt, nextPt)) {
      seg.path.push({ lat: nextPt.lat, lng: nextPt.lng });
    } else {
      out.push({
        key: `${seg.key}-gap`,
        color: TRACK_GAP_COLOR,
        path: [
          { lat: lastPt.lat, lng: lastPt.lng },
          { lat: nextPt.lat, lng: nextPt.lng },
        ],
        hasOs: true,
        gap: true,
      });
    }
  }
  return out;
}

function buildTrackSegments(points, trips, options = {}) {
  const bridgeSegments = options.bridgeSegments !== false;
  const list = Array.isArray(trips) ? trips : [];
  const segments = [];

  if (list.length && points.length) {
    const byKey = new Map();
    for (const p of points) {
      const k = p.segmentKey || '';
      if (!k) continue;
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k).push(p);
    }
    list.forEach((trip, i) => {
      const segPoints = byKey.get(trip.segmentKey || '') || [];
      if (!segPoints.length) return;
      const hasOs =
        trip.hasOs === true ||
        (Array.isArray(trip.operationCodes) && trip.operationCodes.length > 0);
      const color =
        trip.color || (hasOs ? TRACK_TRIP_FALLBACK_COLOR : TRACK_IDLE_COLOR);
      segments.push({
        key: trip.segmentKey || `seg-${i}`,
        color,
        path: segPoints.map((p) => ({ lat: p.lat, lng: p.lng })),
        hasOs: !!hasOs,
        firstPoint: segPoints[0],
        lastPoint: segPoints[segPoints.length - 1],
      });
    });
  } else {
    for (let i = 0; i < points.length; i += 1) {
      const p = points[i];
      const key = p.segmentKey || p.tripId || `idle-${i}`;
      const hasOs = !!(p.hasOs || (p.tripId && p.color && p.color !== TRACK_IDLE_COLOR));
      const color = p.color || (p.tripId && hasOs ? TRACK_TRIP_FALLBACK_COLOR : TRACK_IDLE_COLOR);
      const last = segments[segments.length - 1];
      if (!last || last.key !== key) {
        segments.push({ key, color, path: [{ lat: p.lat, lng: p.lng }], hasOs, firstPoint: p, lastPoint: p });
      } else {
        last.path.push({ lat: p.lat, lng: p.lng });
        last.lastPoint = p;
      }
    }
  }

  return bridgeSegments ? bridgeTrackSegments(segments) : segments;
}

function trackPointTimeMs(p) {
  if (p?.recordedAt) {
    const t = new Date(p.recordedAt).getTime();
    if (Number.isFinite(t)) return t;
  }
  const tb = String(p?.timeBr || '').trim();
  if (!/^\d{2}:\d{2}:\d{2}$/.test(tb)) return Number.NaN;
  const [hh, mm, ss] = tb.split(':').map(Number);
  return ((hh * 60 + mm) * 60 + ss) * 1000;
}

function sortTrackPointsByTime(points) {
  return [...(points || [])]
    .map((p) => ({ p, t: trackPointTimeMs(p) }))
    .filter((x) => Number.isFinite(x.t))
    .sort((a, b) => a.t - b.t)
    .map((x) => x.p);
}

/** Início/Fim exatamente nos extremos da polilinha desenhada (sem pular à frente da linha). */
function resolveFocusTrackEndpoints(segments, points, timelineItem) {
  const drawn = (segments || []).filter((s) => Array.isArray(s.path) && s.path.length >= 2);
  const withPath = drawn.length
    ? drawn
    : (segments || []).filter((s) => Array.isArray(s.path) && s.path.length);
  let start = null;
  let end = null;
  if (withPath.length) {
    const first = withPath[0].path[0];
    const lastSeg = withPath[withPath.length - 1].path;
    const last = lastSeg[lastSeg.length - 1];
    start = { lat: first.lat, lng: first.lng, timeBr: first.timeBr || '' };
    end = { lat: last.lat, lng: last.lng, timeBr: last.timeBr || '' };
  } else {
    const sorted = sortTrackPointsByTime(points);
    if (!sorted.length) {
      return { start: null, end: null, startLabel: 'Início', endLabel: 'Fim' };
    }
    start = sorted[0];
    end = sorted[sorted.length - 1];
  }

  const startLabel = timelineItem?.startedAtBr
    ? `Início ${timelineItem.startedAtBr}`
    : start?.timeBr
      ? `Início ${start.timeBr}`
      : 'Início';
  const endLabel = timelineItem?.finishedAtBr
    ? `Fim ${timelineItem.finishedAtBr}`
    : end?.timeBr
      ? `Fim ${end.timeBr}`
      : 'Fim';
  return { start, end, startLabel, endLabel };
}
/** Polilinhas ao focar trecho da timeline (sem retas entre buracos GPS). */
function buildFocusTrackSegments(points, trips) {
  const sorted = [...(trips || [])].sort((a, b) =>
    String(a.startedAt || '').localeCompare(String(b.startedAt || ''))
  );
  const segments = [];
  for (const trip of sorted) {
    const key = trip.segmentKey || '';
    const segPoints = sortTrackPointsByTime(
      (points || []).filter((p) => (p.segmentKey || '') === key)
    );
    if (!segPoints.length) continue;
    const hasOs =
      trip.hasOs === true ||
      (Array.isArray(trip.operationCodes) && trip.operationCodes.length > 0);
    const color = trip.color || (hasOs ? TRACK_TRIP_FALLBACK_COLOR : TRACK_IDLE_COLOR);
    segments.push({
      key,
      color,
      path: segPoints.map((p) => ({
        lat: p.lat,
        lng: p.lng,
        timeBr: p.timeBr || '',
        recordedAt: p.recordedAt || null,
      })),
      hasOs: !!hasOs,
    });
  }
  return segments;
}

function buildTrackLunchPinEl(title, color, timeBr) {
  const wrap = document.createElement('div');
  wrap.className = 'tpm-map-event-focus-pin tpm-map-track-lunch-pin';
  const c = color || '#f59e0b';
  const label = title || 'Almoço';
  const sub = timeBr || '';
  wrap.innerHTML =
    `<span class="tpm-map-event-focus-pin-bubble"><span class="tpm-map-event-focus-pin-title"></span>` +
    `<span class="tpm-map-event-focus-pin-sub"></span></span>` +
    `<span class="tpm-map-event-focus-pin-stem"></span>` +
    `<span class="tpm-map-event-focus-pin-dot" style="background:${c};box-shadow:0 0 0 3px ${c}33"></span>`;
  const t = wrap.querySelector('.tpm-map-event-focus-pin-title');
  const s = wrap.querySelector('.tpm-map-event-focus-pin-sub');
  if (t) t.textContent = label;
  if (s) {
    s.textContent = sub;
    s.hidden = !sub;
  }
  return wrap;
}

function syncTrackGoogleMap(root, state, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  const g = mapRoot?.__tpmGmap;
  if (!g?.map || !window.google?.maps) return;

  clearGoogleOverlays(mapRoot);
  syncEventFocusChrome(root, { ...state, selectedEventKey: null });

  const selectedSeg = state.selectedTrackSegmentKey || '';
  const filtered = getTrackFilteredView(state);
  const timeBounds = filtered.timeBounds;
  const timeFp =
    timeBounds && !timeBounds.invalid
      ? `${timeBounds.fromMin}-${timeBounds.toMin}`
      : timeBounds?.invalid
        ? 'invalid'
        : '';

  const allPoints = (filtered.points || [])
    .map((p) => ({
      lat: Number(p.lat),
      lng: Number(p.lng),
      timeBr: p.timeBr || '',
      recordedAt: p.recordedAt || p.recordedAtBr || null,
      tripId: p.tripId || null,
      segmentKey: p.segmentKey || null,
      color: p.color || null,
      hasOs: !!p.hasOs,
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

  // Com filtro horário: reconstruir segmentos a partir dos pontos filtrados (sem pointCount original).
  const allTrips = timeBounds
    ? []
    : Array.isArray(state.trackTrips)
      ? state.trackTrips
      : [];
  const services = Array.isArray(filtered.services) ? filtered.services : [];
  let servicePins = buildServicePinsFromServices(services);

  const lunchItem =
    selectedSeg &&
    (filtered.timeline || []).find(
      (t) =>
        (t.segmentKey || '') === selectedSeg &&
        (t.kind === 'lunch-start' || t.kind === 'lunch-end') &&
        hasValidMapCoords(t.lat, t.lng)
    );
  const lunchPins = lunchItem
    ? [
        {
          lat: Number(lunchItem.lat),
          lng: Number(lunchItem.lng),
          title: lunchItem.title || 'Início do almoço',
          color: lunchItem.color || '#f59e0b',
          timeBr: lunchItem.startedAtBr || '',
        },
      ]
    : [];

  let points = allPoints;
  let tripsForSeg = allTrips;
  let focusTimelineItem = null;
  if (selectedSeg) {
    focusTimelineItem =
      (filtered.timeline || []).find((t) => {
        if ((t.segmentKey || '') === selectedSeg) return true;
        return Array.isArray(t.mapSegmentKeys) && t.mapSegmentKeys.includes(selectedSeg);
      }) || null;
    const focusKeys = new Set(
      focusTimelineItem &&
        Array.isArray(focusTimelineItem.mapSegmentKeys) &&
        focusTimelineItem.mapSegmentKeys.length
        ? focusTimelineItem.mapSegmentKeys
        : [selectedSeg]
    );
    const selTrip =
      (Array.isArray(state.trackTrips) ? state.trackTrips : []).find((t) =>
        focusKeys.has(t.segmentKey || '')
      ) || null;
    const selTrips = (Array.isArray(state.trackTrips) ? state.trackTrips : [])
      .filter((t) => focusKeys.has(t.segmentKey || ''))
      .sort((a, b) => String(a.startedAt || '').localeCompare(String(b.startedAt || '')));
    const selSvc = services.find((s) => (s.segmentKey || '') === selectedSeg) || null;
    if (lunchItem) {
      points = [];
      tripsForSeg = [];
      servicePins = [];
    } else {
      points = allPoints.filter((p) => focusKeys.has(p.segmentKey || ''));
      tripsForSeg = !timeBounds && selTrips.length ? selTrips : selTrip && !timeBounds ? [selTrip] : [];
      if (selSvc) {
        servicePins = servicePins.filter((p) => (p.segmentKey || '') === selectedSeg);
      } else if (focusTimelineItem?.tripId || selTrip?.tripId) {
        const tid = focusTimelineItem?.tripId || selTrip?.tripId;
        servicePins = servicePins.filter((p) => p.tripId && String(p.tripId) === String(tid));
      } else {
        servicePins = [];
      }
    }
  }

  if (!points.length && !servicePins.length && !lunchPins.length) {
    ensureViewerMapCenter().then((center) => {
      if (!g.map || getState(mapRoot).mode !== 'track') return;
      const key = `track-empty:${selectedSeg}:${timeFp}:${center.lat.toFixed(4)},${center.lng.toFixed(4)}`;
      if (g.lastLiveKey !== key) {
        g.lastLiveKey = key;
        g.map.setCenter(center);
        g.map.setZoom(12);
      }
    });
    return;
  }

  const path = points.map((p) => ({ lat: p.lat, lng: p.lng }));
  const segments =
    selectedSeg && tripsForSeg.length
      ? buildFocusTrackSegments(points, tripsForSeg)
      : buildTrackSegments(points, tripsForSeg, { bridgeSegments: !selectedSeg });
  g.polylines = [];
  segments.forEach((seg) => {
    if (seg.path.length < 2) return;
    if (seg.gap) {
      g.polylines.push(
        new window.google.maps.Polyline({
          map: g.map,
          path: seg.path,
          geodesic: true,
          clickable: false,
          strokeOpacity: 0,
          zIndex: 0,
          icons: [
            {
              icon: { path: 'M 0,-1 0,1', strokeColor: seg.color, strokeOpacity: 0.85, scale: 2 },
              offset: '0',
              repeat: '10px',
            },
          ],
        })
      );
      return;
    }
    const isNoOs =
      !seg.hasOs ||
      String(seg.color || '').toLowerCase() === TRACK_IDLE_COLOR.toLowerCase();
    const pl = new window.google.maps.Polyline({
      map: g.map,
      path: seg.path,
      geodesic: true,
      clickable: false,
      strokeColor: seg.color,
      strokeOpacity: 0.92,
      strokeWeight: isNoOs ? 5 : 4,
      zIndex: isNoOs ? 3 : 1,
    });
    g.polylines.push(pl);
  });

  if (points.length) {
    const focusEp =
      selectedSeg && (tripsForSeg.length || focusTimelineItem)
        ? resolveFocusTrackEndpoints(segments, points, focusTimelineItem)
        : null;
    const fallbackSorted = selectedSeg ? sortTrackPointsByTime(points) : points;
    const start = focusEp?.start || fallbackSorted[0];
    const end = focusEp?.end || fallbackSorted[fallbackSorted.length - 1];
    const startLabel = focusEp?.startLabel || (start.timeBr ? `Início ${start.timeBr}` : 'Início');
    const endLabel = focusEp?.endLabel || (end.timeBr ? `Fim ${end.timeBr}` : 'Fim');
    const startOverlay = createPinOverlay(window.google.maps, {
      lat: start.lat,
      lng: start.lng,
      el: buildTrackEndpointEl(startLabel, 'start'),
      anchor: 'bottom',
    });
    startOverlay.setMap(g.map);
    g.overlays.push(startOverlay);

    const sameSpot =
      start.lat === end.lat &&
      start.lng === end.lng &&
      start.timeBr === end.timeBr;
    if (points.length > 1 && !sameSpot) {
      const endOverlay = createPinOverlay(window.google.maps, {
        lat: end.lat,
        lng: end.lng,
        el: buildTrackEndpointEl(endLabel, 'end'),
        anchor: 'bottom',
      });
      endOverlay.setMap(g.map);
      g.overlays.push(endOverlay);
    }
  }

  const svcClusters = clusterNearbyServicePins(servicePins);
  const expandedKey = state.expandedTrackSvcClusterKey || '';
  const expandedStillThere = svcClusters.some((c) => c.key === expandedKey && c.pins.length > 1);
  if (expandedKey && !expandedStillThere) state.expandedTrackSvcClusterKey = null;

  svcClusters.forEach((cluster) => {
    if (cluster.pins.length === 1) {
      const pin = cluster.pins[0];
      const isOs = normalizeTrackOpType(pin.operationType, 'atendimento') === 'atendimento' && pin.os;
      const overlay = createPinOverlay(window.google.maps, {
        lat: pin.lat,
        lng: pin.lng,
        el: buildTrackServicePinEl(pin.os, pin.phase, pin.color, {
          startBr: pin.startBr,
          endBr: pin.endBr,
          timeBr: pin.timeBr,
          operationType: pin.operationType,
          cliente: selectedSeg ? pin.cliente || '' : '',
          outsideRadius: !!pin.outsideRadius,
          interactive: isOs,
        }),
        anchor: 'bottom',
        onClick: isOs ? () => openTrackOsDetail(root, pin) : undefined,
      });
      overlay.setMap(g.map);
      g.overlays.push(overlay);
      return;
    }

    const isOpen = (state.expandedTrackSvcClusterKey || '') === cluster.key;
    if (!isOpen) {
      const overlay = createPinOverlay(window.google.maps, {
        lat: cluster.lat,
        lng: cluster.lng,
        el: buildTrackServiceClusterPinEl(cluster.pins),
        anchor: 'bottom',
        onClick: () => {
          state.expandedTrackSvcClusterKey = cluster.key;
          paint(root, opts);
        },
      });
      overlay.setMap(g.map);
      g.overlays.push(overlay);
      return;
    }

    const hub = createPinOverlay(window.google.maps, {
      lat: cluster.lat,
      lng: cluster.lng,
      el: buildTrackServiceStackHubEl(cluster.pins.length),
      anchor: 'center',
      onClick: () => {
        state.expandedTrackSvcClusterKey = null;
        paint(root, opts);
      },
    });
    hub.setMap(g.map);
    g.overlays.push(hub);

    const offsets = servicePinSpiderOffsets(cluster.pins.length);
    cluster.pins.forEach((pin, idx) => {
      const overlay = createPinOverlay(window.google.maps, {
        lat: cluster.lat,
        lng: cluster.lng,
        el: buildTrackServicePinEl(pin.os, pin.phase, pin.color, {
          startBr: pin.startBr,
          endBr: pin.endBr,
          timeBr: pin.timeBr,
          operationType: pin.operationType,
          cliente: selectedSeg ? pin.cliente || '' : '',
          outsideRadius: !!pin.outsideRadius,
          interactive: normalizeTrackOpType(pin.operationType, 'atendimento') === 'atendimento' && !!pin.os,
        }),
        anchor: 'bottom',
        pixelOffset: offsets[idx] || { x: 0, y: 0 },
        onClick:
          normalizeTrackOpType(pin.operationType, 'atendimento') === 'atendimento' && pin.os
            ? () => openTrackOsDetail(root, pin)
            : undefined,
      });
      overlay.setMap(g.map);
      g.overlays.push(overlay);
    });
  });

  lunchPins.forEach((pin) => {
    const overlay = createPinOverlay(window.google.maps, {
      lat: pin.lat,
      lng: pin.lng,
      el: buildTrackLunchPinEl(pin.title, pin.color, pin.timeBr),
      anchor: 'bottom',
    });
    overlay.setMap(g.map);
    g.overlays.push(overlay);
  });

  const tripFp = (tripsForSeg.length
    ? tripsForSeg
    : segments.map((s) => ({ segmentKey: s.key, color: s.color, pointCount: s.path?.length || 0 }))
  )
    .map((t) => `${t.segmentKey || t.tripId || t.key}:${t.color}:${t.pointCount || 0}`)
    .join('|');
  const svcFp = servicePins
    .map((p) => `${p.os}:${p.phase}:${p.lat.toFixed(5)},${p.lng.toFixed(5)}`)
    .join('|');
  const lunchFp = lunchPins
    .map((p) => `${p.title}:${p.lat.toFixed(5)},${p.lng.toFixed(5)}`)
    .join('|');
  const ref = points[0] || servicePins[0] || lunchPins[0];
  const refEnd = points[points.length - 1] || servicePins[servicePins.length - 1] || lunchPins[0] || ref;
  const key = `track:${state.trackDate || ''}:${selectedSeg}:${timeFp}:${points.length}:${tripFp}:${svcFp}:${lunchFp}:${ref.lat.toFixed(5)},${ref.lng.toFixed(5)}:${refEnd.lat.toFixed(5)},${refEnd.lng.toFixed(5)}`;
  if (g.lastLiveKey !== key) {
    g.lastLiveKey = key;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    servicePins.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    lunchPins.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (
      (selectedSeg || timeBounds) &&
      path.length <= 1 &&
      !servicePins.length &&
      lunchPins.length === 1
    ) {
      g.map.setCenter({ lat: lunchPins[0].lat, lng: lunchPins[0].lng });
      g.map.setZoom(16);
    } else if ((selectedSeg || timeBounds) && path.length === 1 && !servicePins.length && !lunchPins.length) {
      g.map.setCenter(path[0]);
      g.map.setZoom(16);
    } else {
      g.map.fitBounds(bounds, 48);
    }
    window.google.maps.event.addListenerOnce(g.map, 'idle', () => {
      (g.overlays || []).forEach((o) => {
        if (typeof o.draw === 'function') o.draw();
      });
    });
  }
}

async function loadTrackDay(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts.technicianId) return;
  const state = getState(mapRoot);
  const gen = state.loadGen;
  const date = state.trackDate || todayYmd();
  state.trackLoading = true;
  state.trackError = null;
  paint(root, opts);
  try {
    const data = await fetchTechnicianPanelLocationTrack(opts.technicianId, date);
    if (gen !== getState(mapRoot).loadGen || getState(mapRoot).mode !== 'track') return;
    const points = Array.isArray(data?.points) ? data.points : [];
    const trips = Array.isArray(data?.trips) ? data.trips : [];
    const services = Array.isArray(data?.services) ? data.services : [];
    const timeline = Array.isArray(data?.timeline) ? data.timeline : [...trips, ...services];
    state.trackPoints = points;
    state.trackTrips = trips;
    state.trackServices = services;
    state.trackTimeline = timeline;
    state.selectedTrackSegmentKey = null;
    state.expandedTrackSvcClusterKey = null;
    state.trackDate = data?.date || date;
    state.trackLoading = false;
    state.trackError =
      data?.message && !points.length && !services.length ? String(data.message) : null;
    state.trackFp = `${state.trackDate}|${points.length}|${trips.length}|${services.length}|${points[0]?.recordedAt || ''}|${points[points.length - 1]?.recordedAt || ''}`;
    paint(root, opts);
  } catch (err) {
    if (gen !== getState(mapRoot).loadGen) return;
    state.trackLoading = false;
    state.trackPoints = [];
    state.trackTrips = [];
    state.trackServices = [];
    state.trackTimeline = [];
    state.selectedTrackSegmentKey = null;
    state.expandedTrackSvcClusterKey = null;
    state.trackError = err.message || 'Erro ao carregar rastreio';
    paint(root, opts);
  }
}

function paint(root, opts = {}, mapOpts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  applyTrackModeGate(root, mapRoot, state, opts);
  syncModeUi(root, mapRoot, state);
  syncLiveWorkdayOffChrome(root, state, opts);
  const busyGeo = !!(state.geocoding || state.geocodeJobs > 0);
  setCoordsLoading(root, busyGeo || (state.mode === 'plan' && state.loading), {
    banner: true,
    count: state.geocodingCount || 0,
    label: busyGeo
      ? state.geocodingCount > 0
        ? `Buscando coordenadas (${state.geocodingCount})…`
        : 'Buscando coordenadas…'
      : state.loading
        ? 'Carregando OS…'
        : 'Carregando coordenadas…',
  });
  setPlanBusyOverlay(root, state);
  renderLiveSide(root, state, opts);
  renderTrackSide(root, state);

  if (state.mode === 'plan') {
    stopLivePoll(state);
    syncEventFocusChrome(root, { ...state, selectedEventKey: null });
    ensureGoogleHost(root);
    setFakeMapVisible(root, false);
    const pinsEl = root.querySelector('[data-tpm="map-pins"]');
    if (pinsEl) pinsEl.innerHTML = '';
    renderPlanSide(root, state);
    // Mostra overlay no mesmo frame (antes do await da key/script).
    if (!mapRoot.__tpmGmap?.map) {
      setMapBootLoading(root, true, {
        title: 'Carregando mapa',
        sub: 'Preparando visualização…',
      });
    }
    ensureGoogleMap(root)
      .then(() => {
        if (getState(mapRoot).mode !== 'plan') return;
        syncGoogleMap(root, getState(mapRoot), opts, mapOpts);
      })
      .catch((err) => {
        setMapBootLoading(root, false);
        const st = getState(mapRoot);
        st.error = err.message || 'Erro no Google Maps';
        renderPlanSide(root, st);
      });
    return;
  }

  if (state.mode === 'track') {
    stopLivePoll(state);
    syncEventFocusChrome(root, { ...state, selectedEventKey: null });
    ensureGoogleHost(root);
    setFakeMapVisible(root, false);
    const pinsEl = root.querySelector('[data-tpm="map-pins"]');
    if (pinsEl) pinsEl.innerHTML = '';
    const svg = root.querySelector('[data-tpm="map-route"]');
    if (svg) svg.hidden = true;
    if (!mapRoot.__tpmGmap?.map) {
      setMapBootLoading(root, true, {
        title: 'Carregando mapa',
        sub: state.trackLoading ? 'Carregando rastreio…' : 'Preparando visualização…',
      });
    }
    ensureGoogleMap(root)
      .then(() => {
        if (getState(mapRoot).mode !== 'track') return;
        syncTrackGoogleMap(root, getState(mapRoot), opts);
      })
      .catch((err) => {
        setMapBootLoading(root, false);
        const st = getState(mapRoot);
        st.trackError = err.message || 'Erro no Google Maps';
        renderTrackSide(root, st);
        syncModeUi(root, mapRoot, st);
      });
    return;
  }

  ensureGoogleHost(root);
  setFakeMapVisible(root, false);
  const pinsEl = root.querySelector('[data-tpm="map-pins"]');
  if (pinsEl) pinsEl.innerHTML = '';
  const svg = root.querySelector('[data-tpm="map-route"]');
  if (svg) svg.hidden = true;
  if (!mapRoot.__tpmGmap?.map) {
    setMapBootLoading(root, true, {
      title: 'Carregando mapa',
      sub:
        state.liveLoading && !state.live
          ? 'Buscando posição ao vivo…'
          : 'Preparando visualização…',
    });
  }
  ensureGoogleMap(root)
    .then(() => {
      if (getState(mapRoot).mode !== 'live') return;
      syncLiveGoogleMap(root, getState(mapRoot), opts);
    })
    .catch((err) => {
      setMapBootLoading(root, false);
      const st = getState(mapRoot);
      st.liveError = err.message || 'Erro no Google Maps';
      syncModeUi(root, mapRoot, st);
    });
}

async function refreshLivePointOnly(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts.technicianId) return;
  const state = getState(mapRoot);
  const gen = state.loadGen;
  try {
    const live = await fetchTechnicianPanelLiveLocation(opts.technicianId);
    if (gen !== getState(mapRoot).loadGen || getState(mapRoot).mode !== 'live') return;

    const prev = state.live || {};
    const samePoint =
      prev.lat === live?.lat &&
      prev.lng === live?.lng &&
      prev.updatedAt === live?.updatedAt &&
      prev.accuracyM === live?.accuracyM &&
      prev.batteryPct === live?.batteryPct &&
      prev.tripId === live?.tripId &&
      prev.trackRecordedAt === live?.trackRecordedAt &&
      prev.hasLocation === live?.hasLocation &&
      prev.workday?.phase === live?.workday?.phase &&
      prev.workday?.finishedAt === live?.workday?.finishedAt &&
      prev.workday?.startedAt === live?.workday?.startedAt &&
      prev.lunch?.active === live?.lunch?.active &&
      prev.lunch?.startedAt === live?.lunch?.startedAt &&
      prev.lunch?.lat === live?.lunch?.lat &&
      prev.lunch?.lng === live?.lunch?.lng;

    state.live = live;
    state.liveError = null;
    state.liveLoading = false;

    if (samePoint) {
      // Só o "há Xs" / bateria tomados de render; re-render leve.
      // Atualiza bubble se o bucket “sem sincronização” mudou (2 / 5 / 10 min).
      renderLiveSide(root, state, opts);
      syncModeUi(root, mapRoot, state);
      const nextKey = liveStaleBucketKey(live);
      if (nextKey !== state.liveStaleKey) {
        state.liveStaleKey = nextKey;
        ensureGoogleMap(root)
          .then(() => {
            if (getState(mapRoot).mode !== 'live') return;
            syncLiveGoogleMap(root, getState(mapRoot), opts);
          })
          .catch(() => {});
      }
      return;
    }

    state.liveFp = livePanelFingerprint({
      live,
      monStatus: state.liveMonStatus,
      dayData: state.liveDay,
      openOrders: state.liveOpenOrders,
      events: state.liveEvents,
    });
    state.liveStaleKey = liveStaleBucketKey(live);
    paint(root, opts);
  } catch {
    // Poll rápido: falha pontual não derruba o painel.
  }
}

async function refreshLivePanel(root, opts = {}, { initial = false, forceDay = false } = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts.technicianId) return;
  const state = getState(mapRoot);
  const gen = state.loadGen;
  const techId = opts.technicianId;
  const fetchDay = shouldFetchLiveDay(state, { initial, forceDay });
  // GPS: no full só no load (e se ainda não há posição); senão o poll loc cobre.
  const fetchLive = initial || !state.live;

  if (initial) {
    state.liveLoading = true;
    state.liveError = null;
    state.liveEvents = [];
    paint(root, opts);
  }

  try {
    // mon + events sempre no full; GPS e day/route sob cadência própria.
    const fetches = [
      fetchLive
        ? fetchTechnicianPanelLiveLocation(techId)
        : Promise.resolve(state.live),
      fetchMonitoramentoTechnician(techId).catch(() => null),
      fetchTechnicianPanelGoEvents(techId).catch(() => []),
    ];
    if (fetchDay) {
      fetches.push(
        fetchTechnicianPanelDay(techId).catch(() => null),
        fetchTodayRoute(techId).catch(() => [])
      );
    }

    const results = await Promise.all(fetches);
    if (gen !== getState(mapRoot).loadGen || getState(mapRoot).mode !== 'live') return;

    const live = results[0];
    const mon = results[1];
    const events = results[2];
    let day = state.liveDay;
    let openOrders = Array.isArray(state.liveOpenOrders) ? state.liveOpenOrders : [];
    if (fetchDay) {
      day = results[3];
      openOrders = Array.isArray(results[4]) ? results[4] : [];
      state.liveDayFetchedAt = Date.now();
    }

    const monStatus = mon?.technician?.status ?? mon?.status ?? null;
    const liveEvents = Array.isArray(events) ? events : [];
    let nextLive = fetchLive ? live : state.live;
    // Workday/almoço: prefer live-location; fallback mon (/technician) no full poll.
    if (nextLive) {
      const wd = (fetchLive ? live?.workday : null) || mon?.workday || nextLive.workday || null;
      const lunch = (fetchLive ? live?.lunch : null) || mon?.lunch || nextLive.lunch || null;
      nextLive = {
        ...nextLive,
        ...(wd ? { workday: wd } : {}),
        ...(lunch ? { lunch } : {}),
      };
    }

    const fp = livePanelFingerprint({
      live: nextLive,
      monStatus,
      dayData: day,
      openOrders,
      events: liveEvents,
    });

    state.live = nextLive;
    state.liveDay = day;
    state.liveOpenOrders = openOrders;
    state.liveMonStatus = monStatus;
    state.liveEvents = liveEvents;
    if (state.selectedEventKey && !liveEvents.some((ev) => ev?.eventKey === state.selectedEventKey && ev.mapSelectable)) {
      state.selectedEventKey = null;
    }
    state.liveLoading = false;
    state.liveError = null;

    if (fp === state.liveFp) {
      // Mesmo fingerprint: side/hint leve; bubble stale se o relógio mudou de faixa.
      const staleKey = liveStaleBucketKey(state.live);
      if (staleKey !== state.liveStaleKey) {
        state.liveStaleKey = staleKey;
        paint(root, opts);
        return;
      }
      syncModeUi(root, mapRoot, state);
      renderLiveSide(root, state, opts);
      return;
    }
    state.liveFp = fp;
    state.liveStaleKey = liveStaleBucketKey(state.live);
    paint(root, opts);
  } catch (err) {
    if (gen !== getState(mapRoot).loadGen) return;
    state.liveLoading = false;
    state.liveError = err.message || 'Erro ao carregar posição';
    paint(root, opts);
  }
}

/** Atualiza “há Xs” / hint / bubble stale sem API. */
function tickLiveUi(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  if (state.mode !== 'live') return;
  if (isDocumentHidden()) return;

  const live = state.live;
  const agoEl = root.querySelector('[data-tpm="map-live-ago"]');
  if (agoEl && !state.liveLoading) {
    const next = formatLiveAgo(liveSyncAt(live)) || (live?.hasLocation ? 'agora' : '—');
    if (agoEl.textContent !== next) agoEl.textContent = next;
  }

  const hint = root.querySelector('[data-tpm="map-hint"]');
  if (hint && live?.hasLocation && !hint.classList.contains('is-live-unlinked')) {
    const nextHint = formatLiveHint(live);
    // hint em unlinked usa innerHTML com span; não sobrescreve.
    if (hint.textContent !== nextHint && !hint.querySelector('.tpm-map-hint-label')) {
      hint.textContent = nextHint;
    }
  }

  // Bubble “sem sincronização” só muda de bucket a cada 1 min — re-pin sem full paint.
  if (live) {
    const staleKey = liveStaleBucketKey(live);
    if (staleKey !== state.liveStaleKey) {
      state.liveStaleKey = staleKey;
      ensureGoogleMap(root)
        .then(() => {
          if (getState(mapRoot).mode !== 'live') return;
          syncLiveGoogleMap(root, getState(mapRoot), opts);
        })
        .catch(() => {});
    }
  }
}

/** Poll só GPS — inFlight + 1 queued; pausa com aba oculta. */
async function runLiveLocPoll(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts.technicianId) return;
  const state = getState(mapRoot);
  if (state.mode !== 'live') {
    stopLivePoll(state);
    return;
  }
  if (isDocumentHidden()) return;
  if (state.liveLocInFlight) {
    state.liveLocQueued = true;
    return;
  }
  state.liveLocInFlight = true;
  try {
    await refreshLivePointOnly(root, opts);
    // Cadência GPS só muda com entrar/sair de trip+deslocamento.
    ensureLivePollSchedule(root, opts);
  } finally {
    const st = getState(mapRoot);
    st.liveLocInFlight = false;
    if (st.liveLocQueued) {
      st.liveLocQueued = false;
      runLiveLocPoll(root, opts);
    }
  }
}

/** Poll full (mon + events; GPS só no load; day/route sob cadência própria). */
async function runLiveFullPoll(root, opts = {}, { initial = false, forceDay = false } = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts.technicianId) return;
  const state = getState(mapRoot);
  if (state.mode !== 'live' && !initial) {
    stopLivePoll(state);
    return;
  }
  if (!initial && isDocumentHidden()) return;
  if (state.liveFullInFlight) {
    state.liveFullQueued = true;
    // Se pediram day forçado, não perde o flag no queue.
    if (forceDay) state.liveFullForceDay = true;
    return;
  }
  state.liveFullInFlight = true;
  const dayForced = forceDay || !!state.liveFullForceDay;
  state.liveFullForceDay = false;
  try {
    await refreshLivePanel(root, opts, { initial, forceDay: dayForced });
    ensureLivePollSchedule(root, opts);
  } finally {
    const st = getState(mapRoot);
    st.liveFullInFlight = false;
    if (st.liveFullQueued) {
      st.liveFullQueued = false;
      const queuedForce = !!st.liveFullForceDay;
      st.liveFullForceDay = false;
      runLiveFullPoll(root, opts, { initial: false, forceDay: queuedForce });
    }
  }
}

/**
 * Dual poll (Map Alive):
 * - GPS: 10s · 5s com trip_id em deslocamento
 * - Full: 25s (mon + events; day/route ~75s; sem re-fetch GPS se loc ativo)
 * - UI tick 1s: “há Xs” sem API
 * Aba oculta: ticks no-op; ao voltar, full (com day) + GPS.
 */
function ensureLivePollSchedule(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts.technicianId) return;
  const state = getState(mapRoot);
  if (state.mode !== 'live') {
    stopLivePoll(state);
    return;
  }

  const locMs = desiredLiveLocPollMs(state);
  if (!state.liveLocPollId || state.liveLocPollMs !== locMs) {
    if (state.liveLocPollId) {
      window.clearInterval(state.liveLocPollId);
      state.liveLocPollId = null;
    }
    state.liveLocPollMs = locMs;
    state.liveLocPollId = window.setInterval(() => {
      runLiveLocPoll(root, opts);
    }, locMs);
  }

  if (!state.liveFullPollId) {
    state.liveFullPollId = window.setInterval(() => {
      runLiveFullPoll(root, opts, { initial: false });
    }, LIVE_FULL_POLL_MS);
  }

  if (!state.liveUiTickId) {
    state.liveUiTickId = window.setInterval(() => {
      tickLiveUi(root, opts);
    }, LIVE_UI_TICK_MS);
  }

  if (!state.liveVisHandler && typeof document !== 'undefined') {
    state.liveVisHandler = () => {
      if (document.visibilityState !== 'visible') return;
      const st = getState(mapRoot);
      if (st.mode !== 'live') return;
      // Ao voltar: full com day/route frescos + GPS.
      runLiveFullPoll(root, opts, { initial: false, forceDay: true });
      runLiveLocPoll(root, opts);
      tickLiveUi(root, opts);
    };
    document.addEventListener('visibilitychange', state.liveVisHandler);
  }
}

function startLivePoll(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  stopLivePoll(state);
  if (!opts.technicianId) return;
  state.liveDayFetchedAt = 0;
  // Full inicial já inclui GPS; loc-poll segue a cadência após o schedule (sem 2× live-location no open).
  runLiveFullPoll(root, opts, { initial: true, forceDay: true }).then(() => {
    if (getState(mapRoot).mode !== 'live') return;
    ensureLivePollSchedule(root, opts);
  });
}

function parseCoord(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const n = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Coordenadas plotáveis no mapa (rejeita 0,0 / inválidas). */
function hasValidMapCoords(lat, lng) {
  const la = parseCoord(lat);
  const ln = parseCoord(lng);
  if (la == null || ln == null) return false;
  if (la === 0 && ln === 0) return false;
  if (Math.abs(la) > 90 || Math.abs(ln) > 180) return false;
  return true;
}

function isSpotUnlocated(s) {
  return !s || !hasValidMapCoords(s.lat, s.lng);
}

function mapOrdersToSpots(orders) {
  return (orders || []).map((o) => {
    const lat = parseCoord(o.lat);
    const lng = parseCoord(o.lng);
    const ok = hasValidMapCoords(lat, lng);
    return {
      os: o.os,
      client: o.cliente || '',
      bairro: o.bairro || '',
      cidade: o.cidade || '',
      endereco: o.endereco || o.enderecoLimpo || '',
      sla: o.calcPrevisao || '',
      lat: ok ? lat : null,
      lng: ok ? lng : null,
      equipamento: o.equipamento || '',
      serie: o.serie || o.numSerie || '',
      pat: o.pat || '',
      defeitoAlert: o.defeitoAlert || null,
      defeito: o.defeito || o.defeitoDescricao || o.motivo || null,
      colorBg: o.colorBg || null,
      preventiva: o.preventiva || null,
      returnCount: Number(o.returnCount) || 0,
      lastReturnCondition: o.lastReturnCondition || null,
      lastReturnAt: o.lastReturnAt || null,
    };
  });
}

/** Aplica lat/lng do geocode/batch por OS (não depende do rematch OUTER APPLY). */
function applyGeocodeResults(orders, geoResults) {
  const byOs = new Map();
  (geoResults || []).forEach((g) => {
    const id = g.orderId != null ? String(g.orderId).trim() : '';
    if (!id) return;
    if (g.error || !hasValidMapCoords(g.lat, g.lng)) {
      byOs.set(id, { lat: null, lng: null, failed: true });
      return;
    }
    byOs.set(id, { lat: parseCoord(g.lat), lng: parseCoord(g.lng), failed: false });
  });
  if (!byOs.size) return orders || [];
  return (orders || []).map((o) => {
    const hit = byOs.get(String(o.os || '').trim());
    if (!hit) return o;
    if (hit.failed || !hasValidMapCoords(hit.lat, hit.lng)) {
      return { ...o, lat: null, lng: null, needsGeocode: true };
    }
    return { ...o, lat: hit.lat, lng: hit.lng, needsGeocode: false };
  });
}

function ordersMissingCoords(orders) {
  return (orders || []).filter((o) => {
    if (hasValidMapCoords(o.lat, o.lng)) return false;
    return !!(o.endereco || o.enderecoLimpo);
  });
}

async function ensureGeocode(orders) {
  const missing = ordersMissingCoords(orders);
  if (!missing.length) return [];
  const payload = missing.map((o) => ({
    orderId: o.os,
    address: o.endereco || o.enderecoLimpo,
  }));
  return geocodeBatch(payload);
}

async function applyPlanDayPayload(state, day, data, orders) {
  state.forecastByDay[day] = data?.forecast || (day === 'hoje' ? todayYmd() : defaultRouteForecastYmd());
  state.spotsByDay[day] = mapOrdersToSpots(orders || []);
  const spotSet = new Set((state.spotsByDay[day] || []).map((s) => s.os));
  // Sequência só com OS elegíveis do dia (backend já filtra; reforço no FE).
  state.routes[day] = (data?.routed || [])
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    .map((r) => String(r.os || '').trim())
    .filter((os) => os && spotSet.has(os));
  snapshotSavedRoute(state, day);
}

async function fetchAndResolvePlanDay(technicianId, day, hooks = {}) {
  let data = await fetchTechnicianPanelMapPlan(technicianId, day);
  let orders = data?.orders || [];
  const missing = ordersMissingCoords(orders);
  if (missing.length) {
    hooks.onGeocodeStart?.(missing.length);
    try {
      const geoResults = await ensureGeocode(orders);
      // Batch tem prioridade por OS; rematch SQL só completa o que o batch falhar.
      let next = applyGeocodeResults(orders, geoResults);
      try {
        data = await fetchTechnicianPanelMapPlan(technicianId, day);
        const rematch = data?.orders || [];
        const rematchByOs = new Map(rematch.map((o) => [String(o.os || '').trim(), o]));
        next = next.map((o) => {
          if (hasValidMapCoords(o.lat, o.lng)) return o;
          const r = rematchByOs.get(String(o.os || '').trim());
          if (r && hasValidMapCoords(r.lat, r.lng)) {
            return { ...o, lat: r.lat, lng: r.lng, needsGeocode: false };
          }
          return { ...o, lat: null, lng: null, needsGeocode: true };
        });
      } catch {
        /* usa só resultado do batch */
      }
      orders = next;
    } finally {
      hooks.onGeocodeEnd?.(missing.length);
    }
  }
  return { data, orders };
}

function makeGeocodeHooks(root, opts, state, isStale) {
  return {
    onGeocodeStart: (n) => {
      beginGeocodeJob(state, n);
      if (!isStale?.()) paint(root, opts);
    },
    onGeocodeEnd: (n) => {
      // Sempre encerra o job — se pular por stale, trava modal/busy.
      endGeocodeJob(state, n);
      if (!isStale?.()) paint(root, opts);
    },
  };
}

function resetGeocodeState(state) {
  state.geocodeJobs = 0;
  state.geocoding = false;
  state.geocodingCount = 0;
}

async function loadPlanDay(root, opts, dayOverride) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  const day = dayOverride === 'hoje' || dayOverride === 'amanha' ? dayOverride : state.day;
  if (!state.planLoadTokens) state.planLoadTokens = { amanha: 0, hoje: 0 };
  const token = ++state.planLoadTokens[day];
  closeUnlocatedModal();
  state.loading = true;
  state.error = null;
  paint(root, opts);
  try {
    const hooks = makeGeocodeHooks(root, opts, state, () => state.planLoadTokens[day] !== token);
    const { data, orders } = await fetchAndResolvePlanDay(opts.technicianId, day, hooks);
    if (state.planLoadTokens[day] !== token) return;
    applyPlanDayPayload(state, day, data, orders);
    state.loading = false;
    state.error = null;
    resetGeocodeState(state);
  } catch (err) {
    if (state.planLoadTokens[day] !== token) return;
    state.loading = false;
    resetGeocodeState(state);
    state.error = err.message || 'Erro ao carregar';
    state.spotsByDay[day] = [];
    state.routes[day] = [];
    snapshotSavedRoute(state, day);
  }
  paint(root, opts, { fit: day === state.day });
  if (state.planLoadTokens[day] === token) {
    maybeShowUnlocatedForDay(root, opts, day);
  }
}

/** Carrega Hoje + Amanhã em paralelo para as duas abas terem contagem/sequência. */
async function loadAllPlanDays(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  if (!state.planLoadTokens) state.planLoadTokens = { amanha: 0, hoje: 0 };
  const tokens = {
    amanha: ++state.planLoadTokens.amanha,
    hoje: ++state.planLoadTokens.hoje,
  };
  closeUnlocatedModal();
  state.loading = true;
  state.error = null;
  paint(root, opts);
  try {
    const [resAmanha, resHoje] = await Promise.all([
      fetchAndResolvePlanDay(
        opts.technicianId,
        'amanha',
        makeGeocodeHooks(root, opts, state, () => state.planLoadTokens.amanha !== tokens.amanha)
      ),
      fetchAndResolvePlanDay(
        opts.technicianId,
        'hoje',
        makeGeocodeHooks(root, opts, state, () => state.planLoadTokens.hoje !== tokens.hoje)
      ),
    ]);
    if (state.planLoadTokens.amanha === tokens.amanha) {
      applyPlanDayPayload(state, 'amanha', resAmanha.data, resAmanha.orders);
    }
    if (state.planLoadTokens.hoje === tokens.hoje) {
      applyPlanDayPayload(state, 'hoje', resHoje.data, resHoje.orders);
    }
    state.loading = false;
    resetGeocodeState(state);
    state.error = null;
  } catch (err) {
    state.loading = false;
    resetGeocodeState(state);
    state.error = err.message || 'Erro ao carregar';
  }
  paint(root, opts, { fit: true });
  if (
    state.planLoadTokens.amanha === tokens.amanha ||
    state.planLoadTokens.hoje === tokens.hoje
  ) {
    maybeShowUnlocatedForDay(root, opts);
  }
}

function toggleOs(root, os, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !os) return;
  const state = getState(mapRoot);
  if (state.mode !== 'plan') {
    state.mode = 'plan';
    paint(root, opts);
    loadAllPlanDays(root, opts);
    return;
  }
  const day = state.day;
  const route = state.routes[day] || [];
  state.routes[day] = route.includes(os) ? route.filter((x) => x !== os) : route.concat([os]);
  syncRouteAfterMutation(root, opts);
  paint(root, opts);
}

function toggleCluster(root, osList, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  if (state.mode !== 'plan') {
    state.mode = 'plan';
    paint(root, opts);
    loadAllPlanDays(root, opts);
    return;
  }
  const list = String(osList || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!list.length) return;
  const day = state.day;
  const route = state.routes[day] || [];
  const allInRoute = list.every((os) => route.includes(os));
  if (allInRoute) {
    // Clique com todas no roteiro → remove o cluster inteiro de uma vez.
    state.routes[day] = route.filter((os) => !list.includes(os));
  } else {
    // Adiciona todas as OS que ainda faltam (mantém as que já estavam).
    const next = route.slice();
    list.forEach((os) => {
      if (!next.includes(os)) next.push(os);
    });
    state.routes[day] = next;
  }
  syncRouteAfterMutation(root, opts);
  paint(root, opts);
}

async function saveRoute(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  const route = state.routes[state.day] || [];
  if (state.saving || !isPlanRouteDirty(state)) return;
  const forecast = state.forecastByDay[state.day];
  if (!forecast) {
    state.error = 'Forecast do dia não disponível';
    paint(root, opts);
    return;
  }
  const savedFp = state.savedRoutes?.[state.day];
  const previousOs =
    savedFp != null && savedFp !== ''
      ? String(savedFp)
          .split('|')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const removedOs = previousOs.filter((os) => !route.includes(os));
  state.saving = true;
  paint(root, opts);
  try {
    const result = await saveRouteBatch({
      technicianId: opts.technicianId,
      forecast,
      source: 'technician_panel',
      items: route.map((os, i) => ({ os, sequence: i + 1 })),
      removedOs,
    });
    if (!result || result.success === false) {
      throw new Error(result?.message || 'Erro ao salvar roteiro');
    }
    // Qualquer persistência do roteiro invalida links públicos anteriores.
    try {
      await revokeTechnicianRoadmapShare({
        technicianId: opts.technicianId,
        day: state.day,
        forecast,
      });
    } catch {
      /* não bloqueia o save */
    }
    state.shareActive = false;
    if (state.shareCache) state.shareCache[state.day === 'hoje' ? 'hoje' : 'amanha'] = null;
    snapshotSavedRoute(state, state.day);
    state.saving = false;
    const finish = root.querySelector('[data-tpm="map-finish"]');
    if (finish) {
      finish.disabled = true;
      finish.classList.add('is-disabled');
      finish.textContent = 'Roteiro salvo ✓';
    }
    window.setTimeout(() => {
      state.saving = false;
      paint(root, opts);
    }, 1200);
  } catch (err) {
    state.saving = false;
    state.error = err.message || 'Erro ao salvar';
    paint(root, opts);
  }
}

function spotsToPrintOrders(seq) {
  return (seq || []).map((p, i) => ({
    TB02115_ORDEM: i + 1,
    TB02115_CODIGO: p.os,
    TB01008_NOME: p.client || '',
    TB01010_RESUMIDO: p.equipamento || '',
    TB02115_NUMSERIE: p.serie || '',
    TB02112_PAT: p.pat || '',
    TB02115_CIDADE: p.cidade || '',
    TB02115_BAIRRO: p.bairro || '',
    CALC_PREVISAO: p.sla || '',
    PEDIDO_VINCULADO: '',
  }));
}

function printMapPlanSequence(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return;
  const state = getState(mapRoot);
  const route = state.routes[state.day] || [];
  const seq = route.map((os) => spotByOs(state, os)).filter(Boolean);
  if (!seq.length) {
    window.alert('Selecione ao menos uma OS no roteiro para imprimir.');
    return;
  }
  printTechnicianOpenRoute({
    technicianName: opts.technicianName || '—',
    orders: spotsToPrintOrders(seq),
  });
}

function setShareMenuOpen(root, state, open) {
  state.shareMenuOpen = !!open;
  const menu = root.querySelector('[data-tpm="map-share-menu"]');
  const btn = root.querySelector('[data-tpm="map-share"]');
  if (menu) menu.hidden = !open;
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
}

async function shareMapPlanLink(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  const day = state.day === 'hoje' ? 'hoje' : 'amanha';
  const route = state.routes[day] || [];
  if (!route.length || state.shareBusy) return;

  if (!state.shareCache) state.shareCache = { amanha: null, hoje: null };
  const forecast = state.forecastByDay[day] || '';
  const fingerprint = routeShareFingerprint(day, forecast, route);
  const cached = state.shareCache[day];

  setShareMenuOpen(root, state, false);

  const openPanel = (payload) => {
    openRoadmapSharePanel({
      ...payload,
      technicianName: opts.technicianName || '',
      onPrint: () => printMapPlanSequence(root, opts),
    });
  };

  // Sem alteração no roteiro: reutiliza o link e só reabre o modal
  if (cached?.url && cached.fingerprint === fingerprint) {
    openPanel({
      url: cached.url,
      reused: true,
      validFrom: cached.validFrom,
      expiresAt: cached.expiresAt,
    });
    return;
  }

  state.shareBusy = true;
  paint(root, opts);
  try {
    const data = await createTechnicianRoadmapShare({
      technicianId: opts.technicianId,
      day,
      osSequence: route,
      technicianName: opts.technicianName || '',
    });
    const url = data?.url || '';
    state.shareCache[day] = {
      url,
      fingerprint,
      validFrom: data?.validFrom || null,
      expiresAt: data?.expiresAt || null,
    };
    state.shareActive = true;
    state.shareBusy = false;
    paint(root, opts);
    openPanel({
      url,
      reused: false,
      validFrom: data?.validFrom || null,
      expiresAt: data?.expiresAt || null,
    });
  } catch (err) {
    state.shareBusy = false;
    paint(root, opts);
    window.alert(err?.message || 'Erro ao gerar link do roteiro.');
  }
}

async function revokeShareAfterLocalChange(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  const day = state.day === 'hoje' ? 'hoje' : 'amanha';
  if (!state.shareActive && !state.shareCache?.[day]?.url) return;
  try {
    await revokeTechnicianRoadmapShare({
      technicianId: opts.technicianId,
      day,
      forecast: state.forecastByDay[day],
    });
  } catch {
    /* ignore */
  }
  state.shareActive = false;
  if (state.shareCache) state.shareCache[day] = null;
}

/**
 * Após mutação do roteiro: se ficou vazio, apaga LCMY_REG_ROADMAP (botão Salvar some).
 */
async function syncRouteAfterMutation(root, opts) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  const day = state.day === 'hoje' ? 'hoje' : 'amanha';
  revokeShareAfterLocalChange(root, opts);
  if ((state.routes[day] || []).length > 0) return;
  try {
    await clearTechnicianMapPlanRoute({
      technicianId: opts.technicianId,
      day,
      forecast: state.forecastByDay[day],
    });
  } catch {
    /* silencioso — UX continua, tabela pode re-sincronizar depois */
  }
}

/**
 * Liga interações do LiveMap. Retorna cleanup.
 * @param {HTMLElement} root
 * @param {{ technicianId?: string, avatarPhotoUrl?: string|null, initials?: string, openOrders?: object[], statusLabel?: string }} opts
 */
export function bindTechnicianPanelMockMap(root, opts = {}) {
  const mapRoot = root.querySelector('[data-tpm="map"]');
  if (!mapRoot) return () => {};

  const state = getState(mapRoot);
  const techKey = opts.technicianId != null ? String(opts.technicianId) : '';
  if (state._boundTechId !== techKey) {
    state._boundTechId = techKey;
    state.startPointsList = null;
    state.startPointsError = null;
    state.startPoint = null;
    state.startPointsOpen = false;
    state.startPointBusy = false;
  }
  // Abertura via menu (ex.: Roteirizar Premium → modo plan). Consome uma vez.
  const bootMode = opts.initialMapMode;
  if (bootMode === 'live' || bootMode === 'plan' || bootMode === 'track') {
    if (bootMode !== 'track' || allowTrackMode(opts)) {
      state.mode = bootMode;
    }
    if (typeof opts.onInitialMapModeApplied === 'function') {
      opts.onInitialMapModeApplied();
    }
  }
  applyTrackModeGate(root, mapRoot, state, opts);
  paint(root, opts);
  if (state.mode === 'plan' && opts.technicianId) {
    loadAllPlanDays(root, opts);
  } else if (state.mode === 'track' && opts.technicianId && allowTrackMode(opts)) {
    loadTrackDay(root, opts);
  } else if (state.mode === 'live' && opts.technicianId) {
    startLivePoll(root, opts);
  }

  const applyTrackDateTime = (nextYmd, timeFrom = '', timeTo = '') => {
    const next = String(nextYmd || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) return;
    const nextFrom = String(timeFrom || '').trim();
    const nextTo = String(timeTo || '').trim();
    const dateChanged = next !== state.trackDate;
    state.trackDate = next;
    state.trackTimeFrom = nextFrom;
    state.trackTimeTo = nextTo;
    state.trackTimePopOpen = false;
    if (dateChanged) state.selectedTrackSegmentKey = null;
    if (dateChanged && state.mode === 'track' && opts.technicianId) loadTrackDay(root, opts);
    else paint(root, opts);
  };

  const onTrackTimeChange = (e) => {
    const el = e.target;
    if (!el || !root.contains(el)) return;
    const isFrom = el.getAttribute?.('data-tpm') === 'map-track-time-from';
    const isTo = el.getAttribute?.('data-tpm') === 'map-track-time-to';
    if (!isFrom && !isTo) return;
    refreshTrackTimePopValidity(root);
  };

  const applyTrackTimeDraft = () => {
    if (!refreshTrackTimePopValidity(root)) return;
    const draft = readTrackTimePopDraft(root);
    state.trackTimeFrom = draft.from;
    state.trackTimeTo = draft.to;
    setTrackTimePopOpen(root, state, false);
    paint(root, opts);
  };

  const onTrackTimeKey = (e) => {
    if (state.mode !== 'track' || !state.trackTimePopOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setTrackTimePopOpen(root, state, false);
      return;
    }
    if (e.key === 'Enter') {
      const t = e.target;
      if (t && (t.getAttribute?.('data-tpm') === 'map-track-time-from' || t.getAttribute?.('data-tpm') === 'map-track-time-to')) {
        e.preventDefault();
        applyTrackTimeDraft();
      }
    }
  };

  const onClick = (e) => {
    const shareMenu = e.target.closest('[data-tpm="map-share-menu"]');
    const shareBtn = e.target.closest('[data-tpm="map-share"]');
    if (!shareMenu && !shareBtn && state.shareMenuOpen) {
      setShareMenuOpen(root, state, false);
    }

    const timePopEl = e.target.closest('[data-tpm="map-track-time-pop"]');
    const timeBtnEl = e.target.closest('[data-tpm="map-track-time-btn"]');
    if (state.trackTimePopOpen && !timePopEl && !timeBtnEl) {
      setTrackTimePopOpen(root, state, false);
    }

    const trackTimeApply = e.target.closest('[data-tpm="map-track-time-apply"]');
    if (trackTimeApply && root.contains(trackTimeApply)) {
      e.preventDefault();
      e.stopPropagation();
      if (state.mode !== 'track') return;
      applyTrackTimeDraft();
      return;
    }

    const trackTimeClear = e.target.closest('[data-tpm="map-track-time-clear"]');
    if (trackTimeClear && root.contains(trackTimeClear)) {
      e.preventDefault();
      e.stopPropagation();
      if (state.mode !== 'track') return;
      state.trackTimeFrom = '';
      state.trackTimeTo = '';
      const fromEl = root.querySelector('[data-tpm="map-track-time-from"]');
      const toEl = root.querySelector('[data-tpm="map-track-time-to"]');
      if (fromEl) fromEl.value = '';
      if (toEl) toEl.value = '';
      setTrackTimePopOpen(root, state, false);
      paint(root, opts);
      return;
    }

    if (timeBtnEl && root.contains(timeBtnEl)) {
      e.preventDefault();
      e.stopPropagation();
      if (state.mode !== 'track') return;
      setTrackTimePopOpen(root, state, !state.trackTimePopOpen);
      return;
    }

    const diarioBtnClick = e.target.closest('[data-tpm="map-track-diario"]');
    if (diarioBtnClick && root.contains(diarioBtnClick)) {
      e.preventDefault();
      e.stopPropagation();
      if (state.mode !== 'track') return;
      setTrackTimePopOpen(root, state, false);
      if (typeof opts.openDiarioOrya === 'function') {
        opts.openDiarioOrya({
          dateYmd: state.trackDate || todayYmd(),
          timeFrom: state.trackTimeFrom || '',
          timeTo: state.trackTimeTo || '',
        });
      }
      return;
    }

    const trackDateBtn = e.target.closest('[data-tpm="map-track-date"]');
    if (trackDateBtn && root.contains(trackDateBtn)) {
      e.preventDefault();
      e.stopPropagation();
      setTrackTimePopOpen(root, state, false);
      const rect = trackDateBtn.getBoundingClientRect();
      const current = state.trackDate || todayYmd();
      if (typeof opts.openTrackDatePicker === 'function') {
        opts.openTrackDatePicker({
          anchorRect: {
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
            width: rect.width,
            height: rect.height,
          },
          currentDate: current,
          timeFrom: state.trackTimeFrom || '',
          timeTo: state.trackTimeTo || '',
          onApply: (payload) => {
            const date = typeof payload === 'string' ? payload : payload?.date;
            const tf = typeof payload === 'object' ? payload?.timeFrom || '' : '';
            const tt = typeof payload === 'object' ? payload?.timeTo || '' : '';
            applyTrackDateTime(date, tf, tt);
          },
          onClear: () => applyTrackDateTime(todayYmd(), '', ''),
        });
      }
      return;
    }

    const eventFocusClose = e.target.closest('[data-tpm="map-event-focus-close"]');
    if (eventFocusClose && root.contains(eventFocusClose)) {
      e.preventDefault();
      e.stopPropagation();
      state.selectedEventKey = null;
      paint(root, opts);
      return;
    }

    const eventRow = e.target.closest('[data-tpm="map-event"]');
    if (eventRow && root.contains(eventRow)) {
      if (eventRow.getAttribute('data-photoable') === '1') {
        e.preventDefault();
        e.stopPropagation();
        openWorkdayPhotoModal(root, {
          title: eventRow.getAttribute('data-photo-title') || 'Foto',
          sub: eventRow.getAttribute('data-photo-sub') || '',
          url: eventRow.getAttribute('data-photo-url') || '',
        });
        return;
      }
      if (eventRow.getAttribute('data-mapable') === '1') {
        e.preventDefault();
        const key = eventRow.getAttribute('data-event-key') || '';
        if (!key) return;
        state.selectedEventKey = state.selectedEventKey === key ? null : key;
        paint(root, opts);
        return;
      }
    }

    const trackTripRow = e.target.closest('[data-tpm="map-track-trip"]');
    if (trackTripRow && root.contains(trackTripRow)) {
      e.preventDefault();
      e.stopPropagation();
      if (state.mode !== 'track') return;
      if (trackTripRow.getAttribute('data-photoable') === '1') {
        openWorkdayPhotoModal(root, {
          title: trackTripRow.getAttribute('data-photo-title') || 'Foto',
          sub: trackTripRow.getAttribute('data-photo-sub') || '',
          url: trackTripRow.getAttribute('data-photo-url') || '',
        });
        return;
      }
      const kind = trackTripRow.getAttribute('data-kind') || '';
      if (kind === 'workday-start' || kind === 'workday-end') return;
      if (kind === 'lunch-end') return;
      if (kind === 'lunch-start' && trackTripRow.getAttribute('data-mapable') !== '1') return;
      const key = trackTripRow.getAttribute('data-segment-key') || '';
      if (!key) return;
      state.selectedTrackSegmentKey =
        state.selectedTrackSegmentKey === key ? null : key;
      paint(root, opts);
      return;
    }

    const modeBtn = e.target.closest('[data-tpm="map-mode"]');
    if (modeBtn && root.contains(modeBtn)) {
      e.preventDefault();
      const mode = modeBtn.getAttribute('data-mode');
      if (!mode || (mode !== 'live' && mode !== 'plan' && mode !== 'track')) return;
      if (mode === 'track' && !allowTrackMode(opts)) return;
      state.mode = mode;
      if (mode !== 'live') state.selectedEventKey = null;
      if (mode !== 'track') {
        state.selectedTrackSegmentKey = null;
        state.expandedTrackSvcClusterKey = null;
      }
      if (mode !== 'plan') {
        closeUnlocatedModal();
        closeMapOsDetailModal();
        state.startPointsOpen = false;
      }
      setShareMenuOpen(root, state, false);
      paint(root, opts);
      if (mode === 'plan' && opts.technicianId) {
        stopLivePoll(state);
        loadAllPlanDays(root, opts);
      } else if (mode === 'track' && opts.technicianId && allowTrackMode(opts)) {
        stopLivePoll(state);
        loadTrackDay(root, opts);
      } else if (mode === 'live' && opts.technicianId) {
        startLivePoll(root, opts);
      }
      return;
    }

    const startTrigger = e.target.closest('[data-tpm="map-start-trigger"]');
    if (startTrigger && root.contains(startTrigger)) {
      e.preventDefault();
      e.stopPropagation();
      if (state.mode !== 'plan' || state.startPointBusy) return;
      state.startPointsOpen = !state.startPointsOpen;
      paint(root, opts);
      if (state.startPointsOpen) ensureStartPointsList(root, opts);
      return;
    }

    const startClear = e.target.closest('[data-tpm="map-start-clear"]');
    if (startClear && root.contains(startClear)) {
      e.preventDefault();
      e.stopPropagation();
      clearMapStartPoint(root, opts);
      return;
    }

    const startOption = e.target.closest('[data-tpm="map-start-option"]');
    if (startOption && root.contains(startOption) && !startOption.disabled) {
      e.preventDefault();
      e.stopPropagation();
      const id = startOption.getAttribute('data-id');
      if (id) selectMapStartPoint(root, opts, id);
      return;
    }

    if (state.startPointsOpen) {
      const startWrap = root.querySelector('[data-tpm="map-start"]');
      if (!startWrap || !startWrap.contains(e.target)) {
        state.startPointsOpen = false;
        paint(root, opts);
      }
    }

    const dayBtn = e.target.closest('[data-tpm="map-day"]');
    if (dayBtn && root.contains(dayBtn)) {
      e.preventDefault();
      const day = dayBtn.getAttribute('data-day');
      if (!day || (day !== 'hoje' && day !== 'amanha')) return;
      state.day = day;
      setShareMenuOpen(root, state, false);
      closeUnlocatedModal();
      paint(root, opts);
      if (state.mode === 'plan' && opts.technicianId) loadPlanDay(root, opts);
      return;
    }

    const cityToggle = e.target.closest('[data-tpm="map-rest-city-toggle"]');
    if (cityToggle && root.contains(cityToggle)) {
      e.preventDefault();
      e.stopPropagation();
      state.restGroupByCity = !state.restGroupByCity;
      paint(root, opts);
      return;
    }

    const unlocatedBar = e.target.closest('[data-tpm="map-unlocated-bar"]');
    if (unlocatedBar && root.contains(unlocatedBar)) {
      e.preventDefault();
      if (unlocatedBar.hidden) return;
      openUnlocatedModal(root, opts, getUnlocatedSpots(state));
      return;
    }

    if (shareBtn && root.contains(shareBtn)) {
      e.preventDefault();
      e.stopPropagation();
      if (shareBtn.disabled) return;
      shareMapPlanLink(root, opts);
      return;
    }

    const sharePdf = e.target.closest('[data-tpm="map-share-pdf"]');
    if (sharePdf && root.contains(sharePdf)) {
      e.preventDefault();
      e.stopPropagation();
      setShareMenuOpen(root, state, false);
      printMapPlanSequence(root, opts);
      return;
    }

    const shareLink = e.target.closest('[data-tpm="map-share-link"]');
    if (shareLink && root.contains(shareLink)) {
      e.preventDefault();
      e.stopPropagation();
      shareMapPlanLink(root, opts);
      return;
    }

    const pin = e.target.closest('[data-tpm="map-pin"]');
    if (pin && root.contains(pin)) {
      e.preventDefault();
      const osList = pin.getAttribute('data-os-list');
      if (osList) toggleCluster(root, osList, opts);
      else toggleOs(root, pin.getAttribute('data-os'), opts);
      return;
    }

    const restAdd = e.target.closest('[data-tpm="map-rest-add"]');
    if (restAdd && root.contains(restAdd)) {
      e.preventDefault();
      e.stopPropagation();
      toggleOs(root, restAdd.getAttribute('data-os'), opts);
      return;
    }

    const restCard = e.target.closest('[data-tpm="map-rest-card"]');
    if (restCard && root.contains(restCard)) {
      e.preventDefault();
      openOsDetailFromMap(root, restCard.getAttribute('data-os'));
      return;
    }

    const remove = e.target.closest('[data-tpm="map-remove"]');
    if (remove && root.contains(remove)) {
      e.preventDefault();
      e.stopPropagation();
      const os = remove.getAttribute('data-os');
      state.routes[state.day] = (state.routes[state.day] || []).filter((x) => x !== os);
      syncRouteAfterMutation(root, opts);
      paint(root, opts);
      return;
    }

    const planItem = e.target.closest('[data-tpm="map-plan-item"]');
    if (
      planItem &&
      root.contains(planItem) &&
      !e.target.closest('[data-tpm="map-remove"]') &&
      !e.target.closest('.tpm-map-plan-grip')
    ) {
      e.preventDefault();
      openOsDetailFromMap(root, planItem.getAttribute('data-os'));
      return;
    }

    const finish = e.target.closest('[data-tpm="map-finish"]');
    if (finish && root.contains(finish) && !finish.disabled) {
      e.preventDefault();
      saveRoute(root, opts);
    }
  };

  const onDragStart = (e) => {
    if (state.mode !== 'plan') return;
    const item = e.target.closest('[data-tpm="map-plan-item"]');
    if (!item || !root.contains(item)) return;
    if (e.target.closest('[data-tpm="map-remove"]') || e.target.closest('[data-tpm="map-os-detail"]')) {
      e.preventDefault();
      return;
    }
    const os = item.getAttribute('data-os');
    if (!os) return;
    state.dragPlanOs = os;
    state.dragPlanAfter = false;
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', os);
      e.dataTransfer.setData('text/os', os);
    } catch {
      /* IE/safe */
    }
    item.classList.add('is-dragging');
  };

  const onDragEnd = () => {
    state.dragPlanOs = null;
    state.dragPlanAfter = false;
    clearPlanDragMarks(root);
  };

  const onDragOver = (e) => {
    if (state.mode !== 'plan') return;
    const item = e.target.closest('[data-tpm="map-plan-item"]');
    if (!item || !root.contains(item)) return;
    const fromOs = state.dragPlanOs;
    const toOs = item.getAttribute('data-os');
    if (!fromOs || !toOs || fromOs === toOs) return;
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = 'move';
    } catch {
      /* ignore */
    }
    const rect = item.getBoundingClientRect();
    const placeAfter = e.clientY > rect.top + rect.height / 2;
    state.dragPlanAfter = placeAfter;
    root.querySelectorAll('.tpm-map-plan-item.is-drag-over-before, .tpm-map-plan-item.is-drag-over-after').forEach((el) => {
      if (el !== item) el.classList.remove('is-drag-over-before', 'is-drag-over-after');
    });
    item.classList.toggle('is-drag-over-before', !placeAfter);
    item.classList.toggle('is-drag-over-after', placeAfter);
  };

  const onDrop = (e) => {
    if (state.mode !== 'plan') return;
    const item = e.target.closest('[data-tpm="map-plan-item"]');
    if (!item || !root.contains(item)) return;
    e.preventDefault();
    e.stopPropagation();
    let fromOs = state.dragPlanOs;
    try {
      fromOs = e.dataTransfer.getData('text/os') || e.dataTransfer.getData('text/plain') || fromOs;
    } catch {
      /* ignore */
    }
    const toOs = item.getAttribute('data-os');
    const placeAfter = !!state.dragPlanAfter;
    state.dragPlanOs = null;
    state.dragPlanAfter = false;
    clearPlanDragMarks(root);
    if (!fromOs || !toOs || fromOs === toOs) return;
    const day = state.day;
    const route = state.routes[day] || [];
    if (!route.includes(fromOs) || !route.includes(toOs)) return;
    const next = reorderRouteOs(route, fromOs, toOs, placeAfter);
    if (next === route || next.join('|') === route.join('|')) return;
    state.routes[day] = next;
    revokeShareAfterLocalChange(root, opts);
    paint(root, opts);
  };

  root.addEventListener('click', onClick);
  root.addEventListener('change', onTrackTimeChange);
  root.addEventListener('keydown', onTrackTimeKey);
  root.addEventListener('dragstart', onDragStart);
  root.addEventListener('dragend', onDragEnd);
  root.addEventListener('dragover', onDragOver);
  root.addEventListener('drop', onDrop);
  return () => {
    state.loadGen += 1;
    stopLivePoll(state);
    clearGoogleOverlays(mapRoot);
    closeRoadmapSharePanel();
    closeUnlocatedModal();
    closeMapOsDetailModal();
    closeWorkdayPhotoModal(root);
    root.removeEventListener('click', onClick);
    root.removeEventListener('change', onTrackTimeChange);
    root.removeEventListener('keydown', onTrackTimeKey);
    root.removeEventListener('dragstart', onDragStart);
    root.removeEventListener('dragend', onDragEnd);
    root.removeEventListener('dragover', onDragOver);
    root.removeEventListener('drop', onDrop);
  };
}

/**
 * Reexecuta carga da aba LiveMap na submodo atual (Ao vivo / Rastreio / Roteirizar).
 */
export async function refreshTechnicianPanelMockMap(root, opts = {}) {
  const mapRoot = root?.querySelector?.('[data-tpm="map"]');
  if (!mapRoot || !opts?.technicianId) return;
  const state = getState(mapRoot);
  if (state.mode === 'plan') {
    await loadAllPlanDays(root, opts);
    return;
  }
  if (state.mode === 'track') {
    if (allowTrackMode(opts)) await loadTrackDay(root, opts);
    return;
  }
  await refreshLivePanel(root, opts, { initial: true, forceDay: true });
}
